import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import init, { generate_transfer_proof, generate_withdraw_proof, decrypt_ciphertext } from '../pkg/confidential_transfer_proof_generation.js';
import { useDeterministicElGamalKeypair } from '../crypto/useDeterministicElGamalKeypair.ts';
import History from './History';
import { RequestService, numericChainIdFromChainKey } from '../services/requestService';
import { createSingleInviteCode } from '../services/inviteCodeService';
import { safeLog, createSecureKeypair } from '../utils/secureMemory.js';
import { getCleanErrorMessage } from '../utils/errorParser.js';
import TokenSelector from './TokenSelector';
import TokenSelectorPill from './TokenSelectorPill';
import KeyDerivationConsentModal from './KeyDerivationConsentModal';
import { CHAINS, STELLAR_LAB_FUND_ACCOUNT_URL } from '../config/chains';
import { secureKeyCache } from '../services/secureKeyCache.js';
import { encryptRandomness } from '../utils/ibeEncryption.js';
import { checkTempoFeeTokenBalance, getTempoFeeTokenSymbols } from '../utils/tempoFeeTokens';
import { uploadBytesToIpfs } from '../utils/ipfs';
import { withRateLimitRetry } from '../utils/rateLimitRetry';
import { nextWithdrawProofNonceFromAccountCore } from '../utils/withdrawProofNonce.js';
import { getExplorerTxUrlFromTokenChain } from '../utils/explorerUtils';
import { executeContractTxWithGasFallback, estimateTxFees } from '../utils/gasEstimation';
import { fetchSacBalanceRaw, formatSacAmountForDisplay } from '../utils/stellarSacBalance.js';
import {
    formatPlainForDisplay,
    maxUserFacingAmountFromPlainCap,
    normalizeTokenDecimals,
    parseUserAmountToPlainBn,
    parseUserAmountToPlainBnStrict,
    parseUserAmountToPlainInt,
    plainToUserAmountNumber,
    truncateAmountInputString,
} from '../utils/confidentialAmountScale.js';

// --- StrictMode-safe account-creation guard (persists across mounts) ---
const accountCreationSessions = new Set();

const makeAccountCreationKey = (addr, chainId, verifyingContract) => {
    const a = addr ? addr.toLowerCase() : 'null';
    const c = chainId ?? 'null';
    const vc = verifyingContract ? verifyingContract.toLowerCase() : 'null';
    return `${a}::${c}::${vc}`;
};

// Helper function to detect if running on mobile device
const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (typeof window !== 'undefined' && window.innerWidth <= 768);
};

/** When false, the "View Breakdown" control and fee panel are not rendered. */
const ENABLE_FEE_BREAKDOWN_UI = false;

// Helper function to detect SSL/certificate errors
const isSSLError = (error) => {
    if (!error) return false;
    const errorMessage = String(error.message || error).toLowerCase();
    const errorCode = String(error.code || '').toLowerCase();
    return (
        errorMessage.includes('ssl') ||
        errorMessage.includes('certificate') ||
        errorMessage.includes('err_ssl') ||
        errorCode.includes('ssl') ||
        errorMessage.includes('unrecognized_name_alert') ||
        errorMessage.includes('cert_') ||
        errorMessage.includes('net::err_ssl')
    );
};

// Helper function to get user-friendly error message for network errors
const getNetworkErrorMessage = (error, chainName = 'this network') => {
    if (isSSLError(error)) {
        return `SSL certificate error connecting to ${chainName} RPC endpoint. The network may be experiencing issues. Please try again later or contact support.`;
    }
    if (error?.message?.includes('network') || error?.message?.includes('connection') || error?.message?.includes('timeout')) {
        return `Network connection error with ${chainName}. Please check your internet connection and try again.`;
    }
    if (error?.code === 'CALL_EXCEPTION' || error?.message?.includes('CALL_EXCEPTION')) {
        return `RPC call failed for ${chainName}. The network may be experiencing issues.`;
    }
    return `Error connecting to ${chainName}. Please try again later.`;
};

// Helper function to get pending action type name
const getPendingActionTypeName = (kind) => {
    // PendingKind enum: NONE=0, CREATE=1, DEPOSIT=2, TRANSFER=3, APPLY=4, WITHDRAW=5
    const actionNames = {
        0: null, // NONE - no pending action
        1: 'account creation',
        2: 'deposit',
        3: 'transfer',
        4: 'apply pending balances',
        5: 'withdrawal'
    };
    return actionNames[kind] || 'request';
};

/** Hex-encode raw bytes (no 0x) for comparing contract pubkeys with WASM-derived pubkeys. */
function bytesToHexLower(bytes) {
    if (!bytes || !bytes.length) return '';
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Same encoding as account creation: WASM `public_key` (base64) → hex body for `get_account_core[4]`. */
function elGamalPubkeyBase64ToHexBody(b64) {
    if (!b64 || typeof b64 !== 'string') return null;
    try {
        const pkBytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
        return bytesToHexLower(pkBytes);
    } catch {
        return null;
    }
}

function contractElGamalPubkeyToHexBody(chainField) {
    if (chainField == null) return null;
    if (typeof chainField === 'string') {
        return String(chainField).toLowerCase().replace(/^0x/, '');
    }
    if (chainField instanceof Uint8Array) {
        return bytesToHexLower(chainField);
    }
    try {
        const s = ethers.utils.hexlify(chainField);
        return String(s).toLowerCase().replace(/^0x/, '');
    } catch {
        return null;
    }
}

/** True iff both are non-empty hex bodies representing the same integer (handles leading-zero width). */
function elGamalPubkeyHexBodiesEqual(a, b) {
    if (a == null || b == null || a === '' || b === '') return false;
    try {
        return BigInt(`0x${a}`) === BigInt(`0x${b}`);
    } catch {
        return a === b;
    }
}

/**
 * Resolve protocol fee display for "View Breakdown": different networks use different
 * fee currencies (e.g. wei on Ethereum, USDC on Arc, PathUSD on Tempo/Ontempo).
 * @param {object} chainConfig - CHAINS[chainId]
 * @param {string} feeTokenAddr - contract.feeToken() (zero address = native)
 * @param {string} feeAmountRaw - contract.feeAmount().toString()
 * @returns {string|null} Display string e.g. "1 wei", "0.000001 USDC", "1 PathUSD", or null
 */
function getProtocolFeeDisplayText(chainConfig, feeTokenAddr, feeAmountRaw) {
    if (!chainConfig || !feeAmountRaw) return null;
    const addr = (feeTokenAddr || '').toLowerCase();
    const zero = '0x0000000000000000000000000000000000000000';
    if (addr === zero) {
        const native = chainConfig.nativeCurrency || {};
        // Arc (and similar) use USDC as native but the contract stores fee in 18-decimal units; use 18 for display.
        const isArcStyleNativeToken = chainConfig.id === 'arc' || chainConfig.id === 'stable';
        const decimals = isArcStyleNativeToken ? 18 : (native.decimals != null ? native.decimals : 18);
        const symbol = native.symbol || 'ETH';
        if (decimals === 18 && !isArcStyleNativeToken) {
            return `${feeAmountRaw} wei`;
        }
        const divisor = Math.pow(10, decimals);
        const amount = Number(feeAmountRaw) / divisor;
        const formatted = amount < 1 && amount > 0
            ? amount.toFixed(Math.min(decimals, 6))
            : amount.toFixed(amount >= 1 ? 2 : 6);
        return `${formatted.replace(/\.?0+$/, '') || '0'} ${symbol}`;
    }
    let decimals = 18;
    let symbol = 'units';
    const tokens = chainConfig.tokens ? Object.values(chainConfig.tokens) : [];
    const feeTokens = chainConfig.feeTokens || [];
    for (const t of [...tokens, ...feeTokens]) {
        if ((t.address || '').toLowerCase() === addr) {
            decimals = t.decimals != null ? t.decimals : 18;
            symbol = t.symbol || symbol;
            break;
        }
    }
    // Tempo (PathUSD) and similar: contract stores fee in 18-decimal units; use 18 for display.
    const isTempoStyleFeeToken = chainConfig.id === 'tempo';
    if (isTempoStyleFeeToken) decimals = 18;
    if (decimals === 0) return `${feeAmountRaw} ${symbol}`;
    const divisor = Math.pow(10, decimals);
    const amount = Number(feeAmountRaw) / divisor;
    const formatted = amount < 1 && amount > 0
        ? amount.toFixed(Math.min(decimals, 6))
        : amount.toFixed(amount >= 1 ? 2 : 6);
    return `${formatted.replace(/\.?0+$/, '') || '0'} ${symbol}`;
}

const TabbedOperations = ({ 
    contract, 
    userAccount, 
    wasmModule,
    provider: providerProp,
    showNotification, 
    onOperationComplete,
    onKeypairUpdate,
    accountData,
    hasActiveAccount,
    accountExists,
    accountCoreSynced = true,
    onConnect,
    confidentialBalance,
    setConfidentialBalance,
    activeTab,
    setActiveTab,
    onTokenDataChange,
    onPendingBalanceChange,
    onSecureClaimRegister,
    onSecureClaimInFlightChange,
    switchToChain,
    activeWalletOrigin
}) => {
    // Use WASM-based deterministic key generation
    const provider = providerProp || contract?.signer?.provider || null;

    // --- Gas breakdown state (for "View Breakdown" UI) ---
    const [showGasBreakdown, setShowGasBreakdown] = useState(false);
    const [gasBreakdownOperation, setGasBreakdownOperation] = useState(null); // 'transfer' | 'deposit' | 'withdraw'
    const [gasBreakdown, setGasBreakdown] = useState(null);
    const [gasBreakdownError, setGasBreakdownError] = useState(null);
    const [isGasBreakdownLoading, setIsGasBreakdownLoading] = useState(false);
    const transferProofPreviewCacheRef = useRef(new Map());
    const withdrawProofPreviewCacheRef = useRef(new Map());

    const buildTransferProofDataForPreview = async (tokenAddress, recipientAddress, amountForContractBn) => {
        try {
            if (!contract || !userAccount || !keypair?.private_key || confidentialBalance === null) return null;
            if (!tokenAddress || !recipientAddress || !amountForContractBn || amountForContractBn.lte(0)) return null;

            const cacheKey = [
                selectedChainId || '',
                String(tokenAddress).toLowerCase(),
                String(recipientAddress).toLowerCase(),
                amountForContractBn.toString(),
                String(confidentialBalance),
                String(userAccount).toLowerCase()
            ].join('::');
            const cached = transferProofPreviewCacheRef.current.get(cacheKey);
            if (cached) return cached;

            const currentBalanceCiphertext = await fetchCurrentBalanceCiphertext(tokenAddress);
            const recipientPubkey = await getRecipientPublicKey(recipientAddress);
            const encodedRecipientPubkey = encodeRecipientPubkey(recipientPubkey);

            const secureKeypair = createSecureKeypair(keypair);
            let privateKey;
            try {
                privateKey = secureKeypair.getPrivateKey();
                if (!privateKey) return null;

                const transferInput = {
                    current_balance_ciphertext: currentBalanceCiphertext,
                    current_balance: Math.floor(confidentialBalance),
                    transfer_amount: parseInt(amountForContractBn.toString(), 10),
                    source_keypair: privateKey,
                    destination_pubkey: encodedRecipientPubkey
                };

                const proofResult = generate_transfer_proof(JSON.stringify(transferInput));
                const proof = JSON.parse(proofResult);
                if (!proof?.success || !proof?.data?.equality_proof || !proof?.data?.ciphertext_validity_proof || !proof?.data?.range_proof) {
                    return null;
                }

                const encodedProofData = ethers.utils.defaultAbiCoder.encode(
                    ['string', 'string', 'string'],
                    [proof.data.equality_proof, proof.data.ciphertext_validity_proof, proof.data.range_proof]
                );
                transferProofPreviewCacheRef.current.set(cacheKey, encodedProofData);
                return encodedProofData;
            } finally {
                secureKeypair.clear();
            }
        } catch (error) {
            console.warn('Failed to build transfer proof for preview estimation:', error);
            return null;
        }
    };

    const buildWithdrawProofDataForPreview = async (tokenAddress, amountForContractBn) => {
        try {
            if (!contract || !userAccount || !keypair?.private_key || confidentialBalance === null) return null;
            if (!tokenAddress || !amountForContractBn || amountForContractBn.lte(0)) return null;
            if (typeof contract.getAccountCore !== 'function') return null;

            const accountCoreTuple = await contract.getAccountCore(userAccount);
            const txIdKey = ethers.BigNumber.from(accountCoreTuple?.[3] ?? 0).toString();
            const cacheKey = [
                selectedChainId || '',
                String(tokenAddress).toLowerCase(),
                amountForContractBn.toString(),
                String(confidentialBalance),
                String(userAccount).toLowerCase(),
                txIdKey
            ].join('::');
            const cached = withdrawProofPreviewCacheRef.current.get(cacheKey);
            if (cached) return cached;

            const currentBalanceCiphertext = await fetchCurrentBalanceCiphertext(tokenAddress);
            const withdrawProofNonce = nextWithdrawProofNonceFromAccountCore(accountCoreTuple);
            const secureKeypair = createSecureKeypair(keypair);
            let privateKey;
            try {
                privateKey = secureKeypair.getPrivateKey();
                if (!privateKey) return null;

                const withdrawInput = {
                    current_balance_ciphertext: currentBalanceCiphertext,
                    current_balance: Math.floor(confidentialBalance),
                    withdraw_amount: parseInt(amountForContractBn.toString(), 10),
                    keypair: privateKey,
                    nonce: withdrawProofNonce
                };

                const proofResult = generate_withdraw_proof(JSON.stringify(withdrawInput));
                const proof = JSON.parse(proofResult);
                if (!proof?.success || !proof?.data?.equality_proof || !proof?.data?.range_proof) {
                    return null;
                }

                const encodedProofData = ethers.utils.defaultAbiCoder.encode(
                    ['string', 'string'],
                    [proof.data.equality_proof, proof.data.range_proof]
                );
                withdrawProofPreviewCacheRef.current.set(cacheKey, encodedProofData);
                return encodedProofData;
            } finally {
                secureKeypair.clear();
            }
        } catch (error) {
            console.warn('Failed to build withdraw proof for preview estimation:', error);
            return null;
        }
    };

    const fetchGasBreakdown = async (operation, opts = {}) => {
        if (!ENABLE_FEE_BREAKDOWN_UI) return;
        // Always record which operation the user is trying to view,
        // so the panel can render even when we're only showing an error message
        setGasBreakdownOperation(operation);

        // Require wallet, contract, and (for actual values) an explicitly selected chain+token
        if (!provider || !userAccount || !contract) {
            setGasBreakdownError('Connect your wallet to view fee breakdown.');
            setGasBreakdown(null);
            return;
        }

        // If no chain/token is selected yet, guide the user instead of showing default-chain values
        if (!selectedChainId || !selectedTokenId) {
            setGasBreakdownError('Select a chain first to view fee breakdown.');
            setGasBreakdown(null);
            return;
        }

        try {
            setIsGasBreakdownLoading(true);
            setGasBreakdownError(null);
            setGasBreakdownOperation(operation);

            let manualGasLimit = 500000;
            if (operation === 'transfer') manualGasLimit = 800000;
            else if (operation === 'withdraw') manualGasLimit = 1000000;

            let breakdown;
            if (operation === 'transfer' && opts.tokenAddress && opts.recipient && contract) {
                const amountForContract = opts.amountForContract != null
                    ? ethers.BigNumber.from(opts.amountForContract)
                    : ethers.BigNumber.from(0);
                let transferProofData = await buildTransferProofDataForPreview(
                    opts.tokenAddress,
                    opts.recipient,
                    amountForContract
                );
                if (!transferProofData) {
                    // Fallback to representative payload sizes if preview proof generation is unavailable.
                    transferProofData = ethers.utils.defaultAbiCoder.encode(
                        ['string', 'string', 'string'],
                        ['A'.repeat(2600), 'B'.repeat(2600), 'C'.repeat(1200)]
                    );
                }

                // Mirror transfer submit path: include native fee value override when fee token is native.
                let txOverrides = {};
                try {
                    const feeTokenAddr = await contract.feeToken();
                    if (feeTokenAddr === ethers.constants.AddressZero) {
                        const feeAmt = await contract.feeAmount();
                        txOverrides = { value: feeAmt };
                    }
                } catch (_) {}

                breakdown = await estimateTxFees(
                    contract,
                    'transferConfidential',
                    [opts.recipient, opts.tokenAddress, transferProofData, false],
                    provider,
                    { manualGasLimit, txOverrides }
                );
            } else if (operation === 'deposit' && opts.tokenAddress && contract) {
                const amountForContract = opts.amountForContract != null
                    ? ethers.BigNumber.from(opts.amountForContract)
                    : ethers.BigNumber.from(0);
                breakdown = await estimateTxFees(
                    contract,
                    'deposit',
                    [opts.tokenAddress, amountForContract],
                    provider,
                    { manualGasLimit }
                );
            } else if (operation === 'withdraw' && opts.tokenAddress && contract) {
                const amountForContract = opts.amountForContract != null
                    ? ethers.BigNumber.from(opts.amountForContract)
                    : ethers.BigNumber.from(0);
                let withdrawProofData = await buildWithdrawProofDataForPreview(
                    opts.tokenAddress,
                    amountForContract
                );
                if (!withdrawProofData) {
                    // Fallback to representative payload sizes if preview proof generation is unavailable.
                    withdrawProofData = ethers.utils.defaultAbiCoder.encode(
                        ['string', 'string'],
                        ['A'.repeat(2600), 'B'.repeat(1200)]
                    );
                }
                breakdown = await estimateTxFees(
                    contract,
                    'withdraw',
                    [opts.tokenAddress, amountForContract.toString(), withdrawProofData, false],
                    provider,
                    { manualGasLimit }
                );
            } else {
                breakdown = await estimateTxFees(null, null, [], provider, { manualGasLimit });
            }

            // Attach protocol fee for transfer only; currency varies by network (wei, USDC, PathUSD, etc.).
            let protocolFeeDisplay = null;
            if (operation === 'transfer' && contract && breakdown) {
                try {
                    const feeTokenAddr = await contract.feeToken();
                    const feeAmt = await contract.feeAmount();
                    const chainConfig = CHAINS[selectedChainId];
                    protocolFeeDisplay = getProtocolFeeDisplayText(chainConfig, feeTokenAddr, feeAmt.toString());
                } catch (_) {
                    protocolFeeDisplay = null;
                }
            }

            setGasBreakdown(protocolFeeDisplay != null ? { ...breakdown, protocolFeeDisplay } : breakdown);
        } catch (error) {
            console.error('Error estimating gas breakdown:', error);
            setGasBreakdownError(error?.message || 'Failed to estimate network fees.');
            setGasBreakdown(null);
        } finally {
            setIsGasBreakdownLoading(false);
        }
    };

    const refreshGasBreakdownForExactTx = async (operation, methodName, methodArgs, options = {}) => {
        if (!ENABLE_FEE_BREAKDOWN_UI || !showGasBreakdown || gasBreakdownOperation !== operation || !provider || !contract) {
            return;
        }
        try {
            setIsGasBreakdownLoading(true);
            setGasBreakdownError(null);
            const breakdown = await estimateTxFees(contract, methodName, methodArgs, provider, options);
            setGasBreakdownOperation(operation);
            setGasBreakdown(breakdown);
        } catch (error) {
            console.warn(`Failed to refresh ${operation} gas breakdown with exact tx args:`, error);
        } finally {
            setIsGasBreakdownLoading(false);
        }
    };
    
    // Helper function to get operation type name
    const getOperationTypeName = (operationKind) => {
        const operationKinds = [
            'create',
            'deposit', 
            'transfer',
            'secure claim',
            'withdraw'
        ];
        return operationKinds[operationKind] || 'unknown';
    };

    // Check if user has a pending action
    const hasPendingAction = () => {
        const hasPending = accountData && accountData.pendingAction;
        console.log('hasPendingAction:', {
            accountData: !!accountData,
            pendingAction: accountData?.pendingAction,
            hasPending
        });
        return hasPending;
    };

    // Get pending action type message from contract
    const getPendingActionMessage = async () => {
        if (!contract || !userAccount) {
            return 'A request is in progress. Please wait for it to complete before submitting a new request.';
        }

        try {
            const pendingActionTuple = await contract.getPendingAction(userAccount);
            // pendingActionTuple structure: [kind, owner, txId, token, recipient, amount, feePaid, useOffchainVerify, data]
            const kindRaw = pendingActionTuple[0];
            // Convert BigNumber to regular number for consistent comparison across chains
            const kind = kindRaw?.toNumber ? kindRaw.toNumber() : Number(kindRaw);
            const actionTypeName = getPendingActionTypeName(kind);
            
            if (actionTypeName) {
                return `You have a pending ${actionTypeName} request. Please wait for it to complete before submitting a new request.`;
            }
        } catch (error) {
            console.error('Error fetching pending action type:', error);
        }
        
        // Fallback message if we can't fetch the type
        return 'A request is in progress. Please wait for it to complete before submitting a new request.';
    };

    // Get progress message for balance display based on pending action kind
    const getPendingProgressMessage = (kind) => {
        // PendingKind enum: NONE=0, CREATE=1, DEPOSIT=2, TRANSFER=3, APPLY=4, WITHDRAW=5
        console.log('getPendingProgressMessage called with kind:', kind, 'type:', typeof kind);
        // Ensure kind is a number for comparison
        const numKind = typeof kind === 'number' ? kind : (kind?.toNumber ? kind.toNumber() : Number(kind));
        console.log('getPendingProgressMessage numKind:', numKind);
        switch (numKind) {
            case 2: // DEPOSIT
                return 'Deposit in progress...';
            case 3: // TRANSFER
                return 'Transfer in progress...';
            case 4: // APPLY (Secure Claim)
                return 'Secure claim in progress...';
            case 5: // WITHDRAW
                return 'Withdraw in progress...';
            default:
                return null;
        }
    };

    // Check for pending action and update state
    const checkPendingAction = async () => {
        if (!contract || !userAccount) {
            setPendingActionKind(null);
            return;
        }

        try {
            // Check contract directly for pending action
            const pendingActionTuple = await contract.getPendingAction(userAccount);
            const kindRaw = pendingActionTuple[0];
            console.log('checkPendingAction - kindRaw:', kindRaw, 'type:', typeof kindRaw, 'contract:', contract.address);
            // Convert BigNumber to regular number for consistent comparison across chains
            const kind = kindRaw?.toNumber ? kindRaw.toNumber() : Number(kindRaw);
            console.log('checkPendingAction - converted kind:', kind, 'type:', typeof kind);
            
            // Only set if there's actually a pending action (kind !== 0 means NONE)
            if (kind && kind !== 0) {
                console.log('checkPendingAction - setting pendingActionKind to:', kind);
                setPendingActionKind(kind);
            } else {
                console.log('checkPendingAction - no pending action, setting to null');
                setPendingActionKind(null);
            }
        } catch (error) {
            console.error('Error checking pending action:', error);
            // On error, check accountData as fallback
            if (accountData && accountData.pendingAction) {
                // Keep current state or try to infer from accountData if possible
                // For now, just set to null on error to avoid showing stale state
                setPendingActionKind(null);
            } else {
                setPendingActionKind(null);
            }
        }
    };

    // Check if user can submit new operations
    const canSubmitOperation = () => {
        if (!accountData) return false;
        return accountData.exists && accountData.finalized && !accountData.pendingAction;
    };

    // Check if user can submit secure claim operations (less strict than regular operations)
    const canSubmitSecureClaim = () => {
        // If we have a user account and can see balances, allow secure claim
        // This is more permissive since secure claim is about claiming already received funds
        if (!userAccount) {
            console.log('canSubmitSecureClaim: No userAccount');
            return false;
        }
        
        // If we have account data, use it for validation
        if (accountData) {
            const canSubmit = accountData.exists && !accountData.pendingAction;
            console.log('canSubmitSecureClaim (with accountData):', {
                exists: accountData.exists,
                pendingAction: accountData.pendingAction,
                finalized: accountData.finalized,
                canSubmit
            });
            return canSubmit;
        }
        
        // If no account data but we have a user account, allow it
        // This handles cases where account data hasn't loaded yet
        console.log('canSubmitSecureClaim: No accountData but userAccount exists, allowing');
        return true;
    };

    // Request tracking functions
    const addPendingRequest = async (requestId, operationKind, details, transactionHash, amount = null, randomness = null) => {
        if (!userAccount) return;
        
        try {
            // Get current chainId from selected token data - convert to numeric chainId for consistency
            const chainKey = getTokenData().chainId; // This is the chain key like 'arbitrum'
            const chainConfig = CHAINS[chainKey];
            let numericChainId = null;
            if (chainKey && chainConfig) {
                const n = numericChainIdFromChainKey(chainKey);
                numericChainId = Number.isFinite(n) ? n : null;
            }
            if (numericChainId === null && chainKey === 'stellar') {
                numericChainId = 0;
            }

            // Transfers: backend validation expects sender, recipient, and token contract (Stellar `C…` strkey).
            let detailsNorm = { ...details };
            if (operationKind === 2) {
                const td = getTokenData();
                detailsNorm = {
                    ...detailsNorm,
                    sender: (detailsNorm.sender || userAccount).toLowerCase(),
                    tokenAddress:
                        detailsNorm.tokenAddress ||
                        (td && td.address ? String(td.address).toLowerCase() : undefined),
                };
            }

            console.log('Adding pending request:', { requestId, operationKind, details: detailsNorm, transactionHash, chainId: numericChainId, chainKey, amount });
            
            // Encrypt sender randomness using IBE if this is a transfer and randomness is provided
            // NOTE: sender_randomness corresponds to transfer_amount_source_ciphertext_lo (transfer amount encrypted for sender)
            // This randomness can be used by auditors to decrypt the transfer amount ciphertext for the sender
            let encryptedSenderRandomness = null;
            if (operationKind === 2 && randomness && randomness.sender) { // 2 = Transfer
                try {
                    console.log('[Transfer] Encrypting sender randomness for:', userAccount.toLowerCase(), 'on chain:', chainKey);
                    console.log('[Transfer] This randomness is for transfer_amount_source_ciphertext_lo (transfer amount encrypted for sender)');
                    encryptedSenderRandomness = await encryptRandomness(userAccount, chainKey, randomness.sender);
                    console.log('[Transfer] Sender randomness encrypted successfully');
                } catch (error) {
                    console.error('Error encrypting sender randomness:', error);
                    // Don't fail the whole operation if encryption fails
                }
            }

            // Receiver randomness on sender payload so backend can mirror `received-*` without a second POST (Stellar + EVM).
            let encryptedReceiverRandomness = null;
            if (operationKind === 2 && detailsNorm.recipient && randomness && randomness.receiver) {
                try {
                    encryptedReceiverRandomness = await encryptRandomness(
                        detailsNorm.recipient,
                        chainKey,
                        randomness.receiver,
                    );
                    console.log('[Transfer] Receiver randomness encrypted successfully');
                } catch (error) {
                    console.error('Error encrypting receiver randomness:', error);
                }
            }

            // Create sender's record
            const requestData = {
                requestId,
                userAddress: userAccount.toLowerCase(),
                operationKind,
                type: getOperationTypeName(operationKind),
                details: detailsNorm,
                transactionHash,
                chainId: numericChainId, // Store numeric chainId for document ID consistency
                chainKey: chainKey, // Also store chain key for display/filtering
                status: 'pending'
            };
            
            // Add encrypted sender randomness if available
            if (encryptedSenderRandomness) {
                requestData.encryptedSenderRandomness = encryptedSenderRandomness;
            }
            if (encryptedReceiverRandomness) {
                requestData.encryptedReceiverRandomness = encryptedReceiverRandomness;
            }

            console.log('Request data to be added:', requestData);
            try {
                await RequestService.addRequest(requestData);
                console.log('Request added to Firebase:', requestId);
            } catch (error) {
                // RequestService.addRequest already retries, so if we get here, all retries failed
                console.error('Failed to add request to Firebase after retries:', error);
                showNotification(
                    'Warning: Transaction was submitted but could not be saved to history. It may still appear after refresh.',
                    'warning'
                );
                // Don't throw - the transaction was successful, just the history tracking failed
            }
            
            // If this is a transfer, also create a receiver record (EVM / legacy). Stellar uses backend mirror from sender POST.
            if (operationKind === 2 && detailsNorm.recipient && numericChainId !== 0) { // 2 = Transfer
                try {
                    const receiverRequestId = `received-${requestId}-${detailsNorm.recipient.toLowerCase()}`;
                    
                    // Encrypt receiver randomness using IBE if randomness is provided
                    let encryptedReceiverRandomness = null;
                    if (randomness && randomness.receiver) {
                        try {
                            console.log('[Transfer] Encrypting receiver randomness for:', detailsNorm.recipient.toLowerCase(), 'on chain:', chainKey);
                            encryptedReceiverRandomness = await encryptRandomness(detailsNorm.recipient, chainKey, randomness.receiver);
                            console.log('[Transfer] Receiver randomness encrypted successfully');
                        } catch (error) {
                            console.error('Error encrypting receiver randomness:', error);
                            // Don't fail the whole operation if encryption fails
                        }
                    }
                    
                    const receiverRequestData = {
                        requestId: receiverRequestId,
                        userAddress: detailsNorm.recipient.toLowerCase(),
                        operationKind: 2,
                        type: 'received',
                        details: {
                            sender: userAccount.toLowerCase(),
                            recipient: detailsNorm.recipient.toLowerCase(),
                            token: detailsNorm.token,
                            tokenAddress: detailsNorm.tokenAddress,
                        },
                        transactionHash,
                        chainId: numericChainId, // Use numeric chainId for consistency
                        chainKey: chainKey, // Also store chain key for display/filtering
                        status: 'pending'
                    };
                    
                    // Add encrypted receiver randomness if available
                    if (encryptedReceiverRandomness) {
                        receiverRequestData.encryptedReceiverRandomness = encryptedReceiverRandomness;
                    }
                    
                    try {
                        await RequestService.addRequest(receiverRequestData);
                        console.log('Receiver record added to Firebase:', receiverRequestId);
                    } catch (error) {
                        console.error('Error adding receiver record after retries:', error);
                        // Don't show notification for receiver record failures - it's less critical
                        // The receiver can still see the transaction from contract events
                    }
                } catch (error) {
                    console.error('Error creating receiver record:', error);
                    // Don't fail the whole operation if receiver record fails
                }
            }
        } catch (error) {
            console.error('Unexpected error in addPendingRequest:', error);
            // This shouldn't happen, but if it does, log it
        }
    };
    
    // Helper to get initial chain - restore from sessionStorage on page refresh
    // Using sessionStorage so it clears when tab is closed, but persists on refresh
    const getInitialChainId = () => {
        try {
            const saved = sessionStorage.getItem('selectedChainId');
            // Validate that the saved chain exists and is active
            if (saved && CHAINS[saved]?.active) {
                console.log('Restoring chain from sessionStorage:', saved);
                return saved;
            }
        } catch (e) {
            console.warn('Error reading selectedChainId from sessionStorage:', e);
        }
        return 'stellar';
    };
    
    // Helper to get initial token - restore from sessionStorage on page refresh
    const getInitialTokenId = () => {
        try {
            const savedChain = sessionStorage.getItem('selectedChainId');
            const savedToken = sessionStorage.getItem('selectedTokenId');
            // Validate that the saved token exists for the saved chain
            if (savedChain && savedToken && CHAINS[savedChain]?.tokens?.[savedToken]) {
                console.log('Restoring token from sessionStorage:', savedToken);
                return savedToken;
            }
        } catch (e) {
            console.warn('Error reading selectedTokenId from sessionStorage:', e);
        }
        return CHAINS.stellar?.tokens?.usdc ? 'usdc' : null;
    };
    
    // Helper to get initial token data - reconstruct from sessionStorage values
    const getInitialTokenData = () => {
        try {
            const savedChain = sessionStorage.getItem('selectedChainId');
            const savedToken = sessionStorage.getItem('selectedTokenId');
            if (savedChain && savedToken && CHAINS[savedChain]?.tokens?.[savedToken]) {
                const chainConfig = CHAINS[savedChain];
                const tokenConfig = chainConfig.tokens[savedToken];
                console.log('Restoring token data from sessionStorage for:', savedChain, savedToken);
                return {
                    ...tokenConfig,
                    id: `${savedChain}_${savedToken}`,
                    chainId: savedChain,
                    chainName: chainConfig.name,
                    chainIcon: chainConfig.icon,
                    chainColor: chainConfig.color,
                    chainActive: chainConfig.active
                };
            }
        } catch (e) {
            console.warn('Error reconstructing token data from sessionStorage:', e);
        }
        if (CHAINS.stellar?.tokens?.usdc) {
            const chainConfig = CHAINS.stellar;
            const tokenConfig = chainConfig.tokens.usdc;
            return {
                ...tokenConfig,
                id: 'stellar_usdc',
                chainId: 'stellar',
                chainName: chainConfig.name,
                chainIcon: chainConfig.icon,
                chainColor: chainConfig.color,
                chainActive: chainConfig.active,
            };
        }
        return null;
    };
    
    // Common state
    const [transferAmount, setTransferAmount] = useState('');
    const transferAmountRef = useRef('');
    transferAmountRef.current = transferAmount;
    const [depositAmount, setDepositAmount] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [transferAmountError, setTransferAmountError] = useState('');
    const [depositAmountError, setDepositAmountError] = useState('');
    const [withdrawAmountError, setWithdrawAmountError] = useState('');
    const depositAmountRef = useRef('');
    depositAmountRef.current = depositAmount;
    const withdrawAmountRef = useRef('');
    withdrawAmountRef.current = withdrawAmount;
    const [selectedChainId, setSelectedChainId] = useState(getInitialChainId);
    const [selectedTokenId, setSelectedTokenId] = useState(getInitialTokenId);
    const [selectedTokenData, setSelectedTokenData] = useState(getInitialTokenData);
    const [showTokenSelector, setShowTokenSelector] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isApplyingPending, setIsApplyingPending] = useState(false);
    const [isFaucetProcessing, setIsFaucetProcessing] = useState(false);
    const [balance, setBalance] = useState('Loading...');
    const [pendingBalance, setPendingBalance] = useState(null);
    const [showPendingNotification, setShowPendingNotification] = useState(false);
    const [pendingNotificationDismissed, setPendingNotificationDismissed] = useState(false);
    const pendingNotificationRef = useRef(null);
    const [hasPendingDeposit, setHasPendingDeposit] = useState(false);
    const [pendingActionKind, setPendingActionKind] = useState(null); // Track pending action kind: 2=deposit, 3=transfer, 5=withdraw
    
    // Key derivation consent state
    const [keyDerivationConsent, setKeyDerivationConsent] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [keysDerived, setKeysDerived] = useState(false);
    const [userCancelledKeyDerivation, setUserCancelledKeyDerivation] = useState(false);
    const [accountCreationFailed, setAccountCreationFailed] = useState(false);
    const [isFirstTimeConnection, setIsFirstTimeConnection] = useState(true);
    
    // State for account creation during key derivation
    const [isCreatingAccount, setIsCreatingAccount] = useState(false);
    const [accountCreationStep, setAccountCreationStep] = useState('');
    const accountCreationAttemptedRef = useRef(false);
    const accountCreationCancelledRef = useRef(false);
    const accountCreationInProgressRef = useRef(false);
    const lastBalanceErrorShownRef = useRef(null); // Track last balance error to prevent duplicates
    /** Sync dismiss flag when a new inbound pending balance appears (overlay + parent banner logic). */
    const prevPendingForDismissRef = useRef(null);
    const onOperationCompleteRef = useRef(onOperationComplete);
    const secureClaimHandlerRef = useRef(null);
    const loadingChainRef = useRef(null); // Track which chain we're loading for
    const loadingTokenRef = useRef(null); // Track which token we're loading for
    const loadingPendingBalanceRef = useRef(false); // Track if we're currently loading pending balance
    /** Dedupe UI warning when derived ElGamal pubkey ≠ on-chain `get_account_core` pubkey. */
    const elGamalOnChainMismatchNotifiedKeyRef = useRef(null);
    const recipientTransferTimeoutRef = useRef(null); // Track pending timeout for recipient transfer listener
    // Cache balances per chain+token combination to avoid unnecessary reloads
    const balanceCacheRef = useRef(new Map()); // key: `${chainId}_${tokenId}`, value: { available: number, pending: number }
    const justRestoredFromCacheRef = useRef(false); // Track if we just restored balances from cache
    const lastLoadedKeyRef = useRef(null); // Track last loaded chain+token key to avoid reloading immediately after cache restore
    const accountStatusCheckInProgressRef = useRef(false); // Track if account status check is currently running
    const lastAccountStatusCheckRef = useRef(0); // Track last time we checked account status (for throttling)
    const prevPendingActionKindRef = useRef(null); // Track previous pendingActionKind to detect when operation completes
    /** Latest chain+token + resolved token row for public (wallet) balance — avoids stale closures in intervals / delayed callbacks. */
    const publicBalanceTokenRef = useRef({ key: '', data: null });
    const selectedChainIdForBalanceRef = useRef(selectedChainId);

    // Utility helpers
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    // Derived keypair (in-memory only)
    // Key derivation is now handled by the WASM hook

    // Transfer-specific state
    const [recipient, setRecipient] = useState('');
    const [recipientError, setRecipientError] = useState(null);
    const [recipientAccountStatus, setRecipientAccountStatus] = useState(null); // null | 'checking' | 'has_account' | 'no_account'
    const recipientCheckTimeoutRef = useRef(null);
    const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);

    // Get the selected chain's chainId for key derivation
    const getSelectedChainId = () => {
        const selectedChain = CHAINS[selectedChainId];
        return selectedChain ? selectedChain.chainIdDecimal : null;
    };
    
    // Get the selected chain's contract address for key derivation
    const getSelectedChainContractAddress = () => {
        const selectedChain = CHAINS[selectedChainId];
        return selectedChain ? selectedChain.contractAddress : null;
    };
    
    const selectedChainIdForKeys = getSelectedChainId();
    const selectedChainContractAddress = getSelectedChainContractAddress();

    const STELLAR_WALLET_ORIGINS = new Set([
        'freighter',
        'hana',
        'xbull',
        'lobstr',
        'albedo',
        'rabet',
        'stellar',
    ]);
    const isStellarWalletOrigin = (origin) => !!origin && STELLAR_WALLET_ORIGINS.has(origin);

    const getStellarTxContext = () => {
        if (selectedChainId !== 'stellar' || !userAccount) return null;
        if (!isStellarWalletOrigin(activeWalletOrigin)) return null;
        const stellar = CHAINS.stellar;
        if (!stellar?.contractAddress) return null;
        return {
            rpcUrl: stellar.rpcUrl,
            networkPassphrase: stellar.networkPassphrase,
            contractId: stellar.contractAddress,
            source: userAccount,
        };
    };
    
    // Only provide verifyingContract when user has selected both chain and token
    const verifyingContract = (selectedChainId && selectedTokenId)
        ? (selectedChainContractAddress || null)
        : null;
    
    const stellarWalletForKeys = isStellarWalletOrigin(activeWalletOrigin) ? activeWalletOrigin : null;

    const { keypair, loading: keyLoading, error: keyError, needsConsent } = useDeterministicElGamalKeypair(
        provider, 
        userAccount, 
        selectedChainIdForKeys, 
        verifyingContract,
        keyDerivationConsent,
        stellarWalletForKeys
    );

    // Debug keypair state changes
    useEffect(() => {
        console.log('Keypair state changed:', {
            keypairExists: !!keypair,
            hasPrivateKey: keypair?.private_key ? true : false,
            keypairLoading: keyLoading,
            keypairError: keyError,
            privateKeyLength: keypair?.private_key?.length,
            selectedChainId,
            selectedChainIdForKeys,
            selectedChainContractAddress,
            verifyingContract,
            userAccount: userAccount ? `${userAccount.slice(0, 6)}...${userAccount.slice(-4)}` : null
        });
    }, [keypair, keyLoading, keyError, selectedChainId, selectedChainIdForKeys, selectedChainContractAddress, verifyingContract, userAccount]);

    // Notify parent when keypair changes
    useEffect(() => {
        if (onKeypairUpdate) {
            onKeypairUpdate(keypair);
        }
        if (keypair) {
            setKeysDerived(true);
            // Mark this wallet+account combination as connected when keys are successfully derived
            if (userAccount && selectedChainIdForKeys && verifyingContract) {
                secureKeyCache.markAsConnected(userAccount, selectedChainIdForKeys, verifyingContract);
            }
        } else {
            setKeysDerived(false);
        }
    }, [keypair, onKeypairUpdate, userAccount, selectedChainIdForKeys, verifyingContract]);

    // Keep onOperationComplete ref updated
    useEffect(() => {
        onOperationCompleteRef.current = onOperationComplete;
    }, [onOperationComplete]);

    // Automatically create account after keys are derived if account doesn't exist
    useEffect(() => {
        // Only run when keys are actually derived and account creation hasn't failed
        // Don't auto-retry if user cancelled account creation
        // Also require that both chain and token are selected
        // Require a bound contract reader — Stellar kit alone is not enough (would skip `getAccountCore`
        // and treat `accountData` as stale, then hit `AccountAlreadyExists` on create).
        const hasTxPath = !!contract && typeof contract.getAccountCore === 'function';
        if (!keysDerived || !keypair || accountCreationFailed || !hasTxPath || !userAccount || !selectedChainId || !selectedTokenId) {
            return;
        }

        // --- StrictMode-safe session guard ---
        const sessionKey = makeAccountCreationKey(
            userAccount,
            selectedChainIdForKeys,
            verifyingContract
        );

        if (accountCreationSessions.has(sessionKey)) {
            console.log('autoCreateAccount: session already handled, skipping (StrictMode guard)');
            return;
        }

        // Existing in-component guards
        if (accountCreationAttemptedRef.current || accountCreationInProgressRef.current) {
            console.log('autoCreateAccount: Account creation already attempted or in progress, skipping');
            return;
        }

        if (isCreatingAccount) {
            console.log('autoCreateAccount: Account creation already in progress (state), skipping');
            return;
        }

        // Mark this session as handled *before* any async work so second mount can't re-trigger it
        accountCreationSessions.add(sessionKey);

        // Set ALL guards atomically before any async operations to prevent race conditions
        accountCreationAttemptedRef.current = true;
        accountCreationInProgressRef.current = true;
        accountCreationCancelledRef.current = false;
        setIsCreatingAccount(true); // Set state immediately to prevent duplicate runs

        const autoCreateAccount = async () => {
            console.log('autoCreateAccount: Checking conditions...', {
                hasKeypair: !!keypair,
                keysDerived,
                hasContract: !!contract,
                hasUserAccount: !!userAccount,
                accountCreationAttempted: accountCreationAttemptedRef.current,
                isCreatingAccount
            });

            // Only proceed if:
            // 1. Keys are derived (keypair exists)
            // 2. We have a contract and user account
            // 3. We haven't already attempted account creation for this key derivation
            if (!keypair || !hasTxPath || !userAccount) {
                console.log('autoCreateAccount: Missing required dependencies, skipping');
                // Allow retry if dependencies were missing
                accountCreationAttemptedRef.current = false;
                accountCreationInProgressRef.current = false;
                accountCreationSessions.delete(sessionKey);
                setIsCreatingAccount(false);
                return;
            }

            if (accountCreationCancelledRef.current) {
                console.log('autoCreateAccount: Cancelled, skipping');
                accountCreationAttemptedRef.current = false;
                accountCreationInProgressRef.current = false;
                accountCreationSessions.delete(sessionKey);
                setIsCreatingAccount(false);
                return;
            }

            try {
                console.log('autoCreateAccount: Checking if account exists...');
                // Check if account exists from contract
                let accountExists = false;
                if (contract && typeof contract.getAccountCore === 'function') {
                    try {
                        const accountDataTuple = await contract.getAccountCore(userAccount);
                        accountExists = accountDataTuple[0]; // exists field
                        console.log('autoCreateAccount: Account exists check result:', accountExists, 'Full tuple:', accountDataTuple);
                    } catch (checkError) {
                        // If getAccountCore reverts (CALL_EXCEPTION), it means the account doesn't exist
                        // This is expected behavior when checking for a non-existent account
                        if (checkError.code === 'CALL_EXCEPTION' || checkError.message?.includes('CALL_EXCEPTION')) {
                            console.log('autoCreateAccount: Account does not exist (contract reverted on getAccountCore)');
                            accountExists = false;
                        } else {
                            // If it's a different error, re-throw it
                            throw checkError;
                        }
                    }
                } else {
                    // No contract reader (should be rare after Stellar facade loads).
                    accountExists = Boolean(accountData?.exists);
                }
                
                if (!accountExists) {
                    if (accountCreationCancelledRef.current) {
                        console.log('autoCreateAccount: Cancelled before account creation');
                        accountCreationAttemptedRef.current = false;
                        accountCreationInProgressRef.current = false;
                        accountCreationSessions.delete(sessionKey);
                        setIsCreatingAccount(false);
                        return;
                    }
                    console.log('Account does not exist, checking balances before creating...');
                    
                    // Check gas and token balances before attempting account creation
                    setAccountCreationStep('Checking balances...');
                    
                    // Get chain configuration
                    const chainConfig = CHAINS[selectedChainId];
                    const nativeCurrencySymbol = chainConfig?.nativeCurrency?.symbol || 'ETH';
                    const nativeCurrencyDecimals = chainConfig?.nativeCurrency?.decimals || 18;
                    const isTempo = selectedChainId === 'tempo';
                    const isStellarChain = selectedChainId === 'stellar';
                    
                    // Check gas balance (or fee token balance for Tempo)
                    let gasBalance;
                    let hasInsufficientGas = false;
                    let feeTokenInfo = null;
                    
                    if (isStellarChain) {
                        console.log('Stellar account creation: skipping EVM gas/token preflight checks');
                    } else if (isTempo) {
                        // For Tempo, check if user has any fee token balance
                        try {
                            feeTokenInfo = await checkTempoFeeTokenBalance(provider, userAccount);
                            console.log('Tempo fee token check:', feeTokenInfo);
                            hasInsufficientGas = !feeTokenInfo.hasFeeToken;
                            if (feeTokenInfo.hasFeeToken) {
                                console.log(`Fee token balance: ${feeTokenInfo.feeTokenBalance} ${feeTokenInfo.feeTokenSymbol}`);
                            }
                        } catch (balanceError) {
                            console.error('Error checking Tempo fee token balance:', balanceError);
                            throw new Error('Failed to check fee token balance. Please check your network connection.');
                        }
                    } else {
                        // For other chains, check native gas balance with retry logic
                        // This is especially important for Privy on some networks like Stable
                        let lastError = null;
                        const maxRetries = 3;
                        for (let attempt = 0; attempt < maxRetries; attempt++) {
                            try {
                                if (attempt > 0) {
                                    // Wait before retry (exponential backoff)
                                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                                    console.log(`Retrying gas balance check (attempt ${attempt + 1}/${maxRetries})...`);
                                }
                                gasBalance = await provider.getBalance(userAccount);
                                // Use formatUnits with correct decimals instead of formatEther
                                const formattedBalance = ethers.utils.formatUnits(gasBalance, nativeCurrencyDecimals);
                                console.log('Gas balance:', formattedBalance, nativeCurrencySymbol);
                                hasInsufficientGas = gasBalance.isZero();
                                lastError = null; // Success, clear error
                                break; // Exit retry loop on success
                            } catch (balanceError) {
                                lastError = balanceError;
                                console.error(`Error checking gas balance (attempt ${attempt + 1}/${maxRetries}):`, balanceError);
                        // If this is the last attempt, throw the error
                        if (attempt === maxRetries - 1) {
                            // Provide more specific error message
                            const errorMessage = balanceError.message || String(balanceError);
                            if (isSSLError(balanceError)) {
                                const chainName = chainConfig?.name || 'this network';
                                throw new Error(`SSL certificate error connecting to ${chainName} RPC endpoint. The network may be experiencing issues. Please try again later.`);
                            } else if (errorMessage.includes('network') || errorMessage.includes('connection') || errorMessage.includes('timeout') || errorMessage.includes('HTTP request failed') || errorMessage.includes('fetch failed') || errorMessage.includes('RPC')) {
                                throw new Error('RPC connection error: Unable to connect to the network. This is likely a temporary issue. Please retry the operation.');
                            } else {
                                throw new Error(`RPC error: ${errorMessage}. This is likely a temporary network issue. Please retry the operation.`);
                            }
                        }
                            }
                        }
                    }
                    
                    // Check token balance
                    let hasInsufficientToken = false;
                    let tokenSymbol = null;
                    const tokenData = getTokenData();
                    if (!isStellarChain && tokenData && tokenData.address && tokenData.address !== '0x0000000000000000000000000000000000000000') {
                        try {
                            const tokenContract = new ethers.Contract(
                                tokenData.address,
                                ['function balanceOf(address) view returns (uint256)'],
                                provider
                            );
                            const tokenBalance = await tokenContract.balanceOf(userAccount);
                            const formattedTokenBalance = ethers.utils.formatUnits(tokenBalance, tokenData.decimals || 18);
                            console.log('Token balance:', formattedTokenBalance, tokenData.symbol);
                            hasInsufficientToken = tokenBalance.isZero();
                            tokenSymbol = tokenData.symbol;
                        } catch (tokenBalanceError) {
                            console.error('Error checking token balance:', tokenBalanceError);
                            // Don't block account creation if we can't check token balance
                            console.log('Could not check token balance, proceeding with account creation');
                        }
                    }
                    
                    // Show combined or individual error messages
                    if (!isStellarChain && hasInsufficientGas && hasInsufficientToken) {
                        let errorMsg;
                        if (isTempo) {
                            const feeTokenSymbols = getTempoFeeTokenSymbols().join(', ');
                            errorMsg = (
                                <>
                                    Insufficient testnet balances. You need one of the following testnet tokens to pay for transaction fees: {feeTokenSymbols}. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet tokens</a>, or switch to another chain/token.
                                </>
                            );
                        } else {
                            // Special case for Arc where both gas and token are USDC
                            const isArc = selectedChainId === 'arc';
                            if (isArc && nativeCurrencySymbol === tokenSymbol) {
                                errorMsg = (
                                    <>
                                        Insufficient testnet {tokenSymbol} balance. You need testnet {tokenSymbol} tokens to pay for transaction fees and use this application. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {tokenSymbol}</a>, or switch to another chain/token.
                                    </>
                                );
                            } else {
                                errorMsg = (
                                    <>
                                        Insufficient testnet balances. You need testnet {nativeCurrencySymbol} to pay for transaction fees and testnet {tokenSymbol} tokens to use this application. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {nativeCurrencySymbol} and testnet {tokenSymbol}</a>, or switch to another chain/token.
                                    </>
                                );
                            }
                        }
                        // Only show notification if we haven't shown this exact error before
                        const errorKey = `insufficient-both-${selectedChainId}-${tokenSymbol}`;
                        if (lastBalanceErrorShownRef.current !== errorKey) {
                            showNotification(errorMsg, 'error', true);
                            lastBalanceErrorShownRef.current = errorKey;
                        }
                        setAccountCreationStep('');
                        // Reset states to allow retry
                        accountCreationAttemptedRef.current = false;
                        accountCreationInProgressRef.current = false;
                        accountCreationSessions.delete(sessionKey);
                        setIsCreatingAccount(false);
                        setAccountCreationFailed(true);
                        setKeysDerived(false);
                        setKeyDerivationConsent(false);
                        return;
                    } else if (!isStellarChain && hasInsufficientGas) {
                        let errorMsg;
                        if (isTempo) {
                            const feeTokenSymbols = getTempoFeeTokenSymbols().join(', ');
                            errorMsg = (
                                <>
                                    Insufficient testnet fee token balance. You need one of the following testnet fee tokens to pay for transaction fees: {feeTokenSymbols}. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet fee tokens</a> or switch to another chain.
                                </>
                            );
                        } else {
                            errorMsg = (
                                <>
                                    Insufficient testnet gas balance. You need testnet {nativeCurrencySymbol} to pay for transaction fees. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {nativeCurrencySymbol}</a> or switch to another chain.
                                </>
                            );
                        }
                        // Only show notification if we haven't shown this exact error before
                        const errorKey = `insufficient-gas-${selectedChainId}`;
                        if (lastBalanceErrorShownRef.current !== errorKey) {
                            showNotification(errorMsg, 'error', true);
                            lastBalanceErrorShownRef.current = errorKey;
                        }
                        setAccountCreationStep('');
                        // Reset states to allow retry
                        accountCreationAttemptedRef.current = false;
                        accountCreationInProgressRef.current = false;
                        accountCreationSessions.delete(sessionKey);
                        setIsCreatingAccount(false);
                        setAccountCreationFailed(true);
                        setKeysDerived(false);
                        setKeyDerivationConsent(false);
                        return;
                    } else if (!isStellarChain && hasInsufficientToken) {
                        // User has gas but no token - show warning but continue with account creation
                        // since they can pay for gas to create the account
                        const warningMsg = (
                            <>
                                You don't have testnet {tokenSymbol} tokens yet, but you can still create an account since you have sufficient gas. You'll need testnet {tokenSymbol} tokens to use this application after account creation. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {tokenSymbol} tokens</a>.
                            </>
                        );
                        // Only show notification if we haven't shown this exact message before
                        const warningKey = `warning-token-${selectedChainId}-${tokenSymbol}`;
                        if (lastBalanceErrorShownRef.current !== warningKey) {
                            showNotification(warningMsg, 'warning', true);
                            lastBalanceErrorShownRef.current = warningKey;
                        }
                        // Continue with account creation instead of returning
                        console.log('Insufficient token balance, but proceeding with account creation since gas balance is sufficient');
                    }
                    
                    console.log('Balances verified. Creating account...');
                    setAccountCreationStep('Creating account...');
                    
                    // Convert Base64 public key to hex for contract
                    const pkBase64 = keypair.public_key;
                    const pkBytes = Uint8Array.from(atob(pkBase64), c => c.charCodeAt(0));
                    const pkHexForContract = '0x' + Array.from(pkBytes).map(b => b.toString(16).padStart(2, '0')).join('');

                    const stellarTxContext = getStellarTxContext();
                    if (stellarTxContext && contract && typeof contract.getAccountCore === 'function') {
                        try {
                            const freshTuple = await contract.getAccountCore(userAccount);
                            const existsOnChain =
                                freshTuple[0] === true || freshTuple[0] === 1 || freshTuple[0] === 1n;
                            if (existsOnChain) {
                                console.log(
                                    'autoCreateAccount: Soroban re-check — account already exists on contract, skipping create'
                                );
                                accountCreationInProgressRef.current = false;
                                accountCreationSessions.delete(sessionKey);
                                setIsCreatingAccount(false);
                                setAccountCreationStep('');
                                if (onOperationCompleteRef.current) {
                                    await onOperationCompleteRef.current();
                                }
                                return;
                            }
                        } catch (reCheckErr) {
                            console.warn('autoCreateAccount: Soroban pre-create re-check failed:', reCheckErr);
                        }
                    }
                    
                    console.log('autoCreateAccount: Submitting account creation transaction with public key:', pkHexForContract.substring(0, 20) + '...');
                    // Submit to contract with hex public key
                    setAccountCreationStep('Submitting account creation transaction...');
                    let tx;
                    if (stellarTxContext) {
                        tx = await executeContractTxWithGasFallback(
                            contract,
                            'createConfidentialAccount',
                            [pkHexForContract],
                            { stellarContext: stellarTxContext }
                        );
                    } else {
                    // Add gas fee overrides to prevent "maxFeePerGas less than block base fee" errors
                    const gasOverrides = await getGasFeeOverrides();
                    
                    // Try to estimate gas first, and if it fails, use a manual gas limit
                    try {
                        // First, try to estimate gas to see if the transaction would succeed
                        const estimatedGas = await contract.estimateGas.createConfidentialAccount(pkHexForContract, gasOverrides);
                        // If estimation succeeds, add a 20% buffer and include it in overrides
                        const gasLimit = estimatedGas.mul(120).div(100);
                        const overridesWithGas = { ...gasOverrides, gasLimit };
                        tx = await contract.createConfidentialAccount(pkHexForContract, overridesWithGas);
                    } catch (estimateError) {
                        // Check if this is a gas-related error that can be fixed with a manual gas limit
                        // This includes UNPREDICTABLE_GAS_LIMIT and "gas required exceeds allowance" errors
                        const isGasError = estimateError.code === 'UNPREDICTABLE_GAS_LIMIT' || 
                                          estimateError.code === -32000 ||
                                          (estimateError.message && (
                                              estimateError.message.includes('UNPREDICTABLE_GAS_LIMIT') ||
                                              estimateError.message.includes('cannot estimate gas') ||
                                              estimateError.message.includes('gas required exceeds allowance')
                                          )) ||
                                          (estimateError.error && estimateError.error.message && (
                                              estimateError.error.message.includes('gas required exceeds allowance')
                                          ));
                        
                        if (isGasError) {
                            console.warn('Gas estimation failed, using manual gas limit:', estimateError);
                            // Use a higher gas limit to ensure the transaction goes through
                            // Account creation typically needs more gas than simple transfers
                            const manualGasLimit = 1000000; // Increased from 500000 to handle Ethereum's requirements
                            const overridesWithManualGas = { ...gasOverrides, gasLimit: manualGasLimit };
                            console.log('Using manual gas limit:', manualGasLimit);
                            
                            // Try the transaction with manual gas limit, and if it still fails with gas error, try without estimation
                            try {
                                tx = await contract.createConfidentialAccount(pkHexForContract, overridesWithManualGas);
                            } catch (txError) {
                                // If the transaction call itself fails with a gas error, it might be trying to estimate again
                                // In this case, we need to use the contract's populateTransaction method and send it directly
                                const isTxGasError = txError.code === 'UNPREDICTABLE_GAS_LIMIT' || 
                                                     txError.code === -32000 ||
                                                     (txError.message && (
                                                         txError.message.includes('UNPREDICTABLE_GAS_LIMIT') ||
                                                         txError.message.includes('cannot estimate gas') ||
                                                         txError.message.includes('gas required exceeds allowance')
                                                     ));
                                
                                if (isTxGasError) {
                                    console.warn('Transaction call also failed with gas error, using populateTransaction:', txError);
                                    // Use populateTransaction to build the transaction without estimation, then send it
                                    const populatedTx = await contract.populateTransaction.createConfidentialAccount(pkHexForContract);
                                    populatedTx.gasLimit = manualGasLimit;
                                    if (overridesWithManualGas.maxFeePerGas) {
                                        populatedTx.maxFeePerGas = overridesWithManualGas.maxFeePerGas;
                                    }
                                    if (overridesWithManualGas.maxPriorityFeePerGas) {
                                        populatedTx.maxPriorityFeePerGas = overridesWithManualGas.maxPriorityFeePerGas;
                                    }
                                    tx = await contract.signer.sendTransaction(populatedTx);
                                } else {
                                    throw txError;
                                }
                            }
                        } else {
                            // For other errors, re-throw them as they indicate actual transaction failures
                            throw estimateError;
                        }
                    }
                    }
                    
                    console.log('autoCreateAccount: Transaction submitted, waiting for confirmation...', tx.hash);
                    setAccountCreationStep('Waiting for transaction confirmation...');
                    await tx.wait();
                    
                    console.log('autoCreateAccount: Transaction confirmed!');
                    setAccountCreationStep('Waiting for account to be activated...');
                    
                    // Wait for account to be processed and finalized
                    let attempts = 0;
                    const maxAttempts = 30; // 30 seconds
                    
                    while (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        
                        // Refresh account status
                        await onOperationCompleteRef.current();
                        
                        // Check if account is now active by checking the contract directly (EVM path)
                        if (contract && typeof contract.getAccountCore === 'function') {
                            try {
                                const accountDataTuple = await contract.getAccountCore(userAccount);
                                const isActive = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized
                                
                                if (isActive) {
                                    console.log('autoCreateAccount: Account activated successfully!');
                                    setAccountCreationStep('Account created successfully!');
                                    showNotification('Account created successfully!', 'success');
                                    // Clear account creation failed flag on success
                                    setAccountCreationFailed(false);
                                    
                                    // Final refresh to ensure UI updates
                                    await onOperationCompleteRef.current();
                                    
                                    setTimeout(() => {
                                        setIsCreatingAccount(false);
                                        setAccountCreationStep('');
                                        accountCreationInProgressRef.current = false;
                                    }, 2000);
                                    return; // Exit the function successfully
                                }
                            } catch (error) {
                                // If getAccountCore still reverts, account isn't ready yet
                                if (error.code === 'CALL_EXCEPTION' || error.message?.includes('CALL_EXCEPTION')) {
                                    // Account still doesn't exist, continue waiting
                                } else {
                                    console.error('Error checking account activation status:', error);
                                }
                            }
                        }
                        
                        attempts++;
                        setAccountCreationStep(`Waiting for account to be activated... (${attempts}/${maxAttempts}s)`);
                    }
                    
                    // If we get here, account creation is still pending
                    setAccountCreationStep('Account creation pending. You can proceed with deposit.');
                  //  showNotification('Account creation is pending. You can proceed with deposit.', 'info');
                    
                    // Refresh account status one more time
                    await onOperationCompleteRef.current();
                    
                    setTimeout(() => {
                        setIsCreatingAccount(false);
                        setAccountCreationStep('');
                        accountCreationInProgressRef.current = false;
                    }, 2000);
                } else {
                    console.log('Account already exists, no need to create');
                    accountCreationInProgressRef.current = false;
                    // Keep sessionKey marked as done (don't delete - we don't want to retry)
                }
            } catch (error) {
                console.error('Error checking/creating account after key derivation:', error);
                console.error('Error details:', {
                    message: error.message,
                    code: error.code,
                    data: error.data,
                    stack: error.stack
                });
                // Only show error notification if we were actually trying to create
                if (accountCreationAttemptedRef.current) {
                    setAccountCreationStep('');
                    const cleanError = getCleanErrorMessage(error);
                    // Check if this is an RPC error and adjust message accordingly
                    const errorMessage = error.message || String(error);
                    const isRpcError = errorMessage.includes('HTTP request failed') || 
                                      errorMessage.includes('fetch failed') || 
                                      errorMessage.includes('RPC') || 
                                      errorMessage.includes('network') || 
                                      errorMessage.includes('connection') ||
                                      cleanError.includes('RPC connection error') ||
                                      cleanError.includes('RPC error');
                    if (isRpcError) {
                        showNotification(`Account creation failed due to RPC connection error. This is likely a temporary network issue. Please retry the operation.`, 'error');
                    } else {
                        showNotification(`Account creation failed: ${cleanError}`, 'error');
                    }
                    // Mark account creation as failed so "Get Keys" buttons show again
                    setAccountCreationFailed(true);
                    // Reset keysDerived so "Get Keys" buttons show again, allowing user to retry
                    // This allows users to retry account creation after cancellation/failure
                    setKeysDerived(false);
                    setKeyDerivationConsent(false); // Reset consent so they can go through flow again
                    // Reset the attempt flag so user can retry account creation
                    accountCreationAttemptedRef.current = false;
                    accountCreationInProgressRef.current = false;
                    // IMPORTANT: allow retry on *failure*
                    accountCreationSessions.delete(sessionKey);
                }
                setIsCreatingAccount(false);
            }
        };

        autoCreateAccount();

        // Cleanup function to cancel any pending operations if effect re-runs
        return () => {
            // Mark that this particular run was cancelled, so the async function can
            // early-out if it checks this ref.
            accountCreationCancelledRef.current = true;
            // DO NOT reset accountCreationAttemptedRef here
            // We only reset it explicitly on:
            //  - account/chain change effect
            //  - error paths where we want to allow a retry
        };
    }, [
        keysDerived,
        keypair,
        contract,
        userAccount,
        accountCreationFailed,
        selectedChainId,
        selectedTokenId,
        selectedChainIdForKeys,
        verifyingContract,
    ]);

    // Reset account creation attempt flag when account or chain changes
    useEffect(() => {
        accountCreationAttemptedRef.current = false;
        lastBalanceErrorShownRef.current = null; // Reset error tracking when account/chain changes
    }, [userAccount, selectedChainId]);

    // Reset isCreatingAccount state when account becomes active
    // This fixes the issue where the deposit button shows "Creating Account..." 
    // even when the account already exists and is active
    useEffect(() => {
        if (hasActiveAccount && accountExists && isCreatingAccount) {
            console.log('Account is active, resetting isCreatingAccount state');
            setIsCreatingAccount(false);
            setAccountCreationStep('');
            accountCreationInProgressRef.current = false;
        }
    }, [hasActiveAccount, accountExists, isCreatingAccount]);

    // Refresh account status when account creation completes
    // This fixes the issue where account is created on-chain but frontend state isn't updated
    const prevIsCreatingAccountRef = useRef(isCreatingAccount);
    useEffect(() => {
        const wasCreating = prevIsCreatingAccountRef.current;
        prevIsCreatingAccountRef.current = isCreatingAccount;

        // If account creation just completed (was creating, now not), refresh account status
        // Throttle to avoid excessive RPC calls (minimum 2 seconds between checks)
        if (wasCreating && !isCreatingAccount && onOperationCompleteRef.current) {
            const now = Date.now();
            const timeSinceLastCheck = now - lastAccountStatusCheckRef.current;
            const minInterval = 2000; // 2 seconds minimum between checks
            
            if (timeSinceLastCheck >= minInterval) {
                console.log('Account creation completed, refreshing account status...');
                lastAccountStatusCheckRef.current = now;
                // Small delay to allow blockchain state to propagate
                setTimeout(() => {
                    if (onOperationCompleteRef.current) {
                        onOperationCompleteRef.current();
                    }
                }, 1500);
            }
        }
    }, [isCreatingAccount]);

    // Check and refresh account status if it seems stale
    // This handles cases where account was created on-chain but state wasn't refreshed
    // Also handles cases where account exists but isn't finalized yet (hasActiveAccount = false)
    // Uses throttling and single-instance checks to avoid RPC overload
    useEffect(() => {
        if (!contract || !userAccount || !keysDerived) return;

        // If keys are derived but account is not active (either doesn't exist or isn't finalized),
        // check if account was actually created/finalized on-chain
        // Only check if not already checking and not too soon since last check
        const now = Date.now();
        const timeSinceLastCheck = now - lastAccountStatusCheckRef.current;
        const minInterval = 3000; // 3 seconds minimum between checks
        
        // Check if account creation is done but account isn't active yet
        // This handles the case where account creation takes longer than expected
        const shouldCheck = !hasActiveAccount && !isCreatingAccount && !accountCreationFailed && 
            !accountStatusCheckInProgressRef.current && timeSinceLastCheck >= minInterval;
        
        if (shouldCheck) {
            console.log('Keys derived but account is not active (exists:', accountExists, 'finalized:', hasActiveAccount, '), checking on-chain status...');
            
            let timeoutId = null;
            let cancelled = false;
            let checkCount = 0;
            const initialIntervalMs = 8000; // 8 seconds for first ~80 seconds (10 checks)
            const checksBeforeExponentialBackoff = 10; // First 10 checks at 8s intervals
            let currentIntervalMs = initialIntervalMs;
            const startTime = Date.now();
            
            // Calculate interval with exponential backoff after 80 seconds
            const getNextInterval = (attemptNumber) => {
                if (attemptNumber <= checksBeforeExponentialBackoff) {
                    // First ~80 seconds: fixed 8 second intervals
                    return initialIntervalMs;
                } else {
                    // After 80 seconds: exponential backoff starting at 16s
                    // Attempt 11: 16s, 12: 32s, 13: 64s, 14: 128s, etc.
                    const backoffAttempt = attemptNumber - checksBeforeExponentialBackoff;
                    return initialIntervalMs * Math.pow(2, backoffAttempt);
                }
            };
            
            // Recursive function to perform periodic checks
            const scheduleNextCheck = async () => {
                if (cancelled || accountStatusCheckInProgressRef.current) {
                    return;
                }
                
                // Throttle checks
                const now = Date.now();
                if (now - lastAccountStatusCheckRef.current < minInterval) {
                    // Reschedule with remaining time
                    const remainingTime = minInterval - (now - lastAccountStatusCheckRef.current);
                    timeoutId = setTimeout(scheduleNextCheck, remainingTime);
                    return;
                }
                
                accountStatusCheckInProgressRef.current = true;
                lastAccountStatusCheckRef.current = now;
                checkCount++;
                const elapsedSeconds = Math.floor((now - startTime) / 1000);
                const nextInterval = getNextInterval(checkCount + 1);
                const nextIntervalSeconds = Math.floor(nextInterval / 1000);
                
                if (checkCount <= checksBeforeExponentialBackoff) {
                    console.log(`Checking account activation status (attempt ${checkCount}, ${elapsedSeconds}s elapsed, next check in ${nextIntervalSeconds}s)...`);
                } else {
                    console.log(`Checking account activation status (attempt ${checkCount}, ${elapsedSeconds}s elapsed, exponential backoff: next check in ${nextIntervalSeconds}s)...`);
                }
                
                try {
                    // Directly check the contract to see if account exists and is finalized
                    const accountDataTuple = await contract.getAccountCore(userAccount);
                    const exists = accountDataTuple[0];
                    const finalized = accountDataTuple[1];
                    const isActive = exists && finalized;
                    
                    // If account is active (exists AND finalized), refresh the UI state
                    if (isActive && !cancelled && onOperationCompleteRef.current) {
                        console.log(`Account is now active on-chain (exists and finalized) after ${elapsedSeconds}s, refreshing UI state...`);
                        await onOperationCompleteRef.current();
                        accountStatusCheckInProgressRef.current = false;
                        return; // Stop checking - account is active
                    } else if (exists && !finalized) {
                        console.log(`Account exists but not finalized yet (${elapsedSeconds}s elapsed), will continue checking...`);
                    } else if (!exists) {
                        console.log(`Account does not exist yet (${elapsedSeconds}s elapsed), will continue checking...`);
                    }
                    
                    // Schedule next check with appropriate interval
                    accountStatusCheckInProgressRef.current = false;
                    currentIntervalMs = getNextInterval(checkCount + 1);
                    if (!cancelled) {
                        timeoutId = setTimeout(scheduleNextCheck, currentIntervalMs);
                    }
                } catch (error) {
                    accountStatusCheckInProgressRef.current = false;
                    if (cancelled) return;
                    
                    // If account doesn't exist yet, that's fine - we'll keep checking
                    if (error.code === 'CALL_EXCEPTION' || error.message?.includes('CALL_EXCEPTION')) {
                        console.log(`Account does not exist yet (${elapsedSeconds}s elapsed), will continue checking...`);
                    } else {
                        console.error('Error during periodic account status check:', error);
                    }
                    
                    // Schedule next check even on error
                    currentIntervalMs = getNextInterval(checkCount + 1);
                    if (!cancelled) {
                        timeoutId = setTimeout(scheduleNextCheck, currentIntervalMs);
                    }
                }
            };
            
            // Throttled immediate check
            const immediateCheck = async () => {
                if (accountStatusCheckInProgressRef.current || cancelled) return;
                
                accountStatusCheckInProgressRef.current = true;
                lastAccountStatusCheckRef.current = Date.now();
                
                try {
                    const accountDataTuple = await contract.getAccountCore(userAccount);
                    const exists = accountDataTuple[0];
                    const finalized = accountDataTuple[1];
                    const isActive = exists && finalized;
                    
                    if (isActive && !cancelled && onOperationCompleteRef.current) {
                        console.log('Account is now active on-chain (exists and finalized), refreshing UI state immediately...');
                        await onOperationCompleteRef.current();
                        accountStatusCheckInProgressRef.current = false;
                        return; // Account is active, no need for periodic checks
                    } else if (exists && !finalized) {
                        console.log('Account exists but not finalized yet, will continue checking indefinitely with exponential backoff...');
                    } else {
                        console.log('Account does not exist yet, will continue checking indefinitely with exponential backoff...');
                    }
                } catch (error) {
                    if (cancelled) {
                        accountStatusCheckInProgressRef.current = false;
                        return;
                    }
                    // If account doesn't exist yet, that's fine - we'll keep checking
                    if (error.code === 'CALL_EXCEPTION' || error.message?.includes('CALL_EXCEPTION')) {
                        console.log('Account does not exist yet, will continue checking indefinitely with exponential backoff...');
                    } else {
                        console.error('Error during immediate account status check:', error);
                    }
                }
                
                // If immediate check didn't find active account, set up periodic checks
                if (cancelled) {
                    accountStatusCheckInProgressRef.current = false;
                    return;
                }
                
                accountStatusCheckInProgressRef.current = false;
                console.log('Setting up periodic checks (8s intervals for first ~80s, then exponential backoff: 16s, 32s, 64s, 128s...)...');
                
                // Schedule first periodic check
                timeoutId = setTimeout(scheduleNextCheck, initialIntervalMs);
            };

            // Delay immediate check slightly to avoid race conditions
            const initialTimeoutId = setTimeout(() => {
                immediateCheck();
            }, 500);

            return () => {
                cancelled = true;
                clearTimeout(initialTimeoutId);
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                accountStatusCheckInProgressRef.current = false;
            };
        }
    }, [contract, userAccount, keysDerived, accountExists, hasActiveAccount, isCreatingAccount, accountCreationFailed]);

    // Show consent modal when keys need to be derived (e.g., after chain change)
        // But actual wallet signing only happens when user clicks "Get Keys"
    useEffect(() => {
        // Only auto-show modal if user hasn't explicitly cancelled AND account creation hasn't failed
        // But allow explicit triggers via triggerKeyDerivation() to always show
        // Also require that user has explicitly selected both chain and token
        if (
            needsConsent &&
            accountCoreSynced &&
            !keyDerivationConsent &&
            !showConsentModal &&
            !userCancelledKeyDerivation &&
            !accountCreationFailed &&
            selectedChainId &&
            selectedTokenId
        ) {
            console.log('Key derivation consent needed, showing modal');
            // Explicitly ensure consent is false before showing modal
            setKeyDerivationConsent(false);
            setShowConsentModal(true);
        } else if (keysDerived && showConsentModal) {
            // Close modal when keys are successfully derived
            console.log('Keys derived, hiding modal');
            setShowConsentModal(false);
        }
    }, [needsConsent, accountCoreSynced, keyDerivationConsent, keysDerived, showConsentModal, userCancelledKeyDerivation, accountCreationFailed, selectedChainId, selectedTokenId]);

    // Reset consent when account or chain changes
    // This MUST run before key derivation attempts
    useEffect(() => {
        console.log('Account or chain changed, resetting consent and hiding modal');
        setKeyDerivationConsent(false);
        setKeysDerived(false);
        setShowConsentModal(false); // Ensure modal is hidden when switching chains
        setUserCancelledKeyDerivation(false); // Reset cancellation flag when account/chain changes
        setAccountCreationFailed(false); // Reset account creation failed flag when account/chain changes
        
        // Check if this wallet+account combination has been connected before
        // Use synchronous checks to ensure this completes before modal is shown
        if (userAccount && selectedChainIdForKeys && verifyingContract) {
            // Synchronous check - no async needed since we're just reading localStorage
            const hasBeenConnected = secureKeyCache.hasBeenConnectedBefore(userAccount, selectedChainIdForKeys, verifyingContract);
            // Also check if keys are cached (which means they've connected before)
            const hasCachedKeys = secureKeyCache.hasCachedKeysSync(userAccount, selectedChainIdForKeys, verifyingContract);
            const isFirstTime = !hasBeenConnected && !hasCachedKeys;
            setIsFirstTimeConnection(isFirstTime);
            
            // Debug: Check what's actually in localStorage
            const connectionKey = `ConfidentialBalances_connected_${userAccount.toLowerCase()}-${selectedChainIdForKeys}-${verifyingContract.toLowerCase()}`;
            const cacheKey = `ConfidentialBalances_${userAccount.toLowerCase()}-${selectedChainIdForKeys}-${verifyingContract.toLowerCase()}`;
            const connectionMarkerExists = !!localStorage.getItem(connectionKey);
            const cacheKeyExists = !!localStorage.getItem(cacheKey);
            
            console.log('First time connection check (sync):', {
                isFirstTime,
                userAccount: userAccount ? `${userAccount.slice(0, 6)}...${userAccount.slice(-4)}` : null,
                selectedChainIdForKeys,
                verifyingContract: verifyingContract ? `${verifyingContract.slice(0, 6)}...${verifyingContract.slice(-4)}` : null,
                hasBeenConnected,
                hasCachedKeys,
                connectionKey,
                connectionMarkerExists,
                cacheKey,
                cacheKeyExists
            });
        } else {
            setIsFirstTimeConnection(true); // Default to first time if we don't have all required info
            console.log('First time connection check: Missing required info', {
                hasUserAccount: !!userAccount,
                hasSelectedChainIdForKeys: !!selectedChainIdForKeys,
                hasVerifyingContract: !!verifyingContract
            });
        }
    }, [userAccount, selectedChainId, selectedChainIdForKeys, verifyingContract]);

    // Handlers for consent modal
    const handleKeyDerivationConsent = () => {
            console.log('User clicked "Get Keys" button - giving consent for key derivation');
        console.log('This will trigger wallet signature popup...');
        setKeyDerivationConsent(true);
        // Don't close modal yet - let it close automatically when keys are derived
    };

    const handleKeyDerivationCancel = () => {
        console.log('User cancelled key derivation');
        setKeyDerivationConsent(false);
        setShowConsentModal(false);
        setUserCancelledKeyDerivation(true); // Track that user explicitly cancelled
        // Don't show notification - user can still click "Get Keys" buttons to retry
        // showNotification('Key derivation cancelled. You will need to approve to use confidential features.', 'info');
    };

    // Function to trigger key derivation
    const triggerKeyDerivation = () => {
        if (!keysDerived || accountCreationFailed) {
            console.log('Triggering key derivation - user explicitly clicked "Get Keys"');
            // Reset cancellation flag, consent state, and account creation failed flag when user explicitly triggers
            setUserCancelledKeyDerivation(false);
            setAccountCreationFailed(false); // Clear failed flag so buttons work correctly
            setKeyDerivationConsent(false); // Reset consent to ensure fresh flow
            setShowConsentModal(true); // Force show modal when user explicitly requests it
        }
    };

    // Get token data from chain configuration
    // Helper function to check if a token address is valid
    const isValidTokenAddress = (address) => {
        if (!address) return false;
        const raw = address.trim();
        const addr = raw.toLowerCase();
        if (addr === '' || addr === '0x0000000000000000000000000000000000000000') return false;
        if (/^0x[a-fA-F0-9]{40}$/.test(addr)) return true;
        const upper = raw.toUpperCase();
        return /^C[ABCDEFGHIJKLMNOPQRSTUVWXYZ234567]{55}$/.test(upper);
    };

    const getTokenData = () => {
        if (selectedTokenData) {
            return selectedTokenData;
        }
        
        // Only return token data if both chain and token are selected
        if (!selectedChainId || !selectedTokenId) {
            return null;
        }
        
        const chain = CHAINS[selectedChainId];
        if (chain && chain.tokens[selectedTokenId]) {
            return {
                ...chain.tokens[selectedTokenId],
                chainId: selectedChainId,
                chainName: chain.name,
            };
        }
        
        // Return null if chain or token is not found
        return null;
    };

    const tokenData = getTokenData();
    publicBalanceTokenRef.current = {
        key: `${selectedChainId}_${selectedTokenId}`,
        data: tokenData,
    };
    selectedChainIdForBalanceRef.current = selectedChainId;
    const parseAmountForContract = (amountStr) => {
        const t = getTokenData();
        const d = normalizeTokenDecimals(t?.decimals);
        return parseUserAmountToPlainInt(amountStr, d);
    };

    useEffect(() => {
        transferProofPreviewCacheRef.current.clear();
        withdrawProofPreviewCacheRef.current.clear();
    }, [selectedChainId, selectedTokenId, userAccount, confidentialBalance, keypair?.private_key]);

    // Real-time gas breakdown: refetch when panel opens and every 10s (refs keep amount/token current)
    useEffect(() => {
        if (!ENABLE_FEE_BREAKDOWN_UI || !showGasBreakdown || !gasBreakdownOperation) return;
        const getBreakdownOpts = () => {
            if (!tokenData?.address) return {};
            if (gasBreakdownOperation === 'transfer') {
                const trimmedRecipient = (recipient || '').trim();
                const amount = parseAmountForContract(transferAmountRef.current);
                return { tokenAddress: tokenData.address, recipient: trimmedRecipient, amountForContract: amount };
            }
            if (gasBreakdownOperation === 'deposit') {
                const amount = parseAmountForContract(depositAmountRef.current);
                return { tokenAddress: tokenData.address, amountForContract: amount };
            }
            if (gasBreakdownOperation === 'withdraw') {
                const amount = parseAmountForContract(withdrawAmountRef.current);
                return { tokenAddress: tokenData.address, amountForContract: amount };
            }
            return {};
        };
        fetchGasBreakdown(gasBreakdownOperation, getBreakdownOpts());
        const intervalId = setInterval(() => {
            fetchGasBreakdown(gasBreakdownOperation, getBreakdownOpts());
        }, 10000);
        return () => clearInterval(intervalId);
    }, [showGasBreakdown, gasBreakdownOperation, tokenData?.address, tokenData?.decimals, recipient]);

    // When deposit amount changes and breakdown is open, debounce refetch
    useEffect(() => {
        if (!ENABLE_FEE_BREAKDOWN_UI || !showGasBreakdown || gasBreakdownOperation !== 'transfer' || !tokenData?.address) return;
        const t = setTimeout(() => {
            const trimmedRecipient = (recipient || '').trim();
            const amount = parseAmountForContract(transferAmountRef.current);
            fetchGasBreakdown('transfer', { tokenAddress: tokenData.address, recipient: trimmedRecipient, amountForContract: amount });
        }, 500);
        return () => clearTimeout(t);
    }, [transferAmount, recipient, showGasBreakdown, gasBreakdownOperation, tokenData?.address, tokenData?.decimals]);

    // When deposit amount changes and breakdown is open, debounce refetch
    useEffect(() => {
        if (!ENABLE_FEE_BREAKDOWN_UI || !showGasBreakdown || gasBreakdownOperation !== 'deposit' || !tokenData?.address) return;
        const t = setTimeout(() => {
            const amount = parseAmountForContract(depositAmountRef.current);
            fetchGasBreakdown('deposit', { tokenAddress: tokenData.address, amountForContract: amount });
        }, 500);
        return () => clearTimeout(t);
    }, [depositAmount, showGasBreakdown, gasBreakdownOperation, tokenData?.address, tokenData?.decimals]);

    // When withdraw amount changes and breakdown is open, debounce refetch
    useEffect(() => {
        if (!ENABLE_FEE_BREAKDOWN_UI || !showGasBreakdown || gasBreakdownOperation !== 'withdraw' || !tokenData?.address) return;
        const t = setTimeout(() => {
            const amount = parseAmountForContract(withdrawAmountRef.current);
            fetchGasBreakdown('withdraw', { tokenAddress: tokenData.address, amountForContract: amount });
        }, 500);
        return () => clearTimeout(t);
    }, [withdrawAmount, showGasBreakdown, gasBreakdownOperation, tokenData?.address, tokenData?.decimals]);
    
    const getSecureClaimKey = () => {
        const t = getTokenData();
        if (!userAccount || !t?.chainId || !t?.address) return null;
        const address = typeof t.address === 'string' ? t.address : String(t.address || '');
        const account = typeof userAccount === 'string' ? userAccount : String(userAccount || '');
        return `${account.toLowerCase()}::${t.chainId}::${address.toLowerCase()}`;
    };

    const formatTokenAmount = (value, decimals = 6) => {
        if (value === null || value === undefined) return '';
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) return '';
        if (numericValue === 0) return '0';
        
        // If value is very small and rounds to zero with the given decimals,
        // find the first significant digit and show at least one digit
        const fixed = numericValue.toFixed(decimals);
        const trimmed = fixed.replace(/\.?0+$/, '');
        
        // If trimmed is empty or '0', but original value is > 0, find first significant digit
        if ((trimmed === '' || trimmed === '0') && numericValue > 0) {
            // Find the position of the first non-zero digit after the decimal point
            const valueStr = numericValue.toString();
            if (valueStr.includes('e')) {
                // Handle scientific notation (e.g., 1e-7)
                const [base, exp] = valueStr.split('e');
                const exponent = parseInt(exp, 10);
                if (exponent < 0) {
                    // For very small numbers, show at least one significant digit
                    const baseNum = parseFloat(base);
                    const firstDigit = Math.floor(Math.abs(baseNum) * 10) % 10;
                    const decimalPlaces = Math.abs(exponent) + (baseNum < 1 ? 1 : 0);
                    return numericValue.toFixed(Math.min(decimalPlaces, 20));
                }
            } else {
                // Find first non-zero digit position
                const absValue = Math.abs(numericValue);
                if (absValue < 1) {
                    // Count leading zeros after decimal point
                    const str = absValue.toString();
                    if (str.includes('.')) {
                        const decimalPart = str.split('.')[1];
                        if (decimalPart) {
                            // Find first non-zero digit
                            let firstNonZero = -1;
                            for (let i = 0; i < decimalPart.length; i++) {
                                if (decimalPart[i] !== '0') {
                                    firstNonZero = i;
                                    break;
                                }
                            }
                            if (firstNonZero >= 0) {
                                // Show at least one digit after the first non-zero
                                const totalDecimals = firstNonZero + 1;
                                return numericValue.toFixed(Math.min(totalDecimals, 20));
                            }
                        }
                    }
                }
            }
            // Fallback: use more decimal places to show at least one digit
            return numericValue.toFixed(Math.min(decimals + 10, 20));
        }
        
        return trimmed === '' ? '0' : trimmed;
    };

    const isValidRecipientAddress = (value) => {
        if (!value || typeof value !== 'string') return false;
        const v = value.trim();
        if (/^0x[a-fA-F0-9]{40}$/i.test(v)) return true;
        if (selectedChainId === 'stellar') {
            const u = v.toUpperCase();
            return (
                /^G[ABCDEFGHIJKLMNOPQRSTUVWXYZ234567]{55}$/.test(u) ||
                /^C[ABCDEFGHIJKLMNOPQRSTUVWXYZ234567]{55}$/.test(u)
            );
        }
        return false;
    };

    // Check if recipient has a Stabletrust account
    const checkRecipientAccount = async (address) => {
        if (!contract || !address || !isValidRecipientAddress(address)) {
            setRecipientAccountStatus(null);
            return;
        }
        
        setRecipientAccountStatus('checking');
        
        try {
            const recipientAccountTuple = await contract.getAccountCore(address);
            const hasActiveAccount = recipientAccountTuple[0] && recipientAccountTuple[1]; // exists && finalized
            
            setRecipientAccountStatus(hasActiveAccount ? 'has_account' : 'no_account');
        } catch (error) {
            // If contract call fails (reverts), it likely means the account doesn't exist
            console.log('Recipient account check error:', error.message);
            setRecipientAccountStatus('no_account');
        }
    };

    // Generate and copy an invite link with a newly created unique code
    const copyInviteLink = async () => {
        if (isGeneratingInvite) return;
        
        setIsGeneratingInvite(true);
        
        try {
            // Create a new unique invite code in the database
            const result = await createSingleInviteCode(userAccount);
            
            if (!result.success) {
                showNotification(result.error || 'Failed to generate invite code', 'error');
                return;
            }
            
            const baseUrl = window.location.origin;
            const link = `${baseUrl}?invite=${encodeURIComponent(result.code)}`;
            
            // Copy to clipboard
            try {
                await navigator.clipboard.writeText(link);
                showNotification('Invite link copied to clipboard!', 'success');
            } catch (clipboardErr) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = link;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                showNotification('Invite link copied to clipboard!', 'success');
            }
        } catch (err) {
            console.error('Error generating invite link:', err);
            showNotification('Failed to generate invite link. Please try again.', 'error');
        } finally {
            setIsGeneratingInvite(false);
        }
    };

    const handleRecipientChange = (value) => {
        setRecipient(value);
        const trimmed = value.trim();
        
        // Clear previous timeout
        if (recipientCheckTimeoutRef.current) {
            clearTimeout(recipientCheckTimeoutRef.current);
            recipientCheckTimeoutRef.current = null;
        }
        
        if (!trimmed) {
            setRecipientError(null);
            setRecipientAccountStatus(null);
            return;
        }
        
        if (!isValidRecipientAddress(trimmed)) {
            setRecipientError('Invalid address');
            setRecipientAccountStatus(null);
            return;
        }
        
        setRecipientError(null);
        
        // Debounce the account check to avoid too many contract calls
        recipientCheckTimeoutRef.current = setTimeout(() => {
            checkRecipientAccount(trimmed);
        }, 500);
    };
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (recipientCheckTimeoutRef.current) {
                clearTimeout(recipientCheckTimeoutRef.current);
            }
        };
    }, []);

    // Faucet helpers (env-driven)
    const getFaucetEnvVarName = () => {
        const chainKey = (CHAINS[selectedChainId]?.name || selectedChainId || '').toUpperCase().replace(/\s+/g, '_');
        const symbolKey = (getTokenData().symbol || '').toUpperCase();
        return `REACT_APP_${chainKey}_${symbolKey}_FAUCET_ADDRESS`;
    };

    const getFaucetAddressForSelection = () => {
        const envKey = getFaucetEnvVarName();
        // Access environment variable in CRA:
        // eslint-disable-next-line no-undef
        const fromEnv = process.env[envKey];
        return fromEnv || getTokenData().faucetAddress || null;
    };

    const handleGetTestTokens = async () => {
        try {
            if (!userAccount) {
                showNotification('Wallet not connected. Please connect your wallet', 'error');
                return;
            }
            if (!contract?.signer) {
                showNotification('Signer not ready. Please try again.', 'error');
                return;
            }
            const faucetAddr = getFaucetAddressForSelection();
            if (!faucetAddr) {
                showNotification(`Test token faucet is not available for ${getTokenData().symbol} on this network.`, 'error');
                return;
            }

            setIsFaucetProcessing(true);
            // Removed "Requesting test tokens..." notification - faucet action is already clear

            // Check if faucet is a web URL (like Circle's faucet) or a contract address
            if (faucetAddr.startsWith('http')) {
                // Web-based faucet (like Circle's)
                showNotification(
                    `Please visit ${faucetAddr} to claim test ${getTokenData().symbol}. Select Ethereum Sepolia and enter your address: ${userAccount}`,
                    'info'
                );
                
                // Open the faucet in a new tab
                window.open(faucetAddr, '_blank');
                
                // Show instructions
                setTimeout(() => {
                    showNotification(
                        'After claiming tokens from the web faucet, refresh this page to see your balance.',
                        'info'
                    );
                }, 2000);
                
                setIsFaucetProcessing(false);
                return;
            }

            // Contract-based faucet
            // Try different faucet patterns
            // Pattern 1: claim() - simple faucet
            // Pattern 2: mint(address) - mint to address
            // Pattern 3: drip() - drip faucet
            const faucetABI = [
                'function claim() external',
                'function mint(address to, uint256 amount) external',
                'function drip() external',
                'function requestTokens() external',
                'function getTokens() external'
            ];

            const faucet = new ethers.Contract(faucetAddr, faucetABI, contract.signer);

            let tx;
            let method = 'claim';
            
            try {
                // Try claim() first (most common)
                tx = await faucet.claim();
            } catch (claimErr) {
                console.log('claim() failed, trying alternative methods...', claimErr);
                
                try {
                    // Try drip()
                    method = 'drip';
                    tx = await faucet.drip();
                } catch (dripErr) {
                    try {
                        // Try requestTokens()
                        method = 'requestTokens';
                        tx = await faucet.requestTokens();
                    } catch (requestErr) {
                        try {
                            // Try getTokens()
                            method = 'getTokens';
                            tx = await faucet.getTokens();
                        } catch (getErr) {
                            // All methods failed, throw the original error
                            throw claimErr;
                        }
                    }
                }
            }

            console.log(`Faucet method ${method}() called, waiting for confirmation...`);
            await tx.wait();
            showNotification('Faucet claim successful! Tokens sent to your wallet.', 'success');
            
            // Refresh displayed ERC20 wallet balance
            await updateTokenBalance();
            
            // Refresh account data to clear any pending action state
            await onOperationComplete();
        } catch (err) {
            console.error('Faucet claim failed:', err);
            
            // Parse error message
            let errorMsg = 'Faucet claim failed';
            
            // Check for common error codes
            if (err?.code === 'UNPREDICTABLE_GAS_LIMIT') {
                const errorData = err?.error?.data || err?.data;
                
                // Try to decode common error selectors
                if (errorData === '0x3db2a12a') {
                    errorMsg = 'Already claimed recently. Please wait before claiming again (cooldown period).';
                } else if (errorData === '0x' || !errorData) {
                    errorMsg = 'Faucet transaction would fail. The faucet may be empty or you may have already claimed.';
                } else {
                    errorMsg = 'Faucet claim failed. You may need to wait before claiming again, or the faucet may be empty.';
                }
            } else if (err?.code === 4001) {
                errorMsg = 'Transaction cancelled by user';
            } else if (err?.message?.includes('insufficient funds')) {
                errorMsg = (
                    <>
                        Insufficient testnet funds for gas. You need some testnet ETH for gas fees. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet ETH</a>.
                    </>
                );
            } else {
                // Use error parser for clean error messages
                errorMsg = getCleanErrorMessage(err);
            }
            
            // Persistent notification for insufficient funds, regular for other errors
            showNotification(errorMsg, 'error', err?.message?.includes('insufficient funds'));
        } finally {
            setIsFaucetProcessing(false);
        }
    };

    const handleChainSelect = async (chainId) => {
        console.log('Chain selected:', {
            from: selectedChainId,
            to: chainId
        });
        
        // Clear all input fields when switching chains
        setRecipient('');
        setRecipientError(null);
        setRecipientAccountStatus(null);
        setTransferAmount('');
        setTransferAmountError('');
        setDepositAmount('');
        setDepositAmountError('');
        setWithdrawAmount('');
        setWithdrawAmountError('');
        
        // IMMEDIATELY reset consent to prevent race conditions
        // This must happen synchronously BEFORE any async operations
        setKeyDerivationConsent(false);
        setKeysDerived(false);
        setShowConsentModal(false);
        
        // Update loading refs immediately to prevent race conditions
        loadingChainRef.current = chainId;
        // Note: loadingTokenRef will be updated when we determine the new token below
        
        // Reset loading flag to allow new load after chain change
        loadingPendingBalanceRef.current = false;
        
        // Save current balances to cache before switching (if we have balances for current chain+token)
        if (confidentialBalance !== null || pendingBalance !== null) {
            const cacheKey = `${selectedChainId}_${selectedTokenId}`;
            balanceCacheRef.current.set(cacheKey, {
                available: confidentialBalance,
                pending: pendingBalance
            });
            console.log('Cached balances for', cacheKey, ':', { available: confidentialBalance, pending: pendingBalance });
        }
        
        // Get the token that will be selected on the new chain (we'll determine this below)
        // For now, try to restore cached balance if available
        const newChainConfig = CHAINS[chainId];
        let newTokenId = null;
        if (newChainConfig && newChainConfig.tokens) {
            const chainTokens = Object.keys(newChainConfig.tokens);
            const activeTokens = chainTokens.filter(tokenId => {
                const token = newChainConfig.tokens[tokenId];
                return isValidTokenAddress(token.address);
            });
            // Only select from active tokens - never fallback to inactive tokens
            if (activeTokens.length > 0) {
                newTokenId = activeTokens.includes(selectedTokenId) ? selectedTokenId : activeTokens[0];
            }
            // If no active tokens, newTokenId remains null and we won't select any token
        }
        
        // Try to restore cached balances for the new chain+token combination (only if we have an active token)
        const newCacheKey = newTokenId ? `${chainId}_${newTokenId}` : null;
        const cachedBalances = newCacheKey ? balanceCacheRef.current.get(newCacheKey) : null;
        if (cachedBalances) {
            console.log('Restoring cached balances for', newCacheKey, ':', cachedBalances);
            // Mark that we just restored from cache and track the key to prevent immediate reload
            // IMPORTANT: Set flag BEFORE state updates to ensure useEffect sees it
            justRestoredFromCacheRef.current = true;
            lastLoadedKeyRef.current = newCacheKey; // Track that we're restoring this key
            
            // Restore cached balances immediately - use flushSync or ensure these happen synchronously
            // Actually, we need to do this BEFORE setSelectedChainId to avoid race condition
            if (cachedBalances.available !== null && cachedBalances.available !== undefined) {
                setConfidentialBalance(cachedBalances.available);
            } else {
                setConfidentialBalance(null);
            }
            if (cachedBalances.pending !== null && cachedBalances.pending !== undefined) {
                setPendingBalance(cachedBalances.pending);
            } else {
                setPendingBalance(null);
            }
        } else {
            // No cached balance, clear them
            console.log('No cached balance for', newCacheKey, '- clearing balances');
            justRestoredFromCacheRef.current = false;
            lastLoadedKeyRef.current = null; // Clear last loaded key
            setPendingBalance(null);
            setConfidentialBalance(null);
        }
        
        const isMobile = isMobileDevice();
        
        // On mobile, skip wallet network switching since wallets are not active
        // Only switch wallet network on desktop
        if (chainId !== selectedChainId && switchToChain && !isMobile) {
            console.log('Switching wallet network to:', chainId);
            const chainConfig = CHAINS[chainId];
            const switched = await switchToChain(chainId);
            
            if (!switched) {
                // Network switch failed or was cancelled
                console.log('Network switch failed or cancelled, keeping current chain');
               // showNotification(`Failed to switch to ${chainConfig?.name || chainId}. Please switch manually in your wallet.`, 'warning');
                return;
            }
            
            // Removed duplicate "Switched to..." notification - chain change is already handled by App.js
        }
        
        // Update selected chain ID
        setSelectedChainId(chainId);
        
        // Update selected token to a default token from the new chain
        // Use newChainConfig that was already determined above
        // Only select active tokens - never select inactive tokens
        if (newChainConfig && newChainConfig.tokens && newTokenId) {
            const chainTokens = Object.keys(newChainConfig.tokens);
            if (chainTokens.length > 0) {
                // Find active tokens (tokens with valid addresses)
                const activeTokens = chainTokens.filter(tokenId => {
                    const token = newChainConfig.tokens[tokenId];
                    return isValidTokenAddress(token.address);
                });
                
                let finalTokenId = newTokenId; // Use the tokenId we determined earlier for cache lookup
                // If there's exactly one active token, use it
                if (activeTokens.length === 1) {
                    finalTokenId = activeTokens[0];
                } else if (activeTokens.length > 0) {
                    // If multiple active tokens, use the first active one or keep current if it's active
                    finalTokenId = activeTokens.includes(selectedTokenId) ? selectedTokenId : activeTokens[0];
                } else {
                    // No active tokens - don't select any token
                    console.log('No active tokens available for chain:', chainId);
                    setSelectedTokenData(null);
                    setSelectedTokenId(null);
                    setPendingBalance(null);
                    setConfidentialBalance(null);
                    return;
                }
                
                // If the final token differs from what we used for cache lookup, we need to check cache again
                if (finalTokenId !== newTokenId) {
                    const finalCacheKey = `${chainId}_${finalTokenId}`;
                    const finalCachedBalances = balanceCacheRef.current.get(finalCacheKey);
                    if (finalCachedBalances) {
                        console.log('Restoring cached balances for final token', finalCacheKey, ':', finalCachedBalances);
                        if (finalCachedBalances.available !== null && finalCachedBalances.available !== undefined) {
                            setConfidentialBalance(finalCachedBalances.available);
                        } else {
                            setConfidentialBalance(null);
                        }
                        if (finalCachedBalances.pending !== null && finalCachedBalances.pending !== undefined) {
                            setPendingBalance(finalCachedBalances.pending);
                        } else {
                            setPendingBalance(null);
                        }
                    } else {
                        setPendingBalance(null);
                        setConfidentialBalance(null);
                    }
                }
                
                // Update loading token ref to prevent race conditions
                loadingTokenRef.current = finalTokenId;
                
                const tokenData = newChainConfig.tokens[finalTokenId];
                const fullTokenData = {
                    ...tokenData,
                    chainId: chainId,
                    chainName: newChainConfig.name,
                    id: `${chainId}_${finalTokenId}`,
                };
                setSelectedTokenData(fullTokenData);
                setSelectedTokenId(finalTokenId);
                console.log('Updated token to:', finalTokenId, 'for chain:', chainId);
            }
        } else if (newChainConfig && newChainConfig.tokens) {
            // No active tokens available for this chain
            console.log('No active tokens available for chain:', chainId);
            setSelectedTokenData(null);
            setSelectedTokenId(null);
            setPendingBalance(null);
            setConfidentialBalance(null);
        }
    };

    const handleTokenSelect = async (tokenData) => {
        console.log('Token selected:', {
            from: selectedTokenId,
            to: tokenData.id.split('_')[1],
            chainId: tokenData.chainId,
            tokenSymbol: tokenData.symbol
        });
        
        // Clear all input fields when switching tokens
        setRecipient('');
        setRecipientError(null);
        setRecipientAccountStatus(null);
        setTransferAmount('');
        setTransferAmountError('');
        setDepositAmount('');
        setDepositAmountError('');
        setWithdrawAmount('');
        setWithdrawAmountError('');
        
        const newTokenId = tokenData.id.split('_')[1];
        const newCacheKey = `${tokenData.chainId}_${newTokenId}`;
        
        // IMMEDIATELY update loading refs to prevent race conditions
        // This must happen before any async operations or state updates
        loadingTokenRef.current = newTokenId;
        loadingChainRef.current = tokenData.chainId;
        
        // Save current balances to cache before switching tokens
        if (confidentialBalance !== null || pendingBalance !== null) {
            const cacheKey = `${selectedChainId}_${selectedTokenId}`;
            balanceCacheRef.current.set(cacheKey, {
                available: confidentialBalance,
                pending: pendingBalance
            });
            console.log('Cached balances for', cacheKey, ':', { available: confidentialBalance, pending: pendingBalance });
        }
        
        // Try to restore cached balances for the new chain+token combination
        const cachedBalances = balanceCacheRef.current.get(newCacheKey);
        if (cachedBalances) {
            console.log('Restoring cached balances for', newCacheKey, ':', cachedBalances);
            // Mark that we just restored from cache and track the key
            justRestoredFromCacheRef.current = true;
            lastLoadedKeyRef.current = newCacheKey;
            // Restore cached balances immediately
            if (cachedBalances.available !== null && cachedBalances.available !== undefined) {
                setConfidentialBalance(cachedBalances.available);
            } else {
                setConfidentialBalance(null);
            }
            if (cachedBalances.pending !== null && cachedBalances.pending !== undefined) {
                setPendingBalance(cachedBalances.pending);
            } else {
                setPendingBalance(null);
            }
        } else {
            // No cached balance, clear them IMMEDIATELY to prevent showing old balance
            console.log('No cached balance for', newCacheKey, '- clearing balances immediately');
            justRestoredFromCacheRef.current = false;
            lastLoadedKeyRef.current = null;
            // Clear balances synchronously before updating token selection
            setPendingBalance(null);
            setConfidentialBalance(null);
        }
        
        // Chain switching is now handled by handleChainSelect
        // Just update the token data and IDs
        setSelectedTokenData(tokenData);
        setSelectedChainId(tokenData.chainId);
        setSelectedTokenId(newTokenId);
    };

    // Save selected chain and token to sessionStorage for persistence across refreshes
    // Using sessionStorage so it clears when tab is closed, but persists on refresh
    useEffect(() => {
        try {
            if (selectedChainId) {
                sessionStorage.setItem('selectedChainId', selectedChainId);
                console.log('Saved chain to sessionStorage:', selectedChainId);
            } else {
                sessionStorage.removeItem('selectedChainId');
            }
        } catch (error) {
            console.error('Error saving/removing chain from sessionStorage:', error);
        }
    }, [selectedChainId]);
    
    // Save selected token to sessionStorage for persistence across refreshes
    useEffect(() => {
        try {
            if (selectedTokenId) {
                sessionStorage.setItem('selectedTokenId', selectedTokenId);
                console.log('Saved token to sessionStorage:', selectedTokenId);
            } else {
                sessionStorage.removeItem('selectedTokenId');
            }
        } catch (error) {
            console.error('Error saving/removing token from sessionStorage:', error);
        }
    }, [selectedTokenId]);
    
    // Track previous userAccount to detect disconnections
    const prevUserAccountRef = useRef(userAccount);
    
    // Clear chain/token selection when wallet disconnects
    useEffect(() => {
        // Detect transition from connected to disconnected
        if (prevUserAccountRef.current && !userAccount) {
            console.log('Wallet disconnected - clearing chain/token selection');
            setSelectedChainId(null);
            setSelectedTokenId(null);
            setSelectedTokenData(null);
            // Also clear sessionStorage (this is also done in App.js but good to be safe)
            try {
                sessionStorage.removeItem('selectedChainId');
                sessionStorage.removeItem('selectedTokenId');
            } catch (e) {
                // ignore
            }
        }
        prevUserAccountRef.current = userAccount;
    }, [userAccount]);

    // Detect wallet's current network and handle mismatches
    useEffect(() => {
        const detectCurrentChain = async () => {
            // On mobile, skip wallet network detection since wallets are not active
            const isMobile = isMobileDevice();
            if (isMobile || !provider || !userAccount) return;
            
            try {
                const network = await provider.getNetwork();
                const currentChainId = network.chainId;
                
                // Find matching chain in CHAINS config
                const matchingChain = Object.entries(CHAINS).find(([_, chainConfig]) => 
                    chainConfig.chainIdDecimal === currentChainId
                );
                
                if (matchingChain) {
                    const [chainId, chainConfig] = matchingChain;
                    console.log('Detected wallet is on chain:', chainId, chainConfig.name, '(UI selection:', selectedChainId + ')');
                    
                    // Check if wallet chain matches UI selection
                    if (chainId !== selectedChainId) {
                        console.log('⚠️ Mismatch detected: Wallet on', chainId, 'but UI shows', selectedChainId);
                        
                        // Check if we've already attempted auto-switch in this session (prevent infinite loops)
                        const autoSwitchAttempted = sessionStorage.getItem('autoSwitchAttempted');
                        const lastAttemptTime = sessionStorage.getItem('lastAutoSwitchTime');
                        const now = Date.now();
                        
                        // Reset flag after 30 seconds to allow retry if needed
                        if (lastAttemptTime && (now - parseInt(lastAttemptTime)) > 30000) {
                            sessionStorage.removeItem('autoSwitchAttempted');
                            sessionStorage.removeItem('lastAutoSwitchTime');
                        }
                        
                        // Only auto-switch if user has explicitly selected a chain
                        // Never auto-select chain based on wallet - user must choose explicitly
                        if (selectedChainId && switchToChain && !autoSwitchAttempted) {
                            console.log('User selected', selectedChainId, '- attempting to switch wallet to match...');
                            
                            // Mark that we've attempted auto-switch to prevent infinite loops
                            sessionStorage.setItem('autoSwitchAttempted', 'true');
                            sessionStorage.setItem('lastAutoSwitchTime', now.toString());
                            
                            // Try to switch wallet to match user's selection
                            const switched = await switchToChain(selectedChainId);
                            if (!switched) {
                                // If switch failed, keep user's selection - don't override with wallet chain
                                console.log('Could not switch wallet, keeping user selection:', selectedChainId);
                                // Do NOT sync UI to wallet - respect user's explicit choice
                            }
                        } else {
                            // No chain selected by user - don't auto-select based on wallet
                            console.log('No chain selected by user. Wallet is on', chainId, 'but UI stays unselected until user chooses.');
                            // Do NOT sync UI to wallet - keep it null until user chooses
                        }
                    } else {
                        console.log('✓ Wallet and UI are in sync on', chainId);
                        // Clear auto-switch flag when chains are in sync
                        sessionStorage.removeItem('autoSwitchAttempted');
                        sessionStorage.removeItem('lastAutoSwitchTime');
                    }
                } else {
                    console.log('Wallet is on unsupported chain:', currentChainId);
                  //  showNotification('Please switch your wallet to a supported network', 'warning');
                }
            } catch (error) {
                console.error('Error detecting current chain:', error);
            }
        };
        
        detectCurrentChain();
    }, [provider, userAccount]); // Only run when provider or account changes

    // Validate that the selected token is active for the selected chain
    // This ensures inactive tokens are never selected by default
    useEffect(() => {
        const chain = CHAINS[selectedChainId];
        if (!chain || !chain.tokens || !selectedTokenId) return;
        
        const token = chain.tokens[selectedTokenId];
        if (!token || !isValidTokenAddress(token.address)) {
            // Current token is inactive - find an active token or clear selection
            const chainTokens = Object.keys(chain.tokens);
            const activeTokens = chainTokens.filter(tokenId => {
                const t = chain.tokens[tokenId];
                return isValidTokenAddress(t.address);
            });
            
            if (activeTokens.length > 0) {
                // Select the first active token
                const newTokenId = activeTokens[0];
                // Clear all input fields when token is automatically switched
                setRecipient('');
                setRecipientError(null);
                setRecipientAccountStatus(null);
                setTransferAmount('');
                setTransferAmountError('');
                setDepositAmount('');
                setDepositAmountError('');
                setWithdrawAmount('');
                setWithdrawAmountError('');
                
                const tokenData = chain.tokens[newTokenId];
                const fullTokenData = {
                    ...tokenData,
                    chainId: selectedChainId,
                    chainName: chain.name,
                    id: `${selectedChainId}_${newTokenId}`,
                };
                setSelectedTokenData(fullTokenData);
                setSelectedTokenId(newTokenId);
                console.log('Validated token selection - switched to active token:', newTokenId);
            } else {
                // No active tokens available - clear selection
                console.log('No active tokens available for chain:', selectedChainId);
                // Clear all input fields when token selection is cleared
                setRecipient('');
                setRecipientError(null);
                setRecipientAccountStatus(null);
                setTransferAmount('');
                setTransferAmountError('');
                setDepositAmount('');
                setDepositAmountError('');
                setWithdrawAmount('');
                setWithdrawAmountError('');
                
                setSelectedTokenData(null);
                setSelectedTokenId(null);
            }
        }
    }, [selectedChainId]); // Only validate when chain changes, not on every token change

    // Update balance when token selection changes
    useEffect(() => {
        if (activeTab === 'deposit' || activeTab === 'withdraw' || activeTab === 'transfer' || activeTab === 'apply') {
            updateTokenBalance();
        }
    }, [selectedTokenId, selectedChainId, activeTab, userAccount, contract]);

    // Load confidential balance from contract when wallet + contract are ready.
    // `getAccountCore` is logged even before WASM / keypair (those are only needed to decrypt ciphertext).
    useEffect(() => {
        // Update the refs to track current chain and token (in case they changed outside handlers)
        loadingChainRef.current = selectedChainId;
        loadingTokenRef.current = selectedTokenId;

        if (!contract || !userAccount) {
            console.log('[Confidential balance] effect: waiting for wallet or contract', {
                hasContract: !!contract,
                hasGetAccountCore: !!(contract && typeof contract.getAccountCore === 'function'),
                contractAddress: contract?.address,
                hasUser: !!userAccount,
            });
            return;
        }

        // Only load if we're still on the same chain and token (prevents race conditions during switches)
        if (loadingChainRef.current !== selectedChainId || loadingTokenRef.current !== selectedTokenId) {
            return;
        }

        const cacheKey = `${selectedChainId}_${selectedTokenId}`;

        // CRITICAL CHECK: If we just restored this exact key from cache, skip loading entirely
        if (justRestoredFromCacheRef.current && lastLoadedKeyRef.current === cacheKey) {
            console.log('Just restored from cache for', cacheKey, '- skipping load to preserve cached balance');
            return;
        }

        console.log('[Confidential balance] effect: triggering contract reads', {
            user: userAccount,
            contract: contract?.address,
            isStellarFacade: !!contract?.isStellarSoroban,
            hasWasm: !!wasmModule,
            hasKeypair: !!keypair,
            selectedChainId,
            selectedTokenId,
        });

        // Normal loading path - not from cache restore
        if (lastLoadedKeyRef.current !== cacheKey) {
            justRestoredFromCacheRef.current = false;
        }
        lastLoadedKeyRef.current = cacheKey;
        loadConfidentialBalance();
        loadPendingBalance();
        // Don't clear pending balance when keypair is null - it might just be temporarily unavailable
        // during chain switches. Only clear it when explicitly changing chains (in handleChainSelect)
    }, [contract, userAccount, wasmModule, keypair, selectedTokenId, selectedChainId]);

    // Check for pending action when accountData changes or periodically
    useEffect(() => {
        if (!contract || !userAccount) {
            setPendingActionKind(null);
            return;
        }

        // Check immediately
        checkPendingAction();

        // Set up periodic check every 5 seconds to catch updates
        const interval = setInterval(() => {
            checkPendingAction();
        }, 5000);

        return () => clearInterval(interval);
    }, [accountData, contract, userAccount, selectedChainId]);

    // Refresh balance when pending operation completes (pendingActionKind goes from non-null to null)
    useEffect(() => {
        // If pendingActionKind changed from a non-null value to null, refresh balance
        if (prevPendingActionKindRef.current !== null && pendingActionKind === null) {
            console.log('Pending operation completed, refreshing balance...');
            if (contract && userAccount) {
                loadConfidentialBalance(0, true); // Force load to get latest balance
                loadPendingBalance(0, true); // Force load to get latest pending balance
            }
        }
        
        // Update ref for next comparison
        prevPendingActionKindRef.current = pendingActionKind;
    }, [pendingActionKind, contract, userAccount, keypair, wasmModule]);

    // When a new pending balance appears (or increases), allow the notification again
    useEffect(() => {
        const prev = prevPendingForDismissRef.current;
        const cur = pendingBalance;
        if (cur != null && cur > 0 && (prev == null || prev <= 0 || cur > prev)) {
            setPendingNotificationDismissed(false);
        }
        prevPendingForDismissRef.current = cur;
    }, [pendingBalance]);

    // When pending balance loads, decide whether to show notification
    useEffect(() => {
        if (!userAccount) {
            setShowPendingNotification(false);
            return;
        }
        
        // Hide notification when token selector modal is open
        if (showTokenSelector) {
            setShowPendingNotification(false);
            return;
        }
        
        // Show notification if there's a pending balance and it hasn't been dismissed
        const hasPending = pendingBalance != null && pendingBalance > 0;
        if (hasPending && !pendingNotificationDismissed) {
            setShowPendingNotification(true);
        } else {
            setShowPendingNotification(false);
        }
    }, [pendingBalance, userAccount, pendingNotificationDismissed, showTokenSelector]);
    
    // Inform parent of pending balance changes
    useEffect(() => {
        if (onPendingBalanceChange) {
            onPendingBalanceChange(pendingBalance);
        }
    }, [pendingBalance, onPendingBalanceChange]);
    
    // Check for pending deposits - only use contract state, never check Firebase
    useEffect(() => {
        if (!userAccount || !selectedChainId) {
            setHasPendingDeposit(false);
            return;
        }

        // Don't show pending deposits if account doesn't exist yet
        if (!accountExists) {
            setHasPendingDeposit(false);
            return;
        }

        // Only use contract state to determine pending deposits
        // pendingActionKind == 2 means DEPOSIT is pending (use == for type coercion with BigNumber)
        // Never check Firebase for pending actions - always get state from contract
        const hasContractPendingDeposit = pendingActionKind == 2; // 2 = DEPOSIT
        setHasPendingDeposit(accountExists && hasContractPendingDeposit);
    }, [userAccount, selectedChainId, accountExists, pendingActionKind]);
    
    // Register the secure claim handler with parent
    useEffect(() => {
        if (onSecureClaimRegister) {
            // Wrap in a function so the state setter stores a callable instead of treating it as an updater
            onSecureClaimRegister(() => () => {
                if (secureClaimHandlerRef.current) {
                    secureClaimHandlerRef.current();
                }
            });
        }
        return () => {
            // Clear the handler when unmounting
            if (onSecureClaimRegister) {
                onSecureClaimRegister(null);
            }
        };
    }, [onSecureClaimRegister]);

    // Update pending notification position to match deposit banner
    useEffect(() => {
        const updatePosition = () => {
            if (pendingNotificationRef.current && showPendingNotification) {
                const depositBanner = document.querySelector('.deposit-banner');
                if (depositBanner) {
                    // Deposit banner exists - position below it (current behavior)
                    const rect = depositBanner.getBoundingClientRect();
                    pendingNotificationRef.current.style.left = `${rect.left}px`;
                    // Reset top position to let CSS handle it (position below banner)
                    pendingNotificationRef.current.style.top = '';
                } else {
                    // No deposit banner - position at top where deposit banner would be
                    // Since deposit banner is in a grid with align-items: start, it aligns with main-card's top
                    const mainCard = document.querySelector('.main-card');
                    const mainContentLayout = document.querySelector('.main-content-layout');
                    const isMobile = window.innerHeight <= 820;
                    const isLargeScreen = window.innerWidth >= 1200 && window.innerHeight >= 821;
                    
                    if (mainCard && mainContentLayout) {
                        const rootStyles = getComputedStyle(document.documentElement);
                        const mainWidth = rootStyles.getPropertyValue('--main-width')?.trim() || (isMobile ? '600px' : '800px');
                        const mainWidthNum = parseInt(mainWidth);
                        const gap = isLargeScreen ? 32 : 16;
                        
                        // Calculate left position to match where deposit banner would be in grid column 3
                        // Deposit banner is at: center + main-width/2 + gap
                        const leftOffset = isMobile ? 
                            `calc(50vw + ${mainWidthNum}px / 2 + ${gap}px - 12px)` :
                            `calc(50vw + ${mainWidthNum}px / 2 + ${gap}px)`;
                        pendingNotificationRef.current.style.left = leftOffset;
                        
                        // Get the main-card's position - deposit banner aligns with it (align-items: start)
                        const cardRect = mainCard.getBoundingClientRect();
                        const layoutRect = mainContentLayout.getBoundingClientRect();
                        
                        // On larger screens, deposit banner might be sticky with top offset
                        // On MacBook, it's position: relative with top: 0
                        // So we use the main-card's top position directly
                        const topPosition = `${cardRect.top}px`;
                        pendingNotificationRef.current.style.top = topPosition;
                    } else {
                        // Fallback: use calculated values if elements not available
                        const mainWidth = isMobile ? 600 : 800;
                        const gap = isLargeScreen ? 32 : 16;
                        const viewportCenter = window.innerWidth / 2;
                        const leftOffset = isMobile ? 
                            viewportCenter + (mainWidth / 2) + gap - 12 : 
                            viewportCenter + (mainWidth / 2) + gap;
                        pendingNotificationRef.current.style.left = `${leftOffset}px`;
                        
                        // Fallback top position using main-content position
                        const mainContent = document.querySelector('.main-content');
                        if (mainContent) {
                            const contentRect = mainContent.getBoundingClientRect();
                            pendingNotificationRef.current.style.top = `${contentRect.top}px`;
                        } else {
                            // Last resort: use viewport calculation
                            const vh = window.innerHeight;
                            const headerGapMax = isMobile ? 24 : 144;
                            const headerGapMin = isMobile ? 12 : 32;
                            const headerGapVh = isMobile ? (vh * 0.025) : (vh * 0.08);
                            const headerGap = Math.max(headerGapMin, Math.min(headerGapMax, headerGapVh));
                            pendingNotificationRef.current.style.top = `${headerGap}px`;
                        }
                    }
                }
            }
        };

        // Update on mount and when notification is shown
        updatePosition();

        // Watch for deposit banner being added/removed from DOM
        // This handles the case when user closes the "Let's get started" banner
        const mainContentLayout = document.querySelector('.main-content-layout');
        let mutationObserver = null;
        let updateTimeout = null;
        
        if (mainContentLayout && showPendingNotification) {
            mutationObserver = new MutationObserver(() => {
                // Debounce updates to avoid excessive calls
                if (updateTimeout) {
                    clearTimeout(updateTimeout);
                }
                updateTimeout = setTimeout(() => {
                    updatePosition();
                }, 50); // Small delay to batch DOM changes
            });
            
            mutationObserver.observe(mainContentLayout, {
                childList: true,
                subtree: true
            });
        }

        // Update on window resize
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition);
        
        // Cleanup on unmount
        return () => {
            if (mutationObserver) {
                mutationObserver.disconnect();
            }
            if (updateTimeout) {
                clearTimeout(updateTimeout);
            }
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };

        return () => {
            if (mutationObserver) {
                mutationObserver.disconnect();
            }
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [showPendingNotification, confidentialBalance]);

    // Set balance to 0 for users without accounts
    // But don't clear if we just restored from cache (preserve cached value)
    useEffect(() => {
        if (contract && userAccount && !hasActiveAccount && !justRestoredFromCacheRef.current) {
            // Check if we have a cached balance first - if so, don't clear it
            const cacheKey = `${selectedChainId}_${selectedTokenId}`;
            const cached = balanceCacheRef.current.get(cacheKey);
            if (!cached || cached.available === null || cached.available === undefined) {
                setConfidentialBalance(0);
            }
        }
    }, [contract, userAccount, hasActiveAccount, selectedChainId, selectedTokenId]);

    // Set up blockchain event listeners for automatic balance updates
    useEffect(() => {
        if (!contract || !userAccount) return;

        // Soroban gateway facade has no ethers `filters` / `on` — balance updates use polling + post-tx refresh.
        if (contract.isStellarSoroban || !contract.filters || typeof contract.on !== 'function') {
            return;
        }

        console.log('Setting up blockchain event listeners for balance updates');
        
        // Listen for account creation processed events
        // This ensures UI refreshes when account is created on-chain
        const createAccountFilter = contract.filters.CreateAccountProcessed(userAccount);
        const createAccountListener = () => {
            console.log('CreateAccountProcessed event received, refreshing account status');
            // Throttle to avoid immediate RPC calls right after event
            const now = Date.now();
            if (now - lastAccountStatusCheckRef.current >= 2000) {
                lastAccountStatusCheckRef.current = now;
                setTimeout(async () => {
                    try {
                        // Load the balance (this is account creation, not a balance-changing operation)
                        await loadConfidentialBalance(0, true);
                    } catch (error) {
                        console.error('Error loading balance after account creation:', error);
                    }
                    // Refresh account status to update accountExists and hasActiveAccount
                    if (onOperationCompleteRef.current) {
                        await onOperationCompleteRef.current();
                    }
                }, 1500); // Wait 1.5 seconds for blockchain state to update
            }
        };
        
        // Listen for deposit processed events
        const depositFilter = contract.filters.DepositProcessed(userAccount);
        const depositListener = () => {
            console.log('Deposit processed event received, updating balances and account data');
            setTimeout(async () => {
                try {
                    // AWAIT the balance load to ensure new balance is displayed before clearing pending status
                    await loadConfidentialBalance(0, true); // Force load after operation
                } catch (error) {
                    console.error('Error loading balance after deposit:', error);
                }
                loadPendingBalance(0, true); // Force load after operation
                updateTokenBalance();
                // Refresh account data to clear pendingAction flag AFTER balance is loaded
                if (onOperationCompleteRef.current) {
                    await onOperationCompleteRef.current();
                }
            }, 2000); // Wait 2 seconds for transaction to be mined
        };

        // Listen for withdraw processed events
        const withdrawFilter = contract.filters.WithdrawProcessed(userAccount);
        const withdrawListener = () => {
            console.log('Withdraw processed event received, updating balances and account data');
            setTimeout(async () => {
                try {
                    // AWAIT the balance load to ensure new balance is displayed before clearing pending status
                    await loadConfidentialBalance(0, true); // Force load after operation
                } catch (error) {
                    console.error('Error loading balance after withdraw:', error);
                }
                loadPendingBalance(0, true); // Force load after operation
                updateTokenBalance();
                // Refresh account data to clear pendingAction flag AFTER balance is loaded
                if (onOperationCompleteRef.current) {
                    await onOperationCompleteRef.current();
                }
            }, 2000);
        };

        // Listen for secure claim processed events
        const applyFilter = contract.filters.ApplyPendingProcessed(userAccount);
        const applyListener = () => {
            console.log('Secure claim processed event received, updating balances and account data');
            setTimeout(async () => {
                try {
                    // AWAIT the balance load to ensure new balance is displayed before clearing pending status
                    await loadConfidentialBalance(0, true); // Force load after operation
                } catch (error) {
                    console.error('Error loading balance after secure claim:', error);
                }
                loadPendingBalance(0, true); // Force load after operation
                // Claim is processed; allow the header button to reappear when there's new pending balance.
                if (onSecureClaimInFlightChange) {
                    onSecureClaimInFlightChange(false, null);
                }
                // Refresh account data to clear pendingAction flag AFTER balance is loaded
                if (onOperationCompleteRef.current) {
                    await onOperationCompleteRef.current();
                }
            }, 2000);
        };

        // Listen for transfer processed events (both as sender and recipient)
        const transferSenderFilter = contract.filters.TransferProcessed(userAccount, null);
        const transferRecipientFilter = contract.filters.TransferProcessed(null, userAccount);
        
        // Listener for when user sends a transfer
        const transferSenderListener = () => {
            console.log('Transfer processed event received (as sender), updating balances and account data');
            setTimeout(async () => {
                try {
                    // AWAIT the balance load to ensure new balance is displayed before clearing pending status
                    await loadConfidentialBalance(0, true); // Force load to get latest balance
                } catch (error) {
                    console.error('Error loading balance after transfer:', error);
                }
                loadPendingBalance(0, true); // Force load to get latest pending balance
                // Refresh account data to clear pendingAction flag AFTER balance is loaded
                if (onOperationCompleteRef.current) {
                    await onOperationCompleteRef.current();
                }
            }, 2000);
        };
        
        // Listener for when user receives a transfer - reload pending balance immediately
        const transferRecipientListener = async (sender, recipient, txId, ok, err, token, ...restArgs) => {
            console.log('Transfer processed event received (as recipient), updating pending balance', {
                sender,
                recipient,
                txId: txId.toString(),
                ok,
                token,
                userAccount
            });
            
            // Verify that recipient matches current user (should be true due to filter, but double-check)
            if (recipient && recipient.toLowerCase() === userAccount.toLowerCase() && ok) {
                console.log('Confirmed: current user is recipient of successful transfer, reloading pending balance');
                
                // Clear any pending timeout to prevent multiple scheduled loads
                if (recipientTransferTimeoutRef.current) {
                    clearTimeout(recipientTransferTimeoutRef.current);
                    recipientTransferTimeoutRef.current = null;
                }
                
                // Use a single timeout to avoid duplicate RPC calls
                // Delay slightly to allow blockchain state to update after event emission
                recipientTransferTimeoutRef.current = setTimeout(async () => {
                    recipientTransferTimeoutRef.current = null;
                    try {
                        // Only reload pending balance - this is what the user needs to see
                        // Confidential balance and account data will be updated by polling or other operations
                        // loadPendingBalance already has protection against concurrent calls
                        loadPendingBalance(0, true); // Force load to get latest pending balance
                        console.log('Pending balance reload triggered for received transfer');
                    } catch (error) {
                        console.error('Error reloading pending balance for received transfer:', error);
                    }
                }, 800); // Single 800ms delay (balances speed and reliability)
            } else {
                if (!ok) {
                    console.log('Transfer failed, skipping balance reload');
                } else {
                    console.warn('Transfer recipient does not match current user, skipping balance reload');
                }
            }
        };

        // Set up event listeners
        contract.on(createAccountFilter, createAccountListener);
        contract.on(depositFilter, depositListener);
        contract.on(withdrawFilter, withdrawListener);
        contract.on(applyFilter, applyListener);
        contract.on(transferSenderFilter, transferSenderListener);
        contract.on(transferRecipientFilter, transferRecipientListener);

        // Cleanup function to remove listeners and clear pending timeouts
        return () => {
            console.log('Cleaning up blockchain event listeners');
            
            // Clear any pending timeout
            if (recipientTransferTimeoutRef.current) {
                clearTimeout(recipientTransferTimeoutRef.current);
                recipientTransferTimeoutRef.current = null;
            }
            
            contract.off(createAccountFilter, createAccountListener);
            contract.off(depositFilter, depositListener);
            contract.off(withdrawFilter, withdrawListener);
            contract.off(applyFilter, applyListener);
            contract.off(transferSenderFilter, transferSenderListener);
            contract.off(transferRecipientFilter, transferRecipientListener);
        };
    }, [contract, userAccount, keypair]);

    // Polling fallback for balance updates (in case event listeners don't work)
    // Use more aggressive polling for Privy/WalletConnect as they may not support event subscriptions reliably
    useEffect(() => {
        if (!contract || !userAccount || !keypair) return;

        const isPrivy = activeWalletOrigin === 'privy';
        // Soroban facades have no ethers `filters` / `on`, so incoming transfers never fire
        // TransferProcessed listeners — receivers only pick up changes via polling (or tab focus).
        const isStellarSoroban = !!contract?.isStellarSoroban;
        const pollIntervalMs = isPrivy || isStellarSoroban ? 5000 : 30000;

        console.log(
            `Setting up balance polling fallback (${isPrivy ? 'Privy' : isStellarSoroban ? 'Soroban' : 'standard'} mode: ${pollIntervalMs}ms interval)`
        );
        
        // Poll for balance updates
        const pollInterval = setInterval(() => {
            console.log('Polling for balance updates');
            loadConfidentialBalance(0, true); // Force load on poll
            loadPendingBalance(0, true); // Force load on poll
            updateTokenBalance();
        }, pollIntervalMs);

        // Refresh balance when page becomes visible (user might have received tokens while away)
        const handleVisibilityChange = () => {
            if (!document.hidden && contract && userAccount && keypair) {
                console.log('Page became visible, refreshing balances...');
                loadConfidentialBalance(0, true);
                loadPendingBalance(0, true);
                updateTokenBalance();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            console.log('Cleaning up balance polling');
            clearInterval(pollInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [contract, userAccount, keypair, activeWalletOrigin, selectedChainId, selectedTokenId]);

    // Helper function to refresh balances aggressively for Privy users
    const refreshBalancesForPrivy = (operationName) => {
        if (activeWalletOrigin === 'privy') {
            console.log(`[Privy] Setting up aggressive balance refresh after ${operationName}`);
            // Refresh immediately, then at 5s, 10s, 20s intervals
            const refreshDelays = [0, 5000, 10000, 20000];
            refreshDelays.forEach((delay, index) => {
                setTimeout(() => {
                    console.log(`[Privy] Balance refresh ${index + 1}/${refreshDelays.length} after ${operationName}`);
                    loadConfidentialBalance(0, true);
                    loadPendingBalance(0, true);
                    updateTokenBalance();
                }, delay);
            });
        }
    };

    // Notify parent of token data changes
    useEffect(() => {
        if (onTokenDataChange) {
            const tokenData = getTokenData();
            onTokenDataChange(tokenData);
        }
    }, [selectedTokenId, selectedChainId, onTokenDataChange]);

    // Key derivation is now automatic via the WASM hook

    // Helper function to convert hex to bytes
    const hexToBytes = (hex) => {
        const bytes = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
        }
        return bytes;
    };

    // Check if user has active account with non-zero balance
    const checkAccountStatus = () => {
        if (!accountExists) {
            return { hasAccount: false, hasBalance: false, message: 'No account found. Please go to the Deposit tab to create an account and add funds.' };
        }

        if (!hasActiveAccount) {
            return { hasAccount: true, hasBalance: false, message: 'Account exists but is not yet finalized. Please wait for account activation or go to the Deposit tab.' };
        }

        // If keypair is not available, we can't check balance yet
        if (!keypair) {
            return { hasAccount: true, hasBalance: false, message: 'Loading balance...' };
        }

        // If balance is null, it's still loading
        if (confidentialBalance === null) {
            return { hasAccount: true, hasBalance: false, message: 'Loading balance...' };
        }

        // Check if balance is greater than 0 (using > 0 instead of === 0 to handle floating point precision and very small balances)
        // confidentialBalance is stored as raw value (scaled by 100), so even 1 unit would be > 0
        if (confidentialBalance > 0) {
            return { hasAccount: true, hasBalance: true, message: '' };
        }

        // Balance is 0 or negative
        return { hasAccount: true, hasBalance: false, message: 'Account has zero balance. Please go to the Deposit tab to add funds.' };
    };

    // Wait for account activation function
    const waitForAccountActivation = async () => {
        setIsCreatingAccount(true);
        setAccountCreationStep('Waiting for account activation...');

        try {
            let attempts = 0;
            const maxAttempts = 30; // reduce attempts
            
            while (attempts < maxAttempts) {
                await sleep(2000); // poll every 2s to avoid rate limits
                await onOperationComplete(); // Refresh account status
                
                // Check if account is now active by checking the contract directly
                try {
                    const accountDataTuple = await contract.getAccountCore(userAccount);
                    const isActive = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized
                    
                    if (isActive) {
                        setAccountCreationStep('Account activated successfully!');
                        // Removed notification - account activation is already clear from UI state
                        setTimeout(() => {
                            setIsCreatingAccount(false);
                            setAccountCreationStep('');
                        }, 2000);
                        return true;
                    }
                } catch (error) {
                    console.error('Error checking account status:', error);
                }
                
                attempts++;
                setAccountCreationStep(`Waiting for account activation... (${attempts}/${maxAttempts}s)`);
            }
            
            // If we get here, account activation timed out
            setAccountCreationStep('Account activation timed out.');
            showNotification('Account activation timed out. Please try again later.', 'error');
            setTimeout(() => {
                setIsCreatingAccount(false);
                setAccountCreationStep('');
            }, 2000);
            return false;
            
        } catch (error) {
            console.error('Error waiting for account activation:', error);
            setAccountCreationStep('');
            const cleanError = getCleanErrorMessage(error);
            showNotification(`Error waiting for account activation: ${cleanError}`, 'error');
            return false;
        } finally {
            setIsCreatingAccount(false);
        }
    };

    // Create account function for deposit flow
    const createAccount = async () => {
        if (!contract || !keypair) {
            showNotification('Contract or keypair not ready. Please try again.', 'error');
            return false;
        }

        setIsCreatingAccount(true);
        setAccountCreationStep('Checking balances...');

        try {
            // Check gas and token balances before attempting account creation
            const chainConfig = CHAINS[selectedChainId];
            const nativeCurrencySymbol = chainConfig?.nativeCurrency?.symbol || 'ETH';
            const nativeCurrencyDecimals = chainConfig?.nativeCurrency?.decimals || 18;
            const isTempo = selectedChainId === 'tempo';
            const isStellarChain = selectedChainId === 'stellar';
            
            // Check gas balance (or fee token balance for Tempo)
            let gasBalance;
            let hasInsufficientGas = false;
            let feeTokenInfo = null;
            
            if (isStellarChain) {
                console.log('Stellar account creation: skipping EVM gas/token preflight checks');
            } else if (isTempo) {
                // For Tempo, check if user has any fee token balance
                try {
                    feeTokenInfo = await checkTempoFeeTokenBalance(provider, userAccount);
                    console.log('Tempo fee token check:', feeTokenInfo);
                    hasInsufficientGas = !feeTokenInfo.hasFeeToken;
                    if (feeTokenInfo.hasFeeToken) {
                        console.log(`Fee token balance: ${feeTokenInfo.feeTokenBalance} ${feeTokenInfo.feeTokenSymbol}`);
                    }
                } catch (balanceError) {
                    console.error('Error checking Tempo fee token balance:', balanceError);
                    throw new Error('Failed to check fee token balance. Please check your network connection.');
                }
            } else {
                // For other chains, check native gas balance with retry logic
                // This is especially important for Privy on some networks like Stable
                let lastError = null;
                const maxRetries = 3;
                for (let attempt = 0; attempt < maxRetries; attempt++) {
                    try {
                        if (attempt > 0) {
                            // Wait before retry (exponential backoff)
                            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                            console.log(`Retrying gas balance check (attempt ${attempt + 1}/${maxRetries})...`);
                        }
                        gasBalance = await provider.getBalance(userAccount);
                        // Use formatUnits with correct decimals instead of formatEther
                        const formattedBalance = ethers.utils.formatUnits(gasBalance, nativeCurrencyDecimals);
                        console.log('Gas balance:', formattedBalance, nativeCurrencySymbol);
                        hasInsufficientGas = gasBalance.isZero();
                        lastError = null; // Success, clear error
                        break; // Exit retry loop on success
                    } catch (balanceError) {
                        lastError = balanceError;
                        console.error(`Error checking gas balance (attempt ${attempt + 1}/${maxRetries}):`, balanceError);
                        // If this is the last attempt, throw the error
                        if (attempt === maxRetries - 1) {
                            // Provide more specific error message
                            const errorMessage = balanceError.message || String(balanceError);
                            if (isSSLError(balanceError)) {
                                const chainName = chainConfig?.name || 'this network';
                                throw new Error(`SSL certificate error connecting to ${chainName} RPC endpoint. The network may be experiencing issues. Please try again later.`);
                            } else if (errorMessage.includes('network') || errorMessage.includes('connection') || errorMessage.includes('timeout') || errorMessage.includes('HTTP request failed') || errorMessage.includes('fetch failed') || errorMessage.includes('RPC')) {
                                throw new Error('RPC connection error: Unable to connect to the network. This is likely a temporary issue. Please retry the operation.');
                            } else {
                                throw new Error(`RPC error: ${errorMessage}. This is likely a temporary network issue. Please retry the operation.`);
                            }
                        }
                    }
                }
            }
            
            // Check token balance
            let hasInsufficientToken = false;
            let tokenSymbol = null;
            const tokenData = getTokenData();
            if (!isStellarChain && tokenData && tokenData.address && tokenData.address !== '0x0000000000000000000000000000000000000000') {
                try {
                    const tokenContract = new ethers.Contract(
                        tokenData.address,
                        ['function balanceOf(address) view returns (uint256)'],
                        provider
                    );
                    const tokenBalance = await tokenContract.balanceOf(userAccount);
                    const formattedTokenBalance = ethers.utils.formatUnits(tokenBalance, tokenData.decimals || 18);
                    console.log('Token balance:', formattedTokenBalance, tokenData.symbol);
                    hasInsufficientToken = tokenBalance.isZero();
                    tokenSymbol = tokenData.symbol;
                } catch (tokenBalanceError) {
                    console.error('Error checking token balance:', tokenBalanceError);
                    // Don't block account creation if we can't check token balance
                    console.log('Could not check token balance, proceeding with account creation');
                }
            }
            
            // Show combined or individual error messages
            if (!isStellarChain && hasInsufficientGas && hasInsufficientToken) {
                let errorMsg;
                if (isTempo) {
                    const feeTokenSymbols = getTempoFeeTokenSymbols().join(', ');
                    errorMsg = (
                        <>
                            Insufficient testnet balances. You need one of the following testnet fee tokens to pay for transaction fees: {feeTokenSymbols}, and testnet {tokenSymbol} tokens to use this application. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet fee tokens and testnet {tokenSymbol}</a>, or switch to another chain/token.
                        </>
                    );
                } else {
                    // Special case for Arc where both gas and token are USDC
                    const isArc = selectedChainId === 'arc';
                    if (isArc && nativeCurrencySymbol === tokenSymbol) {
                        errorMsg = (
                            <>
                                Insufficient testnet {tokenSymbol} balance. You need testnet {tokenSymbol} tokens to pay for transaction fees and use this application. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {tokenSymbol}</a>, or switch to another chain/token.
                            </>
                        );
                    } else {
                        errorMsg = (
                            <>
                                Insufficient testnet balances. You need testnet {nativeCurrencySymbol} to pay for transaction fees and testnet {tokenSymbol} tokens to use this application. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {nativeCurrencySymbol} and testnet {tokenSymbol}</a>, or switch to another chain/token.
                            </>
                        );
                    }
                }
                // Only show notification if we haven't shown this exact error before
                const errorKey = `insufficient-both-${selectedChainId}-${tokenSymbol}`;
                if (lastBalanceErrorShownRef.current !== errorKey) {
                    showNotification(errorMsg, 'error', true);
                    lastBalanceErrorShownRef.current = errorKey;
                }
                setAccountCreationStep('');
                setIsCreatingAccount(false);
                return false;
            } else if (!isStellarChain && hasInsufficientGas) {
                let errorMsg;
                if (isTempo) {
                    const feeTokenSymbols = getTempoFeeTokenSymbols().join(', ');
                    errorMsg = (
                        <>
                            Insufficient testnet fee token balance. You need one of the following testnet fee tokens to pay for transaction fees: {feeTokenSymbols}. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet fee tokens</a> or switch to another chain.
                        </>
                    );
                } else {
                    errorMsg = (
                        <>
                            Insufficient testnet gas balance. You need testnet {nativeCurrencySymbol} to pay for transaction fees. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet {nativeCurrencySymbol}</a> or switch to another chain.
                        </>
                    );
                }
                // Only show notification if we haven't shown this exact error before
                const errorKey = `insufficient-gas-${selectedChainId}`;
                if (lastBalanceErrorShownRef.current !== errorKey) {
                    showNotification(errorMsg, 'error', true);
                    lastBalanceErrorShownRef.current = errorKey;
                }
                setAccountCreationStep('');
                setIsCreatingAccount(false);
                return false;
            } else if (!isStellarChain && hasInsufficientToken) {
                const errorMsg = (
                    <>
                        Insufficient testnet {tokenSymbol} balance. You need testnet {tokenSymbol} tokens to use this application. Please <a href={STELLAR_LAB_FUND_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" style={{color: 'black', textDecoration: 'underline'}}>get some testnet tokens</a> or switch to another chain/token.
                    </>
                );
                // Only show notification if we haven't shown this exact error before
                const errorKey = `insufficient-token-${selectedChainId}-${tokenSymbol}`;
                if (lastBalanceErrorShownRef.current !== errorKey) {
                    showNotification(errorMsg, 'error', true);
                    lastBalanceErrorShownRef.current = errorKey;
                }
                setAccountCreationStep('');
                setIsCreatingAccount(false);
                return false;
            }
            
            console.log('Balances verified. Creating account...');
            setAccountCreationStep('Creating account...');
            
            // Convert Base64 public key to hex for contract
            const pkBase64 = keypair.public_key;
            const pkBytes = Uint8Array.from(atob(pkBase64), c => c.charCodeAt(0));
            const pkHexForContract = '0x' + Array.from(pkBytes).map(b => b.toString(16).padStart(2, '0')).join('');
            
            // Submit to contract with hex public key
            setAccountCreationStep('Submitting account creation transaction...');
            let tx;
            const stellarTxContext = getStellarTxContext();
            if (stellarTxContext) {
                tx = await executeContractTxWithGasFallback(
                    contract,
                    'createConfidentialAccount',
                    [pkHexForContract],
                    { stellarContext: stellarTxContext }
                );
            } else {
            // Add gas fee overrides to prevent "maxFeePerGas less than block base fee" errors
            const gasOverrides = await getGasFeeOverrides();
            
            // Try to estimate gas first, and if it fails with UNPREDICTABLE_GAS_LIMIT, use a manual gas limit
            try {
                // First, try to estimate gas to see if the transaction would succeed
                const estimatedGas = await contract.estimateGas.createConfidentialAccount(pkHexForContract, gasOverrides);
                // If estimation succeeds, add a 20% buffer and include it in overrides
                const gasLimit = estimatedGas.mul(120).div(100);
                const overridesWithGas = { ...gasOverrides, gasLimit };
                tx = await contract.createConfidentialAccount(pkHexForContract, overridesWithGas);
            } catch (estimateError) {
                // Check if this is a gas-related error that can be fixed with a manual gas limit
                // This includes UNPREDICTABLE_GAS_LIMIT and "gas required exceeds allowance" errors
                const isGasError = estimateError.code === 'UNPREDICTABLE_GAS_LIMIT' || 
                                  estimateError.code === -32000 ||
                                  (estimateError.message && (
                                      estimateError.message.includes('UNPREDICTABLE_GAS_LIMIT') ||
                                      estimateError.message.includes('cannot estimate gas') ||
                                      estimateError.message.includes('gas required exceeds allowance')
                                  )) ||
                                  (estimateError.error && estimateError.error.message && (
                                      estimateError.error.message.includes('gas required exceeds allowance')
                                  ));
                
                if (isGasError) {
                    console.warn('Gas estimation failed, using manual gas limit:', estimateError);
                    // Use a higher gas limit to ensure the transaction goes through
                    // Account creation typically needs more gas than simple transfers
                    const manualGasLimit = 1000000; // Increased from 500000 to handle Ethereum's requirements
                    const overridesWithManualGas = { ...gasOverrides, gasLimit: manualGasLimit };
                    console.log('Using manual gas limit:', manualGasLimit);
                    
                    // Try the transaction with manual gas limit, and if it still fails with gas error, try without estimation
                    try {
                        tx = await contract.createConfidentialAccount(pkHexForContract, overridesWithManualGas);
                    } catch (txError) {
                        // If the transaction call itself fails with a gas error, it might be trying to estimate again
                        // In this case, we need to use the contract's populateTransaction method and send it directly
                        const isTxGasError = txError.code === 'UNPREDICTABLE_GAS_LIMIT' || 
                                             txError.code === -32000 ||
                                             (txError.message && (
                                                 txError.message.includes('UNPREDICTABLE_GAS_LIMIT') ||
                                                 txError.message.includes('cannot estimate gas') ||
                                                 txError.message.includes('gas required exceeds allowance')
                                             ));
                        
                        if (isTxGasError) {
                            console.warn('Transaction call also failed with gas error, using populateTransaction:', txError);
                            // Use populateTransaction to build the transaction without estimation, then send it
                            const populatedTx = await contract.populateTransaction.createConfidentialAccount(pkHexForContract);
                            populatedTx.gasLimit = manualGasLimit;
                            if (overridesWithManualGas.maxFeePerGas) {
                                populatedTx.maxFeePerGas = overridesWithManualGas.maxFeePerGas;
                            }
                            if (overridesWithManualGas.maxPriorityFeePerGas) {
                                populatedTx.maxPriorityFeePerGas = overridesWithManualGas.maxPriorityFeePerGas;
                            }
                            tx = await contract.signer.sendTransaction(populatedTx);
                        } else {
                            throw txError;
                        }
                    }
                } else {
                    // For other errors, re-throw them as they indicate actual transaction failures
                    throw estimateError;
                }
            }
            }
            
            setAccountCreationStep('Waiting for transaction confirmation...');
            await tx.wait();
            
            setAccountCreationStep('Creating account');
            // Removed "Creating account" notification - step indicator already shows progress
            
            // Wait for account to be processed
            setAccountCreationStep('Waiting for account to be activated...');
            let attempts = 0;
            const maxAttempts = 30; // 30 seconds
            
            while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Check if account is now active by checking the contract directly
                // Using the same approach as the top bar status check
                try {
                    const accountDataTuple = await contract.getAccountCore(userAccount);
                    const isActive = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized
                    
                    if (isActive) {
                        setAccountCreationStep('Account activated successfully!');
                        // Removed notification - account activation is already clear from UI state
                        // Clear account creation failed flag on success
                        setAccountCreationFailed(false);
                        
                        // Refresh the parent state to update the top bar
                        await onOperationComplete();
                        
                        setTimeout(() => {
                            setIsCreatingAccount(false);
                            setAccountCreationStep('');
                        }, 2000);
                        return true;
                    }
                } catch (error) {
                    console.error('Error checking account activation status:', error);
                }
                
                attempts++;
                setAccountCreationStep(`Waiting for account to be activated... (${attempts}/${maxAttempts}s)`);
            }
            
            // If we get here, account creation is still pending
            setAccountCreationStep('Account creation pending. You can proceed with deposit.');
            //showNotification('Account creation is pending. You can proceed with deposit.', 'info');
            setTimeout(() => {
                setIsCreatingAccount(false);
                setAccountCreationStep('');
            }, 2000);
            return true;
            
        } catch (error) {
            console.error('Error creating account:', error);
            setAccountCreationStep('');
            const cleanError = getCleanErrorMessage(error);
            // Check if this is an RPC error and adjust message accordingly
            const errorMessage = error.message || String(error);
            const isRpcError = errorMessage.includes('HTTP request failed') || 
                              errorMessage.includes('fetch failed') || 
                              errorMessage.includes('RPC') || 
                              errorMessage.includes('network') || 
                              errorMessage.includes('connection') ||
                              cleanError.includes('RPC connection error') ||
                              cleanError.includes('RPC error');
            if (isRpcError) {
                showNotification(`Account creation failed due to RPC connection error. This is likely a temporary network issue. Please retry the operation.`, 'error');
            } else {
                showNotification(`Account creation failed: ${cleanError}`, 'error');
            }
            // Mark account creation as failed so "Get Keys" buttons show again
            setAccountCreationFailed(true);
            // Reset keysDerived so "Get Keys" buttons show again, allowing user to retry
            // This allows users to retry account creation after cancellation/failure
            setKeysDerived(false);
            setKeyDerivationConsent(false); // Reset consent so they can go through flow again
            // Reset the attempt flag so user can retry account creation
            accountCreationAttemptedRef.current = false;
            return false;
        } finally {
            setIsCreatingAccount(false);
        }
    };

    const loadConfidentialBalance = async (retryCount = 0, forceLoad = false) => {
        const MAX_RETRIES = 2; // Will try up to 3 times total (initial + 2 retries)

        if (!contract || !userAccount) {
            if (retryCount === 0) {
                console.log('[loadConfidentialBalance] skip: missing contract or user', {
                    hasContract: !!contract,
                    isStellarFacade: !!contract?.isStellarSoroban,
                    hasUser: !!userAccount,
                });
            }
            return;
        }
        
        // Check if chain has changed during loading (race condition protection)
        if (loadingChainRef.current !== selectedChainId) {
            console.log('Chain changed during confidential balance load, aborting');
            return;
        }
        
        // Check if token has changed during loading (race condition protection)
        if (loadingTokenRef.current !== selectedTokenId) {
            console.log('Token changed during confidential balance load, aborting');
            return;
        }
        
        // CRITICAL: If we just restored from cache, don't load - preserve the cached value
        // UNLESS forceLoad is true (e.g., after operations that change the balance)
        const cacheKey = `${selectedChainId}_${selectedTokenId}`;
        if (retryCount === 0 && !forceLoad && justRestoredFromCacheRef.current && lastLoadedKeyRef.current === cacheKey) {
            console.log('Skipping loadConfidentialBalance - just restored from cache for', cacheKey);
            return;
        }
        
        // If forceLoad is true, clear the flag to allow this load
        if (forceLoad && justRestoredFromCacheRef.current) {
            console.log('Force loading balance - clearing cache restore flag');
            justRestoredFromCacheRef.current = false;
        }
        
        try {
            if (retryCount === 0) {
                console.log('[loadConfidentialBalance] reading account from contract (getAccountCore)…', {
                    hasWasm: !!wasmModule,
                    hasKeypair: !!keypair,
                });
            }

            // Get account data from contract (no WASM / local key required for this read)
            const accountDataTuple = await contract.getAccountCore(userAccount);
            const existsBool =
                accountDataTuple[0] === true ||
                accountDataTuple[0] === 1 ||
                accountDataTuple[0] === 1n;
            const finalizedBool =
                accountDataTuple[1] === true ||
                accountDataTuple[1] === 1 ||
                accountDataTuple[1] === 1n;
            const hasActiveAccountLocal = existsBool && finalizedBool;
            const accountExistsLocal = existsBool;
            if (retryCount === 0) {
                console.log('[loadConfidentialBalance] getAccountCore (ethers-shaped tuple)', {
                    user: userAccount,
                    isStellarFacade: !!contract?.isStellarSoroban,
                    contract: contract?.address,
                    exists_raw: accountDataTuple[0],
                    finalized_raw: accountDataTuple[1],
                    pending_action_raw: accountDataTuple[2],
                    existsBool,
                    finalizedBool,
                    hasActiveAccountLocal,
                    accountExistsLocal,
                    tx_id: accountDataTuple[3]?.toString?.(),
                });
            }

            // If account is active but props haven't been updated, refresh account status
            // This fixes the issue where account data shows active but deposit button is still disabled
            if (hasActiveAccountLocal && (!accountExists || !hasActiveAccount) && onOperationCompleteRef.current) {
                console.log('Account is active but props are stale, refreshing account status...');
                // Use setTimeout to avoid calling during render
                setTimeout(() => {
                    if (onOperationCompleteRef.current) {
                        onOperationCompleteRef.current();
                    }
                }, 0);
            }

            if (!hasActiveAccountLocal) {
                if (retryCount === 0) {
                    console.log('[loadConfidentialBalance] stop: account not active (need exists && finalized)', {
                        existsBool,
                        finalizedBool,
                        hint: !existsBool
                            ? 'no AccountCore row or exists=false'
                            : 'operator has not finalized account yet (finalized=false)',
                    });
                }
                // Only set to 0 if we didn't just restore from cache AND we don't already have a balance set
                // This prevents clearing a balance that was just restored from cache
                if (!justRestoredFromCacheRef.current && (confidentialBalance === null || confidentialBalance === undefined)) {
                    setConfidentialBalance(0);
                    // Update cache
                    const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                    const cached = balanceCacheRef.current.get(cacheKey) || {};
                    balanceCacheRef.current.set(cacheKey, { ...cached, available: 0 });
                } else if (justRestoredFromCacheRef.current) {
                    console.log('Skipping setting balance to 0 - we just restored from cache');
                }
                return;
            }

            if (!wasmModule || !keypair) {
                if (retryCount === 0) {
                    console.log(
                        '[loadConfidentialBalance] account is active; waiting for WASM + keypair before getAvailable / decrypt',
                        { hasWasm: !!wasmModule, hasKeypair: !!keypair }
                    );
                }
                return;
            }

            // Check again if token has changed during async operation (race condition protection)
            if (loadingTokenRef.current !== selectedTokenId) {
                console.log('Token changed during confidential balance load (after account check), aborting');
                return;
            }
            
            // Get available balance ciphertext from contract
            const currentTokenData = getTokenData();
            const tokenAddr = currentTokenData.address; // Use selected token for balance check
            if (retryCount === 0) {
                console.log('Loading balance for token:', selectedTokenId, 'at address:', tokenAddr);
            }
            const [availC1, availC2] = await contract.getAvailable(userAccount, tokenAddr);
            
            // Check again if token has changed after contract call (race condition protection)
            if (loadingTokenRef.current !== selectedTokenId) {
                console.log('Token changed during confidential balance load (after getAvailable), aborting');
                return;
            }
            
            if (retryCount === 0) {
                console.log('Raw ciphertext from contract:', { availC1, availC2 });
            }
            
            // Check if ciphertext is truly uninitialized (empty strings, not zero values)
            // Note: C2 can be all zeros and still be a valid ciphertext that decrypts correctly
            const c1Empty = !availC1 || availC1 === '0x';
            const c2Empty = !availC2 || availC2 === '0x';
            
            if (c1Empty && c2Empty) {
                if (retryCount === 0) {
                    console.log('Empty ciphertext detected (no data returned from contract)');
                }
                // Only set to 0 if we didn't just restore from cache AND we don't already have a balance set
                // This prevents clearing a balance that was just restored from cache
                if (!justRestoredFromCacheRef.current && (confidentialBalance === null || confidentialBalance === undefined)) {
                    if (retryCount === 0) {
                        console.log('Setting balance to 0');
                    }
                    setConfidentialBalance(0);
                    // Update cache
                    const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                    const cached = balanceCacheRef.current.get(cacheKey) || {};
                    balanceCacheRef.current.set(cacheKey, { ...cached, available: 0 });
                } else if (justRestoredFromCacheRef.current) {
                    if (retryCount === 0) {
                        console.log('Skipping setting balance to 0 - we just restored from cache');
                    }
                }
                return;
            }
            
            // Convert hex to bytes and combine into 64-byte ciphertext (32 bytes commitment + 32 bytes handle)
            const c1Bytes = hexToBytes(availC1.slice(2));
            const c2Bytes = hexToBytes(availC2.slice(2));
            const combinedBytes = new Uint8Array(64);
            combinedBytes.set(c1Bytes, 0);
            combinedBytes.set(c2Bytes, 32);
            const combinedCiphertext = btoa(String.fromCharCode(...combinedBytes));

            if (retryCount === 0) {
                console.log('Converted ciphertext:', { 
                    c1Hex: availC1, 
                    c2Hex: availC2, 
                    combinedCiphertext 
                });
            }
            
            // Decrypt the balance using the user's derived secret key
            try {
                if (retryCount === 0) {
                    safeLog('Decrypting confidential balance...');
                }
                
                // Check if keypair is available
                if (!keypair || !keypair.private_key) {
                    if (retryCount === 0) {
                        console.log('Keypair not available for decryption');
                    }
                    // Don't clear balance if we just restored from cache OR if we already have a balance set
                    // This preserves the cached value and prevents clearing a balance that was just restored
                    if (!justRestoredFromCacheRef.current && (confidentialBalance === null || confidentialBalance === undefined)) {
                        setConfidentialBalance(0);
                    } else if (justRestoredFromCacheRef.current) {
                        console.log('Keypair not available but preserving cached balance');
                    }
                    return;
                }
                
                // Create secure wrapper for keypair
                const secureKeypair = createSecureKeypair(keypair);
                
                // Get the private key before clearing
                const privateKey = secureKeypair.getPrivateKey();
                if (!privateKey) {
                    throw new Error('Failed to retrieve private key from secure wrapper');
                }
                
                // Validate inputs before decryption
                if (!combinedCiphertext || combinedCiphertext.length === 0) {
                    throw new Error('Invalid ciphertext: empty or null');
                }
                if (!privateKey || privateKey.length === 0) {
                    throw new Error('Invalid keypair: empty or null');
                }
                
                // Ensure WASM module is initialized before calling decrypt_ciphertext
                // The decrypt_ciphertext function uses a global wasm variable that needs to be initialized
                try {
                    await init();
                } catch (initError) {
                    secureKeypair.clear();
                    if (retryCount === 0) {
                        console.error('Failed to initialize WASM module:', initError);
                    }
                    throw new Error(`WASM initialization failed: ${initError?.message || initError}`);
                }
                
                if (retryCount === 0) {
                    console.log('Calling decrypt_ciphertext with:', {
                        ciphertextLength: combinedCiphertext.length,
                        keypairLength: privateKey.length,
                        wasmModuleAvailable: !!wasmModule
                    });
                }
                
                let decryptedResult;
                try {
                    decryptedResult = decrypt_ciphertext(
                        combinedCiphertext,
                        privateKey
                    );
                } catch (wasmError) {
                    // Clear the secure keypair from memory before rethrowing
                    secureKeypair.clear();
                    if (retryCount === 0) {
                        console.error('WASM decrypt_ciphertext threw an error:', wasmError);
                        console.error('Error details:', {
                            message: wasmError?.message,
                            stack: wasmError?.stack,
                            name: wasmError?.name,
                            toString: String(wasmError)
                        });
                    }
                    throw wasmError;
                }
                
                // Clear the secure keypair from memory
                secureKeypair.clear();

                // WASM decryption completed
                
                if (decryptedResult === undefined || decryptedResult === null) {
                    throw new Error('WASM decryption returned undefined/null');
                }
                
                // Parse JSON response from WASM
                const result = JSON.parse(decryptedResult);
                
                // Check again if token has changed after decryption (race condition protection)
                if (loadingTokenRef.current !== selectedTokenId) {
                    console.log('Token changed during confidential balance load (after decryption), aborting');
                    return;
                }
                
                const numericBalance = parseFloat(result.decrypted_amount);
                if (isNaN(numericBalance)) {
                    if (retryCount === 0) {
                        console.error('Decrypted amount is not a number:', result.decrypted_amount);
                    }
                    // Only set to 0 if we didn't just restore from cache
                    if (!justRestoredFromCacheRef.current) {
                        setConfidentialBalance(0);
                    }
                } else {
                    console.log('Final numeric balance:', numericBalance);
                    console.log('Setting confidentialBalance state to:', numericBalance);
                    // Always update with the new balance - it's the correct value from the contract
                    setConfidentialBalance(numericBalance);
                    // Update cache with the new balance
                    const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                    const cached = balanceCacheRef.current.get(cacheKey) || {};
                    balanceCacheRef.current.set(cacheKey, { ...cached, available: numericBalance });
                    // Only clear the flag if this was the key we restored from cache
                    if (lastLoadedKeyRef.current === cacheKey) {
                        justRestoredFromCacheRef.current = false;
                    }
                }
            } catch (decryptError) {
                // Always log the error on first attempt for debugging
                if (retryCount === 0) {
                    console.error('Decryption error (attempt 1):', decryptError);
                    console.error('Error message:', decryptError?.message);
                    console.error('Error stack:', decryptError?.stack);
                }
                
                // Retry logic for decryption errors
                if (retryCount < MAX_RETRIES) {
                    if (retryCount === 0) {
                        console.log(`Retrying decryption (attempt ${retryCount + 2}/${MAX_RETRIES + 1})...`);
                    }
                    await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
                    return loadConfidentialBalance(retryCount + 1);
                }
                
                // Log error details after all retries failed
                const errorMsg = decryptError?.message || String(decryptError);
                console.error('WASM decryption error after all retries:', {
                    error: decryptError,
                    message: errorMsg,
                    retryCount: retryCount + 1
                });
                
                // Only set to 0 if we didn't just restore from cache AND we don't already have a balance
                // This preserves the cached value and prevents clearing a balance that was just restored
                if (!justRestoredFromCacheRef.current && (confidentialBalance === null || confidentialBalance === undefined)) {
                    setConfidentialBalance(0);
                } else if (justRestoredFromCacheRef.current) {
                    console.log('Decryption error but preserving cached balance');
                }
            }
            
        } catch (error) {
            // Retry logic for general errors
            // For SSL errors, use longer delays as they're often infrastructure issues
            const isSSL = isSSLError(error);
            const retryDelay = isSSL ? 2000 * (retryCount + 1) : 300 * (retryCount + 1);
            
            if (retryCount < MAX_RETRIES) {
                if (isSSL) {
                    console.warn(`SSL error detected, retrying with longer delay (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
                }
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return loadConfidentialBalance(retryCount + 1);
            }
            
            // Only log error if all retries failed
            console.error('Error loading confidential balance after retries:', error);
            
            // Show user-friendly error message for SSL errors
            if (isSSL) {
                const chainConfig = CHAINS[selectedChainId];
                const chainName = chainConfig?.name || 'this network';
                console.error(getNetworkErrorMessage(error, chainName));
            }
            
            // Only set to 0 if we didn't just restore from cache
            if (!justRestoredFromCacheRef.current) {
                setConfidentialBalance(0);
                // Update cache
                const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                const cached = balanceCacheRef.current.get(cacheKey) || {};
                balanceCacheRef.current.set(cacheKey, { ...cached, available: 0 });
            }
        }
    };

    const loadPendingBalance = async (retryCount = 0, forceLoad = false) => {
        const MAX_RETRIES = 2; // Will try up to 3 times total (initial + 2 retries)

        const isPendingLimbEmpty = (hex) => {
            if (hex == null || hex === '' || hex === '0x') return true;
            const body = String(hex).startsWith('0x') ? String(hex).slice(2) : String(hex);
            if (body.length === 0) return true;
            return /^0+$/.test(body);
        };
        
        // Prevent multiple simultaneous loads
        if (retryCount === 0 && loadingPendingBalanceRef.current) {
            console.log('Pending balance load already in progress, skipping');
            return;
        }
        
        if (!contract || !userAccount) {
            if (retryCount === 0) {
                console.log('[loadPendingBalance] skip: missing contract or user', {
                    hasContract: !!contract,
                    isStellarFacade: !!contract?.isStellarSoroban,
                    hasUser: !!userAccount,
                });
            }
            loadingPendingBalanceRef.current = false;
            return;
        }
        
        // Check if chain has changed during loading (race condition protection)
        if (loadingChainRef.current !== selectedChainId) {
            console.log('Chain changed during pending balance load, aborting');
            loadingPendingBalanceRef.current = false;
            return;
        }
        
        // CRITICAL: If we just restored from cache, don't load - preserve the cached value
        // UNLESS forceLoad is true (e.g., after operations that change the balance)
        const cacheKey = `${selectedChainId}_${selectedTokenId}`;
        if (retryCount === 0 && !forceLoad && justRestoredFromCacheRef.current && lastLoadedKeyRef.current === cacheKey) {
            console.log('Skipping loadPendingBalance - just restored from cache for', cacheKey);
            loadingPendingBalanceRef.current = false;
            return;
        }
        
        // If forceLoad is true, clear the flag to allow this load
        if (forceLoad && justRestoredFromCacheRef.current) {
            console.log('Force loading pending balance - clearing cache restore flag');
            justRestoredFromCacheRef.current = false;
        }
        
        // Mark as loading (only on first attempt)
        if (retryCount === 0) {
            loadingPendingBalanceRef.current = true;
        }

        try {
            if (retryCount === 0) {
                console.log('[loadPendingBalance] reading account from contract (getAccountCore)…', {
                    hasWasm: !!wasmModule,
                    hasKeypair: !!keypair,
                });
            }
            
            // Get account data from contract (no WASM / local key required for this read)
            const accountDataTuple = await contract.getAccountCore(userAccount);
            const existsBoolP =
                accountDataTuple[0] === true ||
                accountDataTuple[0] === 1 ||
                accountDataTuple[0] === 1n;
            const finalizedBoolP =
                accountDataTuple[1] === true ||
                accountDataTuple[1] === 1 ||
                accountDataTuple[1] === 1n;
            const hasActiveAccount = existsBoolP && finalizedBoolP;
            const hexByteLenLocal = (hex) => {
                if (hex == null || typeof hex !== 'string') return null;
                const h = hex.startsWith('0x') ? hex.slice(2) : hex;
                return Math.floor(h.length / 2);
            };
            if (retryCount === 0) {
                console.log('[loadPendingBalance] getAccountCore (full snapshot)', {
                    user: userAccount,
                    chain: selectedChainId,
                    token: selectedTokenId,
                    contract: contract?.address,
                    isStellarSoroban: !!contract?.isStellarSoroban,
                    exists_raw: accountDataTuple[0],
                    finalized_raw: accountDataTuple[1],
                    pending_action_raw: accountDataTuple[2],
                    tx_id: accountDataTuple[3]?.toString?.() ?? String(accountDataTuple[3]),
                    elgamal_pubkey_bytes: hexByteLenLocal(accountDataTuple[4]),
                    elgamal_pubkey_hex: accountDataTuple[4],
                    pending_credit_counter:
                        accountDataTuple[5]?.toString?.() ?? String(accountDataTuple[5]),
                    minimum_pending_credit_counter:
                        accountDataTuple[6]?.toString?.() ?? String(accountDataTuple[6]),
                    existsBool: existsBoolP,
                    finalizedBool: finalizedBoolP,
                    hasActiveAccount,
                });
            }

            // Check chain again before proceeding (async operations might take time)
            if (loadingChainRef.current !== selectedChainId) {
                console.log('Chain changed during pending balance load (after account check), aborting');
                loadingPendingBalanceRef.current = false;
                return;
            }
            
            if (!hasActiveAccount) {
                if (retryCount === 0) {
                    console.log('No active account found for pending balance');
                }
                // Only set if chain hasn't changed
                if (loadingChainRef.current === selectedChainId) {
                    setPendingBalance(0);
                    // Update cache
                    const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                    const cached = balanceCacheRef.current.get(cacheKey) || {};
                    balanceCacheRef.current.set(cacheKey, { ...cached, pending: 0 });
                }
                return;
            }

            if (!wasmModule || !keypair) {
                if (retryCount === 0) {
                    console.log(
                        '[loadPendingBalance] account is active; waiting for WASM + keypair before getPending / decrypt',
                        { hasWasm: !!wasmModule, hasKeypair: !!keypair }
                    );
                }
                loadingPendingBalanceRef.current = false;
                return;
            }

            // Detect wrong local keys (causes "Failed to decode discrete log" on decrypt).
            const onChainPkBody = contractElGamalPubkeyToHexBody(accountDataTuple[4]);
            const derivedPkBody = elGamalPubkeyBase64ToHexBody(keypair.public_key);
            const pkMatch = elGamalPubkeyHexBodiesEqual(onChainPkBody, derivedPkBody);
            if (retryCount === 0) {
                console.log('[loadPendingBalance] ElGamal pubkey check (derived vs get_account_core[4])', {
                    match: pkMatch,
                    on_chain_hex_len: onChainPkBody?.length ?? 0,
                    derived_hex_len: derivedPkBody?.length ?? 0,
                    on_chain_prefix: onChainPkBody ? `${onChainPkBody.slice(0, 16)}…` : null,
                    derived_prefix: derivedPkBody ? `${derivedPkBody.slice(0, 16)}…` : null,
                });
            }
            if (!pkMatch && onChainPkBody && derivedPkBody && retryCount === 0) {
                const warnKey = `${userAccount}|${selectedChainId}|${verifyingContract}|${onChainPkBody.slice(0, 16)}`;
                console.error(
                    '[loadPendingBalance] Derived ElGamal pubkey does NOT match the pubkey stored on-chain for this account. ' +
                        'Incoming ciphertext was encrypted to the on-chain key; your cached or newly derived keys cannot decrypt it. ' +
                        'Clear stored derived keys (Secure Cache Manager or remove localStorage keys prefixed ConfidentialBalances_) ' +
                        'and complete key derivation again with the same wallet that registered this account. ' +
                        'If this started after an app update, the key-derivation domain may have changed — contact the team.'
                );
                if (elGamalOnChainMismatchNotifiedKeyRef.current !== warnKey) {
                    elGamalOnChainMismatchNotifiedKeyRef.current = warnKey;
                    showNotification(
                        'Your encryption keys do not match this account on-chain (see browser console). ' +
                            'Clear derived keys in Secure Cache Manager, then sign key setup again with the same wallet.',
                        'error',
                        true
                    );
                }
            }

            // Get pending balance ciphertext from contract
            const currentTokenData = getTokenData();
            const tokenAddr = currentTokenData.address; // Use selected token for balance check
            const [pendingC1, pendingC2] = await contract.getPending(userAccount, tokenAddr);

            // Also surface `get_available` so both sides are visible when debugging.
            let availableC1 = null;
            let availableC2 = null;
            if (typeof contract.getAvailable === 'function') {
                try {
                    [availableC1, availableC2] = await contract.getAvailable(userAccount, tokenAddr);
                } catch (availErr) {
                    console.warn('[loadPendingBalance] getAvailable failed (debug only):', availErr);
                }
            }
            
            // Check chain again after async operation
            if (loadingChainRef.current !== selectedChainId) {
                console.log('Chain changed during pending balance load (after getPending), aborting');
                loadingPendingBalanceRef.current = false;
                return;
            }
            
            if (retryCount === 0) {
                console.log('[loadPendingBalance] pending+available ciphertexts', {
                    user: userAccount,
                    token: tokenAddr,
                    pending_c1_hex: pendingC1,
                    pending_c2_hex: pendingC2,
                    pending_c1_bytes: hexByteLenLocal(pendingC1),
                    pending_c2_bytes: hexByteLenLocal(pendingC2),
                    pending_c1_empty: isPendingLimbEmpty(pendingC1),
                    pending_c2_empty: isPendingLimbEmpty(pendingC2),
                    available_c1_hex: availableC1,
                    available_c2_hex: availableC2,
                    available_c1_bytes: hexByteLenLocal(availableC1),
                    available_c2_bytes: hexByteLenLocal(availableC2),
                });
            }
            
                // Check if ciphertext is empty (no pending balance) — Soroban may return longer all-zero hex
                if (isPendingLimbEmpty(pendingC1) && isPendingLimbEmpty(pendingC2)) {
                    if (retryCount === 0) {
                        console.log('Empty pending ciphertext detected');
                    }
                    if (loadingChainRef.current === selectedChainId && (!justRestoredFromCacheRef.current || forceLoad)) {
                        if (retryCount === 0) {
                            console.log('Setting pending balance to 0 (empty ciphertext)');
                        }
                        setPendingBalance(0);
                        const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                        const cached = balanceCacheRef.current.get(cacheKey) || {};
                        balanceCacheRef.current.set(cacheKey, { ...cached, pending: 0 });
                    } else if (justRestoredFromCacheRef.current && retryCount === 0) {
                        console.log('Skipping pending=0 overwrite — restored from cache (use forceLoad to refresh)');
                    }
                    return;
                }
            
            // Convert hex to bytes and combine into 64-byte ciphertext (32 bytes commitment + 32 bytes handle)
            const p1Bytes = hexToBytes(pendingC1.slice(2));
            const p2Bytes = hexToBytes(pendingC2.slice(2));
            const combinedPendingBytes = new Uint8Array(64);
            combinedPendingBytes.set(p1Bytes, 0);
            combinedPendingBytes.set(p2Bytes, 32);
            const combinedPendingCiphertext = btoa(String.fromCharCode(...combinedPendingBytes));

            if (retryCount === 0 && (p1Bytes.length !== 32 || p2Bytes.length !== 32)) {
                console.warn('[loadPendingBalance] unexpected limb byte length (expected 32 each):', {
                    p1_len: p1Bytes.length,
                    p2_len: p2Bytes.length,
                });
            }
            if (retryCount === 0) {
                console.log('[loadPendingBalance] Converted pending ciphertext:', {
                    p1_len: p1Bytes.length,
                    p2_len: p2Bytes.length,
                    p1Hex: pendingC1,
                    p2Hex: pendingC2,
                    combinedPendingCiphertext,
                });

                console.log('[loadPendingBalance] Keypair info for pending:', {
                    private_key_length: keypair.private_key.length,
                    private_key_preview: keypair.private_key.substring(0, 50) + '...',
                    private_key_format: 'base64',
                });
            }

            // Decrypt the pending balance using the user's derived secret key
            try {
                try {
                    await init();
                } catch (initErr) {
                    console.error('[loadPendingBalance] WASM init() before pending decrypt failed:', initErr);
                    throw initErr;
                }

                const decryptedPendingResult = decrypt_ciphertext(
                    combinedPendingCiphertext,
                    keypair.private_key
                );

                if (retryCount === 0) {
                    console.log('[loadPendingBalance] Raw WASM pending result:', decryptedPendingResult);
                    console.log('[loadPendingBalance] WASM pending result type:', typeof decryptedPendingResult);
                }
                
                if (decryptedPendingResult === undefined || decryptedPendingResult === null) {
                    throw new Error('WASM decryption returned undefined/null');
                }
                
                // Parse JSON response from WASM
                const result = JSON.parse(decryptedPendingResult);
                if (retryCount === 0) {
                    console.log('[loadPendingBalance] Parsed WASM pending result:', result);
                }
                
                // Check chain again before setting state (decryption might have taken time)
                if (loadingChainRef.current !== selectedChainId) {
                    console.log('Chain changed during pending balance load (after decryption), aborting');
                    loadingPendingBalanceRef.current = false;
                    return;
                }
                
                const numericPendingBalance = parseFloat(result.decrypted_amount);
                if (isNaN(numericPendingBalance)) {
                    if (retryCount === 0) {
                        console.warn('[loadPendingBalance] decrypted_amount is not a parseable number:', {
                            decrypted_amount: result.decrypted_amount,
                            raw_keys: result && typeof result === 'object' ? Object.keys(result) : null,
                        });
                    }
                    // Only set if chain hasn't changed and we didn't just restore from cache
                    if (loadingChainRef.current === selectedChainId && !justRestoredFromCacheRef.current) {
                        setPendingBalance(0);
                    }
                } else {
                    console.log('[loadPendingBalance] Final numeric pending balance (raw units):', numericPendingBalance);
                    // Only set if chain hasn't changed
                    if (loadingChainRef.current === selectedChainId) {
                        setPendingBalance(numericPendingBalance);
                        // Update cache with the new pending balance
                        const cacheKey = `${selectedChainId}_${selectedTokenId}`;
                        const cached = balanceCacheRef.current.get(cacheKey) || {};
                        balanceCacheRef.current.set(cacheKey, { ...cached, pending: numericPendingBalance });
                        // Only clear the flag if this was the key we restored from cache
                        if (lastLoadedKeyRef.current === cacheKey) {
                            justRestoredFromCacheRef.current = false;
                        }
                    }
                }
                // Mark as not loading after successful completion
                loadingPendingBalanceRef.current = false;
            } catch (decryptError) {
                // Retry logic for decryption errors
                if (retryCount < MAX_RETRIES) {
                    await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
                    return loadPendingBalance(retryCount + 1);
                }
                
                // Only log error details if all retries failed, but don't spam console
                // Decryption errors can happen if keypair doesn't match the ciphertext (e.g., after chain change)
                if (retryCount === MAX_RETRIES) {
                    const errorMsg = decryptError?.message || String(decryptError);
                    const likelyKeyMismatch = errorMsg.includes('decode discrete log');
                    // Always log (incl. discrete-log failures) so console filters on `loadPendingBalance` show the real reason UI shows 0.
                    console.warn(
                        '[loadPendingBalance] pending decrypt failed after retries — setting pendingBalance to 0 for UI.',
                        {
                            errorMsg,
                            likelyKeyMismatch,
                            hint: likelyKeyMismatch
                                ? 'On-chain ciphertext was encrypted to a different ElGamal pubkey than your derived key (wallet / consent / cache).'
                                : 'Inspect stack below if needed.',
                            retriesExhaustedAt: retryCount,
                        }
                    );
                    if (!likelyKeyMismatch) {
                        console.error('[loadPendingBalance] WASM pending decryption error after retries:', decryptError);
                    }
                }
                // Only set if chain hasn't changed
                if (loadingChainRef.current === selectedChainId) {
                    setPendingBalance(0);
                }
                // Mark as not loading after error handling
                loadingPendingBalanceRef.current = false;
            }

        } catch (error) {
            // Check chain before retrying
            if (loadingChainRef.current !== selectedChainId) {
                console.log('Chain changed during pending balance load (in catch), aborting');
                loadingPendingBalanceRef.current = false;
                return;
            }
            
            // Retry logic for general errors
            if (retryCount < MAX_RETRIES) {
                await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
                return loadPendingBalance(retryCount + 1);
            }
            
            // Only log error if all retries failed
            console.error('Error loading pending balance after retries:', error);
            // Only set if chain hasn't changed and we didn't just restore from cache
            if (loadingChainRef.current === selectedChainId && !justRestoredFromCacheRef.current) {
                setPendingBalance(0);
            }
            // Mark as not loading after all retries exhausted
            loadingPendingBalanceRef.current = false;
        }
    };

    const updateTokenBalance = async (retryCount = 0) => {
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 1000; // Start with 1 second

        if (!userAccount) {
            // If user is not ready, keep balance as Loading...
            if (balance === 'Loading...') {
                return;
            }
            setBalance('Loading...');
            return;
        }

        const stellarPublicBalance = selectedChainIdForBalanceRef.current === 'stellar' && CHAINS.stellar;
        // EVM ERC-20 path needs ethers provider on the gateway contract
        if (!stellarPublicBalance && (!contract || !contract.provider)) {
            if (balance === 'Loading...') {
                return;
            }
            setBalance('Loading...');
            return;
        }

        let balanceFetchKey = '';
        try {
            const snap = publicBalanceTokenRef.current;
            const currentTokenData = snap?.data;
            balanceFetchKey = snap?.key || '';
            if (!currentTokenData || !currentTokenData.address) {
                setBalance('Token not found');
                return;
            }

            // Stellar / Soroban SAC: `balance(Address)` via simulation (no ethers provider).
            if (stellarPublicBalance) {
                const stellarCfg = CHAINS.stellar;
                const decimals = Number(currentTokenData.decimals) || 7;
                const raw = await fetchSacBalanceRaw({
                    rpcUrl: stellarCfg.rpcUrl,
                    networkPassphrase: stellarCfg.networkPassphrase,
                    tokenContractId: currentTokenData.address,
                    ownerPublicKey: userAccount,
                });
                if (publicBalanceTokenRef.current.key !== balanceFetchKey) {
                    return;
                }
                const amtStr = formatSacAmountForDisplay(raw, decimals);
                const tokenName = currentTokenData.name || currentTokenData.symbol || 'Token';
                setBalance(`${amtStr} ${tokenName}`);
                return;
            }

            const tokenContract = new ethers.Contract(
                currentTokenData.address,
                ['function balanceOf(address) view returns (uint256)', 'function decimals() view returns (uint8)', 'function symbol() view returns (string)'],
                contract.provider
            );

            const balance = await tokenContract.balanceOf(userAccount);
            const decimals = await tokenContract.decimals();
            if (publicBalanceTokenRef.current.key !== balanceFetchKey) {
                return;
            }
            const formattedBalance = ethers.utils.formatUnits(balance, decimals);
            
            // Use the proper token name from configuration instead of contract symbol
            // Clean up the formatted balance to remove trailing zeros
            const cleanBalance = parseFloat(formattedBalance);
            
            // Ensure we have a valid number
            if (isNaN(cleanBalance) || !isFinite(cleanBalance)) {
                // Retry if we haven't exceeded max retries
                if (retryCount < MAX_RETRIES) {
                    console.log(`Retrying balance load (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
                    await sleep(RETRY_DELAY * (retryCount + 1)); // Exponential backoff
                    return updateTokenBalance(retryCount + 1);
                }
                if (publicBalanceTokenRef.current.key === balanceFetchKey) {
                    setBalance('Error loading balance');
                }
                return;
            }
            
            const tokenName = currentTokenData.name || currentTokenData.symbol || 'Token';
            const balanceString = `${cleanBalance} ${tokenName}`;
            
            // Only update if we have a valid balance string
            if (publicBalanceTokenRef.current.key !== balanceFetchKey) {
                return;
            }
            if (balanceString && balanceString.trim().length > 0) {
                setBalance(balanceString);
            } else {
                // Retry if we haven't exceeded max retries
                if (retryCount < MAX_RETRIES) {
                    console.log(`Retrying balance load (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
                    await sleep(RETRY_DELAY * (retryCount + 1)); // Exponential backoff
                    return updateTokenBalance(retryCount + 1);
                }
                if (publicBalanceTokenRef.current.key === balanceFetchKey) {
                    setBalance('Error loading balance');
                }
            }
        } catch (error) {
            console.error('Error fetching token balance:', error);
            
            // Detect SSL errors and use longer retry delays
            const isSSL = isSSLError(error);
            const retryDelay = isSSL ? RETRY_DELAY * 5 * (retryCount + 1) : RETRY_DELAY * (retryCount + 1);
            
            // Retry if we haven't exceeded max retries
            if (retryCount < MAX_RETRIES) {
                if (isSSL) {
                    console.warn(`SSL error detected, retrying with longer delay (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
                } else {
                    console.log(`Retrying balance load after error (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
                }
                await sleep(retryDelay); // Exponential backoff, longer for SSL errors
                return updateTokenBalance(retryCount + 1);
            }
            
            // Show more specific error message for SSL errors
            if (balanceFetchKey && publicBalanceTokenRef.current.key !== balanceFetchKey) {
                return;
            }
            if (isSSL) {
                const chainConfig = CHAINS[selectedChainIdForBalanceRef.current];
                const chainName = chainConfig?.name || 'this network';
                console.error(getNetworkErrorMessage(error, chainName));
                setBalance('SSL Error - Network issue');
            } else {
                setBalance('Error loading balance');
            }
        }
    };

    const handlePercentageClick = (percentage) => {
        console.log('Percentage clicked:', percentage);
        
        if (confidentialBalance === null) {
            showNotification('Balance not loaded yet', 'error');
            return;
        }
        
        const currentTokenData = getTokenData();
        const d = normalizeTokenDecimals(currentTokenData?.decimals);
        const userFacingBalance = plainToUserAmountNumber(confidentialBalance, d);
        const decimalsForDisplay = d;
        let amountValue = percentage === 'max' 
            ? userFacingBalance 
            : userFacingBalance * (percentage / 100);
        
        const maxAmount = maxUserFacingAmountFromPlainCap(d);
        
        if (amountValue > maxAmount) {
            amountValue = maxAmount;
            showNotification(`Amount capped to maximum: ${formatTokenAmount(maxAmount, decimalsForDisplay)} ${currentTokenData.symbol}`, 'info');
        }
        
        const formattedAmount = formatTokenAmount(amountValue, decimalsForDisplay);
        setTransferAmount(formattedAmount);
        setTransferAmountError(''); // Clear any previous error since we're setting a valid amount
        console.log('Transfer amount set to:', formattedAmount);
    };

    const handleWithdrawPercentageClick = (percentage) => {
        console.log('Withdraw percentage clicked:', percentage);
        
        if (confidentialBalance === null) {
            showNotification('Balance not loaded yet', 'error');
            return;
        }
        
        const currentTokenData = getTokenData();
        const d = normalizeTokenDecimals(currentTokenData?.decimals);
        const userFacingBalance = plainToUserAmountNumber(confidentialBalance, d);
        const decimalsForDisplay = d;
        let amountValue = percentage === 'max' 
            ? userFacingBalance 
            : userFacingBalance * (percentage / 100);
        
        const maxAmount = maxUserFacingAmountFromPlainCap(d);
        
        if (amountValue > maxAmount) {
            amountValue = maxAmount;
            showNotification(`Amount capped to maximum: ${formatTokenAmount(maxAmount, decimalsForDisplay)} ${currentTokenData.symbol}`, 'info');
        }
        
        const formattedAmount = formatTokenAmount(amountValue, decimalsForDisplay);
        setWithdrawAmount(formattedAmount);
        setWithdrawAmountError(''); // Clear any previous error since we're setting a valid amount
        console.log('Withdraw amount set to:', formattedAmount);
    };

    // Helper function to check if balance is confirmed to be insufficient (0)
    // Returns true only when balance has been successfully loaded and is exactly 0
    const hasInsufficientBalance = () => {
        // If balance is still loading, we don't know yet - return false (don't show "Insufficient balance")
        if (!balance || balance === 'Loading...' || typeof balance !== 'string' || balance.trim().length === 0) {
            return false;
        }
        
        // If there was an error loading balance or token not found, don't show "Insufficient balance"
        // (the balance just hasn't loaded yet, it might not actually be 0)
        if (balance === 'Error loading balance' || balance === 'Token not found') {
            return false;
        }
        
        // Extract numeric value from balance string (e.g., "100.5 USD Coin" -> 100.5, "1 USDC" -> 1)
        const balanceParts = balance.trim().split(/\s+/);
        const balanceValue = parseFloat(balanceParts[0]);
        
        // Only return true if balance is successfully parsed and equals exactly 0
        // Invalid/NaN values should not trigger "Insufficient balance" message
        return (!isNaN(balanceValue) && isFinite(balanceValue) && balanceValue === 0);
    };

    const handleDepositPercentageClick = (percentage) => {
        console.log('Deposit percentage clicked:', percentage);
        console.log('Current balance string:', balance);
        
        if (!userAccount) {
            showNotification('Wallet not connected. Please connect your wallet', 'error');
            return;
        }
        
        if (hasInsufficientBalance()) {
            // Don't show notification - the button will show "Not enough XYZ" instead
            return;
        }
        
        // If balance is still loading, show notification
        if (!balance || balance === 'Loading...') {
            showNotification('Balance is loading. Please wait...', 'info');
            return;
        }
        
        // Extract numeric value from balance string (e.g., "100.5 USD Coin" -> 100.5, "1 USDC" -> 1)
        // Handle different formats: "1 USD Coin", "1.0 USDC", etc.
        const balanceParts = balance.trim().split(/\s+/);
        const balanceValue = parseFloat(balanceParts[0]);
        
        console.log('Parsed balance value:', balanceValue);
        
        if (isNaN(balanceValue) || !isFinite(balanceValue) || balanceValue < 0) {
            console.error('Invalid balance value:', balanceValue, 'from string:', balance);
            showNotification('Invalid balance. Please try again.', 'error');
            return;
        }
        
        // Allow 0 balance (user might want to deposit 0, though unlikely)
        if (balanceValue === 0) {
            setDepositAmount('0');
            return;
        }
        
        const currentTokenData = getTokenData();
        const decimalsForDisplay = normalizeTokenDecimals(currentTokenData?.decimals);
        let amountValue = percentage === 'max' 
            ? balanceValue 
            : balanceValue * (percentage / 100);
        
        // Cap amount to max deposit limit (40M tokens)
        const MAX_DEPOSIT_TOKENS = 40000000;
        
        if (amountValue > MAX_DEPOSIT_TOKENS) {
            amountValue = MAX_DEPOSIT_TOKENS;
            showNotification(`Amount capped to maximum: ${formatTokenAmount(MAX_DEPOSIT_TOKENS, decimalsForDisplay)} ${currentTokenData.symbol}`, 'info');
        }
        
        const formattedAmount = formatTokenAmount(amountValue, decimalsForDisplay);
        setDepositAmount(formattedAmount);
        setDepositAmountError(''); // Clear any previous error since we're setting a valid amount
        console.log('Deposit amount set to:', formattedAmount);
    };

    const getRecipientPublicKey = async (recipientAddress) => {
        try {
            const recipientAccountTuple = await contract.getAccountCore(recipientAddress);
            const hasActiveAccount = recipientAccountTuple[0] && recipientAccountTuple[1]; // exists && finalized
            
            if (!hasActiveAccount) {
                throw new Error('Recipient does not have an active Stabletrust account');
            }
            
            const rawPubkey = recipientAccountTuple[4]; // elgamalPubkey is at index 4
            console.log('Raw recipient public key from contract:', rawPubkey);
            return rawPubkey;
        } catch (error) {
            console.error('Error fetching recipient public key:', error);
            throw error;
        }
    };

    const validateTransferInputs = async () => {
        const trimmedRecipient = recipient.trim();
        if (!transferAmount || !trimmedRecipient) return 'Please enter amount and recipient address';
        if (transferAmountError) return transferAmountError;
        const transferAmountValue = parseFloat(transferAmount);
        if (isNaN(transferAmountValue) || transferAmountValue < 0) return 'Amount cannot be negative';
        
        const currentTokenData = getTokenData();
        const d = normalizeTokenDecimals(currentTokenData?.decimals);
        const strict = parseUserAmountToPlainBnStrict(transferAmount, d);
        if (!strict.ok && strict.reason === 'max') {
            const maxAmount = maxUserFacingAmountFromPlainCap(d);
            return `Amount exceeds maximum (${formatTokenAmount(maxAmount, d)} ${currentTokenData?.symbol || 'tokens'})`;
        }
        if (!strict.ok && strict.reason === 'parse') {
            return 'Invalid amount';
        }
        
        // Fetch fresh account data from contract to ensure we have latest state
        let currentAccountData = accountData;
        try {
            const accountDataTuple = await contract.getAccountCore(userAccount);
            currentAccountData = {
                exists: accountDataTuple[0],
                finalized: accountDataTuple[1],
                pendingAction: accountDataTuple[2]
            };
        } catch (error) {
            console.error('Error fetching fresh account data for transfer validation:', error);
        }
        
        // Check for pending action using fresh data
        if (currentAccountData && currentAccountData.pendingAction) {
            const pendingMessage = await getPendingActionMessage();
            return pendingMessage;
        }
        
        // Check if user can submit operations using fresh account data
        const canSubmit = currentAccountData && 
                          currentAccountData.exists && 
                          currentAccountData.finalized && 
                          !currentAccountData.pendingAction;
        if (!canSubmit) return 'Account not ready. Please ensure your account is finalized and no requests are in progress.';
        if (!keypair) return 'Keypair not ready. Please wait for key generation to complete.';
        if (confidentialBalance === null) return 'Balance not loaded. Please wait and try again.';
        if (!isValidRecipientAddress(trimmedRecipient)) return 'Please enter a valid recipient address';

        const userFacingBalance = plainToUserAmountNumber(confidentialBalance, d);
        if (userFacingBalance < transferAmountValue) return `Insufficient balance. Available: ${formatTokenAmount(userFacingBalance, d)}, Requested: ${formatTokenAmount(transferAmountValue, d)}`;
        return null;
    };

    const fetchCurrentBalanceCiphertext = async (tokenAddr) => {
        const [availC1, availC2] = await contract.getAvailable(userAccount, tokenAddr);
        const availC1Bytes = hexToBytes(availC1.slice(2));
        const availC2Bytes = hexToBytes(availC2.slice(2));
        const combinedCiphertextBytes = new Uint8Array(64);
        combinedCiphertextBytes.set(availC1Bytes, 0);
        combinedCiphertextBytes.set(availC2Bytes, 32);
        return btoa(String.fromCharCode(...combinedCiphertextBytes));
    };

    const encodeRecipientPubkey = (recipientPubkey) => {
        if (!recipientPubkey) throw new Error('Invalid recipient public key: null or undefined');
        
        console.log('Encoding recipient public key:', {
            type: typeof recipientPubkey,
            length: recipientPubkey?.length,
            preview: typeof recipientPubkey === 'string' ? recipientPubkey.slice(0, 50) + '...' : recipientPubkey
        });
        
        if (typeof recipientPubkey === 'string') {
            if (recipientPubkey.startsWith('0x')) {
                const hexString = recipientPubkey.slice(2);
                console.log('Converting hex string to bytes, hex length:', hexString.length);
                
                // Convert hex to string to check if it's JSON
                const hexBytes = new Uint8Array(hexString.match(/.{2}/g).map(byte => parseInt(byte, 16)));
                const decodedString = String.fromCharCode(...hexBytes);
                
                console.log('Decoded hex to string:', decodedString);
                
                // Check if it's a JSON object containing a key
                try {
                    const jsonObj = JSON.parse(decodedString);
                    if (jsonObj && typeof jsonObj === 'object' && jsonObj.key) {
                        console.log('Found JSON with key, extracting base64 key:', jsonObj.key);
                        return jsonObj.key; // Return the base64 key directly
                    }
                } catch (e) {
                    console.log('Not JSON, treating as raw bytes');
                }
                
                // If not JSON, treat as raw bytes
                const encoded = btoa(String.fromCharCode(...hexBytes));
                console.log('Encoded raw bytes, length:', encoded.length);
                return encoded;
            }
            
            // Check if it's already base64
            try {
                atob(recipientPubkey);
                console.log('Public key appears to be base64 already');
                return recipientPubkey;
            } catch (_) {
                console.log('Not base64, trying other formats...');
                let keyBytes;
                if (recipientPubkey.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(recipientPubkey)) {
                    keyBytes = new Uint8Array(recipientPubkey.match(/.{2}/g).map(byte => parseInt(byte, 16)));
                } else {
                    keyBytes = new Uint8Array(recipientPubkey.split(',').map(Number));
                }
                const encoded = btoa(String.fromCharCode(...keyBytes));
                console.log('Encoded from other format, length:', encoded.length);
                return encoded;
            }
        }
        
        if (Array.isArray(recipientPubkey)) {
            const encoded = btoa(String.fromCharCode(...new Uint8Array(recipientPubkey)));
            console.log('Encoded from array, length:', encoded.length);
            return encoded;
        }
        
        throw new Error('Invalid recipient public key: unsupported format');
    };

    // Helper function to get gas fee overrides with buffer to prevent "maxFeePerGas less than block base fee" errors
    const getGasFeeOverrides = async () => {
        try {
            const provider = contract.signer?.provider;
            if (provider) {
                const feeData = await provider.getFeeData();
                if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
                    // Add 20% buffer to maxFeePerGas to account for base fee increases
                    const bufferedMaxFee = feeData.maxFeePerGas.mul(120).div(100);
                    return {
                        maxFeePerGas: bufferedMaxFee,
                        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
                    };
                }
            }
        } catch (feeError) {
            console.warn('Failed to get fee data, proceeding without gas overrides:', feeError);
        }
        return {};
    };

    const generateTransferProof = (currentBalanceCiphertext, privateKey, encodedRecipientPubkey) => {
        const currentTokenData = getTokenData();
        const d = normalizeTokenDecimals(currentTokenData?.decimals);
        const transferAmountScaled = parseUserAmountToPlainInt(transferAmount, d);
        
        const transferInput = {
            current_balance_ciphertext: currentBalanceCiphertext,
            current_balance: confidentialBalance,
            transfer_amount: transferAmountScaled,
            source_keypair: privateKey,
            destination_pubkey: encodedRecipientPubkey
        };
        
        // Debug logging to verify amounts
        console.log('Transfer Proof Input:', {
            user_input_amount: transferAmount,
            contract_scale_factor: 10 ** d,
            scaled_transfer_amount: transferAmountScaled,
            current_balance_raw: confidentialBalance,
            current_balance_formatted: formatPlainForDisplay(confidentialBalance, d)
        });
        
        const proofResult = generate_transfer_proof(JSON.stringify(transferInput));
        const proof = JSON.parse(proofResult);
        if (!proof.success) throw new Error(proof.error || 'Proof generation failed');
        
        // Extract randomness from proof data if available
        const randomness = {
            sender: proof.data?.sender_randomness || null,
            receiver: proof.data?.receiver_randomness || null
        };
        
        // Log randomness values for verification
        console.log('[Transfer Proof] Sender randomness present:', !!randomness.sender);
        console.log('[Transfer Proof] Receiver randomness present:', !!randomness.receiver);
        if (randomness.sender) {
            console.log('[Transfer Proof] Sender randomness length:', randomness.sender.length, 'chars (base64)');
            console.log('[Transfer Proof] NOTE: Sender randomness is for transfer_amount_source_ciphertext_lo (transfer amount encrypted for sender)');
        }
        if (randomness.receiver) {
            console.log('[Transfer Proof] Receiver randomness length:', randomness.receiver.length, 'chars (base64)');
            console.log('[Transfer Proof] NOTE: Receiver randomness is for transfer_amount_destination_ciphertext_lo (transfer amount encrypted for receiver)');
        }
        
        return { proof, randomness };
    };

    const submitTransfer = async (recipient, proofData) => {
        const currentTokenData = getTokenData();
        const transferProofData = encodeProofData(proofData.proof);
        let overrides = {};
        
        // For Tempo chain, fees are paid with PathUSD (ERC20 token)
        // We need to approve the fee amount before submitting the transfer
        const isTempo = selectedChainId === 'tempo';
        
        if (isTempo) {
            try {
                // Get fee amount from contract
                const feeAmt = await contract.feeAmount();
                
                // Get PathUSD token address (first fee token for Tempo)
                const tempoChain = CHAINS.tempo;
                if (!tempoChain || !tempoChain.feeTokens || tempoChain.feeTokens.length === 0) {
                    throw new Error('Tempo fee tokens not configured');
                }
                
                // PathUSD is always the first fee token
                const pathUSDToken = tempoChain.feeTokens[0];
                const pathUSDAddress = pathUSDToken.address;
                
                // Create ERC20 token contract instance for PathUSD
                const pathUSDContract = new ethers.Contract(
                    pathUSDAddress,
                    [
                        'function allowance(address owner, address spender) view returns (uint256)',
                        'function approve(address spender, uint256 amount) returns (bool)',
                        'function balanceOf(address account) view returns (uint256)'
                    ],
                    contract.signer
                );
                
                // Check current allowance
                const currentAllowance = await withRateLimitRetry(
                    () => pathUSDContract.allowance(userAccount, contract.address),
                    { retries: 3, baseDelayMs: 1200 }
                );
                
                // If allowance is insufficient, approve the contract to spend fee tokens
                if (currentAllowance.lt(feeAmt)) {
                    // Add gas fee overrides to prevent "maxFeePerGas less than block base fee" errors
                    const gasOverrides = await getGasFeeOverrides();
                    const approveTx = await withRateLimitRetry(
                        () => pathUSDContract.approve(contract.address, feeAmt, gasOverrides),
                        { retries: 3, baseDelayMs: 1500 }
                    );
                    await approveTx.wait();
                    
                    // Small delay to avoid immediate follow-up hitting RPC rate limits
                    await sleep(1500);
                }
            } catch (error) {
                console.error('Error approving PathUSD fee token for Tempo:', error);
                throw new Error(`Failed to approve fee token: ${error.message}`);
            }
        } else {
            // For other chains, handle native currency fees (if any)
            try {
                const feeTokenAddr = await contract.feeToken();
                if (feeTokenAddr === ethers.constants.AddressZero) {
                    const feeAmt = await contract.feeAmount();
                    overrides = { value: feeAmt };
                }
            } catch (_) {}
        }
        
        // Add gas fee overrides to prevent "maxFeePerGas less than block base fee" errors
        const gasOverrides = await getGasFeeOverrides();
        const finalOverrides = { ...overrides, ...gasOverrides };

        // For Tempo, upload the ABI-encoded proof bytes to IPFS and submit a CID pointer
        let transferZkpArg = transferProofData;
        if (isTempo) {
            try {
                const proofBytes = ethers.utils.arrayify(transferProofData);
                const cid = await uploadBytesToIpfs(proofBytes, 'transfer-proof.bin');
                console.log('[Tempo] Transfer proof bytes uploaded to IPFS with CID:', cid);
                // Backend resolveProofBytesIfIPFS expects pointer string; use ipfs://<cid> so ipfsURLFromPointer recognizes it
                const pointer = `ipfs://${cid}`;
                transferZkpArg = ethers.utils.toUtf8Bytes(pointer);
            } catch (ipfsError) {
                console.error('Failed to upload transfer proof to IPFS:', ipfsError);
                throw new Error('Failed to upload transfer proof to IPFS. Please try again.');
            }
        }

        await refreshGasBreakdownForExactTx(
            'transfer',
            'transferConfidential',
            [recipient, currentTokenData.address, transferZkpArg, false],
            { manualGasLimit: 800000, txOverrides: finalOverrides }
        );
        
        // Execute transfer with gas estimation fallback
        // Note: The utility will add its own gas overrides, so we merge them here
        const tx = await executeContractTxWithGasFallback(
            contract,
            'transferConfidential',
            [recipient, currentTokenData.address, transferZkpArg, false, finalOverrides],
            {
                manualGasLimit: 800000, // Transfer operations need more gas due to proof verification
                rateLimitRetry: withRateLimitRetry,
                retryOptions: { retries: 3, baseDelayMs: 1500 },
                stellarContext: getStellarTxContext(),
            }
        );
        const receipt = await tx.wait();
        let requestId = getRequestIdFromReceipt(receipt, contract, ['TransferRequested','TransferProcessed']);
        console.log('Extracted request ID from transfer event:', requestId);
        
        // Fallback: If we can't extract request ID from event, use transaction hash
        // This ensures the transfer is still recorded in Firebase and history
        if (!requestId) {
            console.warn('No request ID found in transfer transaction receipt events. Using transaction hash as fallback.');
            requestId = tx.hash;
        }
        
        // Always save the transfer to Firebase, even if we had to use the transaction hash as fallback
        // Pass the transfer amount and randomness to addPendingRequest for IBE encryption
        await addPendingRequest(requestId, 2, {
            token: currentTokenData.symbol,
            tokenAddress: currentTokenData.address,
            recipient,
            fallbackRequestId: requestId === tx.hash, // Flag to indicate if we used fallback
        }, tx.hash, transferAmount, proofData.randomness);
        return tx.hash;
    };

    const handleTransfer = async () => {
        // Check if keys need to be derived first, or if account creation failed
        if (!keysDerived || accountCreationFailed) {
            triggerKeyDerivation();
            return;
        }

        const validationError = await validateTransferInputs();
        if (validationError) { showNotification(validationError, 'error'); return; }

        const normalizedRecipient = recipient.trim();
        if (!normalizedRecipient || !isValidRecipientAddress(normalizedRecipient)) {
            setRecipientError('Invalid address');
            showNotification('Please enter a valid recipient address', 'error');
            return;
        }

        setIsProcessing(true);

        try {
            const currentTokenData = getTokenData();
            const currentBalanceCiphertext = await fetchCurrentBalanceCiphertext(currentTokenData.address);

            // Removed "Fetching recipient public key..." notification - happens quickly
            const recipientPubkey = await getRecipientPublicKey(normalizedRecipient);

            if (keyLoading) throw new Error('Keypair is still being derived. Please wait...');
            if (!keypair || !keypair.private_key) throw new Error('Keypair not available. Please wait for key derivation to complete.');
            const secureKeypair = createSecureKeypair(keypair);
            const privateKey = secureKeypair.getPrivateKey();
            if (!privateKey) throw new Error('Failed to retrieve private key from secure wrapper');

            const encodedRecipientPubkey = encodeRecipientPubkey(recipientPubkey);
            const proofData = generateTransferProof(currentBalanceCiphertext, privateKey, encodedRecipientPubkey);
            secureKeypair.clear();

            const txHash = await submitTransfer(normalizedRecipient, proofData);
            
            // Store values for notification before clearing
            const submittedAmount = transferAmount;
            const submittedRecipient = normalizedRecipient;
            
            // Clear input fields immediately after successful transaction
            setTransferAmount('');
            setRecipient('');
            setTransferAmountError('');
            setRecipientError(null);
            setRecipientAccountStatus(null);
            
            // Create notification with clickable transaction hash
            const tokenDataForNotification = getTokenData();
            const chainKey = tokenDataForNotification?.chainId;
            const chainConfig = chainKey ? CHAINS[chainKey] : null;
            const explorerUrl = getExplorerTxUrlFromTokenChain(chainKey, chainConfig, txHash);
            const shortTxHash = `${txHash.slice(0, 6)}...${txHash.slice(-4)}`;
            
            showNotification(
                <>
                    Transfer submitted: {submittedAmount} {currentTokenData.symbol} → {submittedRecipient.slice(0, 6)}...{submittedRecipient.slice(-4)} (tx:{' '}
                    {explorerUrl ? (
                        <a 
                            href={explorerUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {shortTxHash}
                        </a>
                    ) : (
                        shortTxHash
                    )}
                    ). It might take a few moments before the change takes effect.
                </>,
                'success'
            );
            
            // Refresh account data after successful transfer submission
            onOperationComplete();
            // Trigger checkPendingAction to update pendingActionKind quickly (show "Transfer in progress...")
            checkPendingAction();
            // Don't await loadConfidentialBalance here - the balance won't change until the operation is processed
            // The event listener will handle loading the new balance
            loadPendingBalance(0, true);
            updateTokenBalance();
            
            // For Privy users, add additional delayed refreshes since event listeners may not work reliably
            refreshBalancesForPrivy('transfer');
        } catch (error) {
            console.error('Error transferring tokens:', error);
            const cleanError = getCleanErrorMessage(error);
            showNotification(`Transfer failed: ${cleanError}`, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeposit = async () => {
        // Check if keys need to be derived first, or if account creation failed
        if (!keysDerived || accountCreationFailed) {
            triggerKeyDerivation();
            return;
        }

        if (!depositAmount) {
            showNotification('Please enter amount to deposit', 'error');
            return;
        }
        
        if (depositAmountError) {
            showNotification(depositAmountError, 'error');
            return;
        }
        
        const depositAmountValue = parseFloat(depositAmount);
        if (isNaN(depositAmountValue) || depositAmountValue < 0) {
            showNotification('Amount cannot be negative', 'error');
            return;
        }

        // Check if amount exceeds max deposit limit (40M tokens)
        const currentTokenData = getTokenData();
        const MAX_DEPOSIT_TOKENS = 40000000;
        
        if (depositAmountValue > MAX_DEPOSIT_TOKENS) {
            showNotification(`Amount exceeds maximum (${MAX_DEPOSIT_TOKENS} ${currentTokenData?.symbol || 'tokens'})`, 'error');
            return;
        }

        // Fetch fresh account data from the contract FIRST
        // to ensure we have the latest state (props may not have updated yet after previous operation)
        let currentAccountData = accountData;
        try {
            const accountDataTuple = await contract.getAccountCore(userAccount);
            currentAccountData = {
                exists: accountDataTuple[0],
                finalized: accountDataTuple[1],
                pendingAction: accountDataTuple[2],
                txId: accountDataTuple[3]?.toString() || '0',
                elgamalPubkey: accountDataTuple[4],
                pendingCreditCounter: accountDataTuple[5]?.toString() || '0',
                minimumPendingCreditCounter: accountDataTuple[6]?.toString() || '0',
                availableShare: '0x',
                pendingShare: '0x',
            };
        } catch (error) {
            console.error('Error fetching fresh account data:', error);
        }

        // Check if user has a pending action using fresh data
        if (currentAccountData && currentAccountData.pendingAction) {
            const pendingMessage = await getPendingActionMessage();
            showNotification(pendingMessage, 'error');
            return;
        }

        // Check if user can submit operations using fresh account data
        const canSubmit = currentAccountData && 
                          currentAccountData.exists && 
                          currentAccountData.finalized && 
                          !currentAccountData.pendingAction;
        
        if (!canSubmit) {
            showNotification('Account not ready. Please ensure your account is finalized and no requests are in progress.', 'error');
            return;
        }

        setIsProcessing(true);

        try {
            const currentTokenData = getTokenData();
            const stellarCtx = getStellarTxContext();
            const isStellarDeposit =
                (selectedChainId === 'stellar' || contract?.isStellarSoroban) && !!stellarCtx;

            let tx;
            /** @type {ethers.BigNumber|string} */
            let amountForFirebase;

            if (isStellarDeposit) {
                // Soroban `deposit(owner, token, plain_amount)` must match EVM `plainAmount`: confidential units are
                // `floor(userAmount * 10^decimals)` (same scale as withdraw/transfer/decrypt).
                const d = normalizeTokenDecimals(currentTokenData.decimals);
                const plainAmount = BigInt(parseUserAmountToPlainBn(depositAmount, d).toString());
                if (plainAmount <= 0n) {
                    showNotification('Amount must be positive', 'error');
                    setIsProcessing(false);
                    return;
                }
                amountForFirebase = plainAmount.toString();

                await refreshGasBreakdownForExactTx(
                    'deposit',
                    'deposit',
                    [currentTokenData.address, plainAmount],
                    { manualGasLimit: 500000 }
                );

                tx = await executeContractTxWithGasFallback(
                    contract,
                    'deposit',
                    [currentTokenData.address, plainAmount],
                    {
                        manualGasLimit: 500000,
                        rateLimitRetry: withRateLimitRetry,
                        retryOptions: { retries: 3, baseDelayMs: 1500 },
                        stellarContext: stellarCtx,
                    }
                );
            } else {
                // EVM: internal confidential plain uses 10^token_decimals; ERC-20 approval uses the same token decimals.
                const amountForContract = parseUserAmountToPlainBn(depositAmount, currentTokenData.decimals);
                amountForFirebase = amountForContract;
                const amountForApproval = ethers.utils.parseUnits(depositAmount, currentTokenData.decimals);

                const tokenContract = new ethers.Contract(
                    currentTokenData.address,
                    [
                        'function allowance(address owner, address spender) view returns (uint256)',
                        'function approve(address spender, uint256 amount) returns (bool)',
                        'function balanceOf(address account) view returns (uint256)',
                    ],
                    contract.signer
                );

                const currentAllowance = await withRateLimitRetry(
                    () => tokenContract.allowance(userAccount, contract.address),
                    { retries: 3, baseDelayMs: 1200 }
                );

                if (currentAllowance.lt(amountForApproval)) {
                    const gasOverrides = await getGasFeeOverrides();
                    const approveTx = await withRateLimitRetry(
                        () => tokenContract.approve(contract.address, amountForApproval, gasOverrides),
                        { retries: 3, baseDelayMs: 1500 }
                    );
                    await approveTx.wait();
                    await sleep(1500);
                }

                await refreshGasBreakdownForExactTx(
                    'deposit',
                    'deposit',
                    [currentTokenData.address, amountForContract],
                    { manualGasLimit: 500000 }
                );

                tx = await executeContractTxWithGasFallback(
                    contract,
                    'deposit',
                    [currentTokenData.address, amountForContract],
                    {
                        manualGasLimit: 500000,
                        rateLimitRetry: withRateLimitRetry,
                        retryOptions: { retries: 3, baseDelayMs: 1500 },
                        stellarContext: getStellarTxContext(),
                    }
                );
            }
            
            // Log full transaction hash
            console.log('Deposit transaction hash:', tx.hash);
            
            // Extract request ID from transaction receipt
            const receipt = await tx.wait();
            console.log('Transaction receipt:', receipt);
            console.log('Deposit transaction hash (from receipt):', receipt.transactionHash);
            console.log('Receipt logs:', receipt.logs);
            console.log('Contract address:', contract.address);
            
            // Store values for notification before clearing
            const submittedAmount = depositAmount;
            
            // Clear input fields immediately after successful transaction
            setDepositAmount('');
            setDepositAmountError('');
            
            let requestId = getRequestIdFromReceipt(receipt, contract, [
                'DepositRequested'
            ]);
            console.log('Extracted request ID from event:', requestId);
            
            // Fallback: If we can't extract request ID from event, use transaction hash
            // This ensures the deposit is still recorded in Firebase and history
            if (!requestId) {
                console.warn('No request ID found in transaction receipt events. Using transaction hash as fallback.');
                requestId = tx.hash;
            }
            
            // Always save the deposit to Firebase, even if we had to use the transaction hash as fallback
            const amountStored =
                typeof amountForFirebase === 'string'
                    ? amountForFirebase
                    : amountForFirebase.toString();
            await addPendingRequest(requestId, 1, {
                // 1 = Deposit operation kind — plain amount in confidential integer units (floor(user * 10^decimals))
                token: currentTokenData.symbol,
                tokenAddress: currentTokenData.address,
                amount: amountStored,
                amountInWei: true,
                fallbackRequestId: requestId === tx.hash,
            }, tx.hash);
            // Immediately set pending deposit state to show "Deposit in progress"
            // Only set if this deposit is for the currently selected token and account exists
            const currentTokenDataForCheck = getTokenData();
            const checkAddress = currentTokenDataForCheck?.address;
            const depositAddress = currentTokenData?.address;
            if (checkAddress && depositAddress && 
                typeof checkAddress === 'string' && typeof depositAddress === 'string' &&
                checkAddress.toLowerCase() === depositAddress.toLowerCase() && accountExists) {
                setHasPendingDeposit(true);
                // Trigger checkPendingAction to update pendingActionKind quickly
                checkPendingAction();
            }
            
            // Create notification with clickable transaction hash
            const tokenDataForNotification = getTokenData();
            const chainKey = tokenDataForNotification?.chainId;
            const chainConfig = chainKey ? CHAINS[chainKey] : null;
            const explorerUrl = getExplorerTxUrlFromTokenChain(chainKey, chainConfig, tx.hash);
            const shortTxHash = `${tx.hash.slice(0, 6)}...${tx.hash.slice(-4)}`;
            
            showNotification(
                <>
                    Deposit submitted: {submittedAmount} {currentTokenData.symbol} (tx:{' '}
                    {explorerUrl ? (
                        <a 
                            href={explorerUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {shortTxHash}
                        </a>
                    ) : (
                        shortTxHash
                    )}
                    ). It might take a few moments before the change takes effect. Open the{' '}
                    <button
                        type="button"
                        style={{
                            color: 'inherit',
                            textDecoration: 'underline',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            cursor: 'pointer',
                            verticalAlign: 'baseline'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab('transfer');
                        }}
                    >
                        Transfer
                    </button>
                    {' '}tab to see your new balance.
                </>,
                'success'
            );
            
            // Refresh account data after successful deposit submission
            onOperationComplete();
            // Don't await loadConfidentialBalance here - the balance won't change until the operation is processed
            // The event listener will handle loading the new balance
            loadPendingBalance(0, true);
            updateTokenBalance();
            
            // For Privy users, add additional delayed refreshes since event listeners may not work reliably
            refreshBalancesForPrivy('deposit');
            
        } catch (error) {
            console.error('Error depositing tokens:', error);
            const cleanError = getCleanErrorMessage(error);
            const isRpcConfigError = typeof cleanError === 'string' &&
                cleanError.toLowerCase().includes('set the rpc url to https://sepolia-rollup.arbitrum.io/rpc');
            // Make RPC-configuration guidance errors persistent so users have enough time to read/fix them
            showNotification(`Deposit failed: ${cleanError}`, 'error', isRpcConfigError);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleApplyPending = async () => {
        // Check if keys need to be derived first, or if account creation failed
        if (!keysDerived || accountCreationFailed) {
            triggerKeyDerivation();
            return;
        }

        // Fetch fresh account data from contract to ensure we have latest state
        let currentAccountData = accountData;
        try {
            const accountDataTuple = await contract.getAccountCore(userAccount);
            currentAccountData = {
                exists: accountDataTuple[0],
                finalized: accountDataTuple[1],
                pendingAction: accountDataTuple[2]
            };
        } catch (error) {
            console.error('Error fetching fresh account data for secure claim:', error);
        }

        // Check if user has a pending action using fresh data
        if (currentAccountData && currentAccountData.pendingAction) {
            const pendingMessage = await getPendingActionMessage();
            showNotification(pendingMessage, 'error');
            return;
        }

        // Check if user can submit secure claim operations using fresh data
        const canSubmit = currentAccountData && currentAccountData.exists && !currentAccountData.pendingAction;
        if (!canSubmit) {
            showNotification('Account not ready for secure claim. Please ensure your account exists and no requests are in progress.', 'error');
            return;
        }

        // Check if there's any pending balance to claim
        console.log('handleApplyPending - pendingBalance check:', {
            pendingBalance,
            type: typeof pendingBalance,
            isNull: pendingBalance === null,
            isZero: pendingBalance === 0,
            tokenData: getTokenData()
        });
        
            // If pendingBalance is null or 0, try to reload it first
            if (pendingBalance === null || pendingBalance === 0) {
                console.log('No pending balance found, attempting to reload...');
            await loadPendingBalance(0, true); // Force load
            
            // Check again after reload
            if (pendingBalance === null || pendingBalance === 0) {
                showNotification('No pending balance to claim.', 'info');
                return;
            }
        }

        setIsApplyingPending(true);
        
        // Close the notification when user clicks secure claim
        setShowPendingNotification(false);
        setPendingNotificationDismissed(true);

        try {
            // Submit secure claim transaction
            // Execute with gas estimation fallback
            const tx = await executeContractTxWithGasFallback(
                contract,
                'applyPending',
                [],
                {
                    manualGasLimit: 500000, // Apply pending operations need moderate gas
                    rateLimitRetry: withRateLimitRetry,
                    retryOptions: { retries: 3, baseDelayMs: 1500 },
                    stellarContext: getStellarTxContext(),
                }
            );
            const receipt = await tx.wait();
            
            // Extract request ID from transaction receipt
            let requestId = getRequestIdFromReceipt(receipt, contract, [
                'ApplyPendingRequested'
            ]);
            console.log('Extracted request ID from apply pending event:', requestId);
            
            // Fallback: If we can't extract request ID from event, use transaction hash
            // This ensures the secure claim is still recorded in Firebase and history
            if (!requestId) {
                console.warn('No request ID found in apply pending transaction receipt events. Using transaction hash as fallback.');
                requestId = tx.hash;
            }
            
            // Always save the secure claim to Firebase, even if we had to use the transaction hash as fallback
            // Store amount in wei format (raw units) for consistency
            // pendingBalance is already in wei format (raw units)
            await addPendingRequest(requestId, 3, { // 3 = SecureClaim operation kind
                token: tokenData.symbol,
                tokenAddress: tokenData.address,
                pendingAmount: pendingBalance.toString(), // Already in wei, ensure it's a string
                amountInWei: true, // Flag to indicate amount is in wei
                fallbackRequestId: requestId === tx.hash // Flag to indicate if we used fallback
            }, tx.hash);
            
            // Hide the header "Secure Claim" button immediately after the request is accepted,
            // so users don't get prompted to sign again while the claim is being processed.
            if (onSecureClaimInFlightChange) {
                onSecureClaimInFlightChange(true, getSecureClaimKey());
            }
            
            const currentTokenData = getTokenData();
            
            // Create notification with clickable transaction hash
            const tokenDataForNotification = getTokenData();
            const chainKey = tokenDataForNotification?.chainId;
            const chainConfig = chainKey ? CHAINS[chainKey] : null;
            const explorerUrl = getExplorerTxUrlFromTokenChain(chainKey, chainConfig, tx.hash);
            const shortTxHash = `${tx.hash.slice(0, 6)}...${tx.hash.slice(-4)}`;
            const claimDecimals = normalizeTokenDecimals(currentTokenData?.decimals);
            const claimAmount = formatPlainForDisplay(pendingBalance, claimDecimals);
            
            showNotification(
                <>
                    Secure claim initiated for {claimAmount} C{currentTokenData.symbol} (tx:{' '}
                    {explorerUrl ? (
                        <a 
                            href={explorerUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {shortTxHash}
                        </a>
                    ) : (
                        shortTxHash
                    )}
                    ). It might take a few moments before the change takes effect.
                </>,
                'success'
            );
            
            // Refresh account data after successful secure claim submission
            onOperationComplete();
            // Trigger checkPendingAction to update pendingActionKind quickly (show "Secure claim in progress...")
            checkPendingAction();
            // Don't await loadConfidentialBalance here - the balance won't change until the operation is processed
            // The event listener will handle loading the new balance
            setTimeout(async () => {
                loadPendingBalance();
                updateTokenBalance();
                
                // For Privy users, add additional delayed refreshes since event listeners may not work reliably
                refreshBalancesForPrivy('applyPending');
            }, 3000);
            
        } catch (error) {
            console.error('Error processing secure claim:', error);
            const cleanError = getCleanErrorMessage(error);
            showNotification(`Secure claim failed: ${cleanError}`, 'error');
        } finally {
            setIsApplyingPending(false);
        }
    };

    // Keep the secure claim handler reference fresh for the header button
    useEffect(() => {
        secureClaimHandlerRef.current = handleApplyPending;
    });

    // Helpers
    const parseRequestLog = (iface, log) => {
        try {
            return iface.parseLog({ topics: log.topics, data: log.data });
        } catch {
            return undefined;
        }
    };

    const extractRequestId = (parsed) => {
        if (!parsed || !parsed.args) return undefined;
        if (parsed.name === 'DepositRequested' && parsed.args.txId) return parsed.args.txId.toString();
        if (parsed.args.requestId) return parsed.args.requestId.toString();
        if (parsed.args.txId) return parsed.args.txId.toString();
        return undefined;
    };

    const getRequestIdFromReceipt = (receipt, contractInstance, eventNames) => {
        try {
            if (!contractInstance?.interface) {
                console.warn('getRequestIdFromReceipt: no ethers ABI (e.g. Soroban facade); skipping log parse');
                return null;
            }
            const iface = contractInstance.interface;
            const contractAddr = contractInstance?.address && typeof contractInstance.address === 'string' 
                ? contractInstance.address.toLowerCase() 
                : null;
            console.log('Looking for events:', eventNames, 'in contract:', contractAddr);
            console.log('Total logs in receipt:', receipt.logs?.length || 0);
            
            if (!contractAddr) {
                console.warn('Contract address is not available, cannot match events');
                return null;
            }
            
            for (const log of receipt.logs) {
                const logAddr = log.address && typeof log.address === 'string' 
                    ? log.address.toLowerCase() 
                    : null;
                console.log('Checking log from address:', logAddr, 'matches contract:', logAddr === contractAddr);
                
                if (logAddr !== contractAddr) continue;
                
                const parsed = parseRequestLog(iface, log);
                console.log('Parsed log:', parsed ? { name: parsed.name, args: parsed.args } : 'failed to parse');
                
                if (!parsed || !eventNames.includes(parsed.name)) continue;
                
                const id = extractRequestId(parsed);
                console.log('Extracted ID from parsed log:', id);
                if (id !== undefined) return id;
            }
            
            console.warn('No matching event found in receipt logs. Searched for:', eventNames);
        } catch (e) {
            console.error('Failed to parse receipt for requestId:', e);
            console.error('Error details:', e.message, e.stack);
        }
        return undefined;
    };

    const validateWithdrawInputs = async () => {
        if (!withdrawAmount) return 'Please enter amount to withdraw';
        if (withdrawAmountError) return withdrawAmountError;
        const withdrawAmountValue = parseFloat(withdrawAmount);
        if (isNaN(withdrawAmountValue) || withdrawAmountValue < 0) return 'Amount cannot be negative';
        
        const currentTokenData = getTokenData();
        const d = normalizeTokenDecimals(currentTokenData?.decimals);
        const strict = parseUserAmountToPlainBnStrict(withdrawAmount, d);
        if (!strict.ok && strict.reason === 'max') {
            const maxAmount = maxUserFacingAmountFromPlainCap(d);
            return `Amount exceeds maximum (${formatTokenAmount(maxAmount, d)} ${currentTokenData?.symbol || 'tokens'})`;
        }
        if (!strict.ok && strict.reason === 'parse') {
            return 'Invalid amount';
        }
        
        // Fetch fresh account data from contract to ensure we have latest state
        let currentAccountData = accountData;
        try {
            const accountDataTuple = await contract.getAccountCore(userAccount);
            currentAccountData = {
                exists: accountDataTuple[0],
                finalized: accountDataTuple[1],
                pendingAction: accountDataTuple[2]
            };
        } catch (error) {
            console.error('Error fetching fresh account data for withdraw validation:', error);
        }
        
        // Check for pending action using fresh data
        if (currentAccountData && currentAccountData.pendingAction) {
            const pendingMessage = await getPendingActionMessage();
            return pendingMessage;
        }
        
        // Check if user can submit operations using fresh account data
        const canSubmit = currentAccountData && 
                          currentAccountData.exists && 
                          currentAccountData.finalized && 
                          !currentAccountData.pendingAction;
        if (!canSubmit) return 'Account not ready. Please ensure your account is finalized and no requests are in progress.';
        if (!keypair) return 'Keypair not ready. Please wait for key generation to complete.';
        if (confidentialBalance === null) return 'Balance not loaded. Please wait and try again.';

        const userFacingBalance = plainToUserAmountNumber(confidentialBalance, d);
        if (userFacingBalance < withdrawAmountValue) return `Insufficient balance. Available: ${formatTokenAmount(userFacingBalance, d)}, Requested: ${formatTokenAmount(withdrawAmountValue, d)}`;
        return null;
    };

    const handleWithdraw = async () => {
        // Check if keys need to be derived first, or if account creation failed
        if (!keysDerived || accountCreationFailed) {
            triggerKeyDerivation();
            return;
        }

        const withdrawValidation = await validateWithdrawInputs();
        if (withdrawValidation) { showNotification(withdrawValidation, 'error'); return; }

        setIsProcessing(true);

        try {
            const currentTokenData = getTokenData();
            console.log('Withdrawing from token:', selectedTokenId, 'at address:', currentTokenData.address);
            
            // Get current balance ciphertext from contract
            const [c1, c2] = await contract.getAvailable(userAccount, currentTokenData.address);
            
            // Convert hex ciphertext to base64 format for WASM
            const c1Bytes = hexToBytes(c1.slice(2));
            const c2Bytes = hexToBytes(c2.slice(2));
            const combinedCiphertextBytes = new Uint8Array(64);
            combinedCiphertextBytes.set(c1Bytes, 0);
            combinedCiphertextBytes.set(c2Bytes, 32);
            const currentBalanceCiphertext = btoa(String.fromCharCode(...combinedCiphertextBytes));

            if (typeof contract.getAccountCore !== 'function') {
                throw new Error('Contract is missing getAccountCore; cannot determine withdraw proof nonce');
            }
            const accountCoreTupleForWithdraw = await contract.getAccountCore(userAccount);
            const withdrawProofNonce = nextWithdrawProofNonceFromAccountCore(accountCoreTupleForWithdraw);
            
            // Check if keypair is available
            if (keyLoading) {
                throw new Error('Keypair is still being derived. Please wait...');
            }
            
            if (!keypair || !keypair.private_key) {
                throw new Error('Keypair not available. Please wait for key derivation to complete.');
            }
            
            // Generate withdraw proof using user-provided keypair
            const secureKeypair = createSecureKeypair(keypair);
            
            // Get the private key before clearing
            const privateKey = secureKeypair.getPrivateKey();
            if (!privateKey) {
                throw new Error('Failed to retrieve private key from secure wrapper');
            }
            
            const wd = normalizeTokenDecimals(currentTokenData.decimals);
            const withdrawAmountScaled = parseUserAmountToPlainInt(withdrawAmount, wd);
            
            const withdrawInput = {
                current_balance_ciphertext: currentBalanceCiphertext,
                current_balance: Math.floor(confidentialBalance), // Raw decrypted balance (already in contract format)
                withdraw_amount: withdrawAmountScaled,
                keypair: privateKey,
                nonce: withdrawProofNonce
            };
            
            // Validate inputs before sending to WASM
            if (typeof withdrawInput.current_balance_ciphertext !== 'string') {
                throw new Error('Ciphertext must be a string');
            }
            if (!Number.isInteger(withdrawInput.current_balance)) {
                throw new Error('Current balance must be an integer');
            }
            if (!Number.isInteger(withdrawInput.withdraw_amount)) {
                throw new Error('Withdraw amount must be an integer');
            }
            if (typeof withdrawInput.keypair !== 'string') {
                throw new Error('Keypair must be a string');
            }
            if (typeof withdrawInput.nonce !== 'string' || !/^\d+$/.test(withdrawInput.nonce)) {
                throw new Error('Withdraw proof nonce must be a non-negative decimal string');
            }
            
            safeLog('Generating withdraw proof...', {
                current_balance: confidentialBalance,
                withdraw_amount: withdrawAmountScaled,
                user_input_amount: parseFloat(withdrawAmount),
                contract_scale_factor: 10 ** wd,
                ciphertext_available: !!currentBalanceCiphertext
            });
            
            // Check WASM module state before calling
            const inputString = JSON.stringify(withdrawInput);
            
            // Check if input is too large
            if (inputString.length > 100000) { // 100KB limit
                throw new Error(`Input too large: ${inputString.length} bytes`);
            }
            
            let proofResult;
            try {
                proofResult = generate_withdraw_proof(JSON.stringify(withdrawInput));
                safeLog('Withdraw proof generated successfully');
            } catch (wasmError) {
                console.error('WASM call failed:', wasmError);
                throw wasmError;
            } finally {
                // Clear the secure keypair from memory
                secureKeypair.clear();
            }
            
            console.log('Raw proofResult:', proofResult);
            const proof = JSON.parse(proofResult);
            console.log('Parsed proof object:', proof);
            console.log('Proof structure:', {
                success: proof.success,
                data: proof.data,
                equality_proof: proof.data?.equality_proof,
                range_proof: proof.data?.range_proof
            });
            
            if (!proof.success) {
                throw new Error(proof.error || 'Proof generation failed');
            }
            
            const amountForContract = parseUserAmountToPlainBn(withdrawAmount, currentTokenData.decimals);
            const withdrawProofData = encodeProofData(proof);

            // Only for Tempo chain, upload the ABI-encoded withdraw proof bytes to IPFS and
            // send the resulting CID (as bytes) as the proof argument in the transaction.
            const isTempo = selectedChainId === 'tempo';
            let withdrawZkpArg = withdrawProofData;
            if (isTempo) {
                try {
                    const proofBytes = ethers.utils.arrayify(withdrawProofData);
                    const cid = await uploadBytesToIpfs(proofBytes, 'withdraw-proof.bin');
                    console.log('[Tempo] Withdraw proof bytes uploaded to IPFS with CID:', cid);
                    // Backend resolveProofBytesIfIPFS expects pointer string; use ipfs://<cid> so ipfsURLFromPointer recognizes it
                    const pointer = `ipfs://${cid}`;
                    withdrawZkpArg = ethers.utils.toUtf8Bytes(pointer);
                } catch (ipfsError) {
                    console.error('Failed to upload withdraw proof to IPFS:', ipfsError);
                    throw new Error('Failed to upload withdraw proof to IPFS. Please try again.');
                }
            }

            await refreshGasBreakdownForExactTx(
                'withdraw',
                'withdraw',
                [currentTokenData.address, amountForContract.toString(), withdrawZkpArg, false],
                { manualGasLimit: 1000000 }
            );
            
            // Execute withdraw with gas estimation fallback
            const tx = await executeContractTxWithGasFallback(
                contract,
                'withdraw',
                [currentTokenData.address, amountForContract.toString(), withdrawZkpArg, false],
                {
                    manualGasLimit: 1000000, // Withdraw operations need more gas due to proof verification and data gas costs
                    rateLimitRetry: withRateLimitRetry,
                    retryOptions: { retries: 3, baseDelayMs: 1500 },
                    stellarContext: getStellarTxContext(),
                }
            );
            const receipt = await tx.wait();
            
            // Store values for notification before clearing
            const submittedAmount = withdrawAmount;
            
            // Clear input fields immediately after successful transaction
            setWithdrawAmount('');
            setWithdrawAmountError('');
            
            // Record pending withdraw request in DB
            try {
                let requestId = getRequestIdFromReceipt(receipt, contract, [
                    'WithdrawRequested',
                    'WithdrawProcessed'
                ]);
                console.log('Extracted request ID from withdraw event:', requestId);
                
                // Fallback: If we can't extract request ID from event, use transaction hash
                // This ensures the withdraw is still recorded in Firebase and history
                if (!requestId) {
                    console.warn('No request ID found in withdraw transaction receipt events. Using transaction hash as fallback.');
                    requestId = tx.hash;
                }
                
                // Always save the withdraw to Firebase, even if we had to use the transaction hash as fallback
                const withdrawAmountForContract = parseUserAmountToPlainBn(submittedAmount, currentTokenData.decimals);
                await addPendingRequest(requestId, 4, { // 4 = Withdraw operation kind
                    token: currentTokenData.symbol,
                    tokenAddress: currentTokenData.address,
                    amount: withdrawAmountForContract.toString(),
                    amountInWei: true, // Flag to indicate amount is in raw format
                    fallbackRequestId: requestId === tx.hash // Flag to indicate if we used fallback
                }, tx.hash);
            } catch (e) {
                console.warn('Failed to record withdraw request:', e);
            }

            // Create notification with clickable transaction hash
            const tokenDataForNotification = getTokenData();
            const chainKey = tokenDataForNotification?.chainId;
            const chainConfig = chainKey ? CHAINS[chainKey] : null;
            const explorerUrl = getExplorerTxUrlFromTokenChain(chainKey, chainConfig, tx.hash);
            const shortTxHash = `${tx.hash.slice(0, 6)}...${tx.hash.slice(-4)}`;
            
            showNotification(
                <>
                    Withdraw submitted: {submittedAmount} {currentTokenData.symbol} (tx:{' '}
                    {explorerUrl ? (
                        <a 
                            href={explorerUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {shortTxHash}
                        </a>
                    ) : (
                        shortTxHash
                    )}
                    ). It might take a few moments before the change takes effect.
                </>,
                'success'
            );
            
            // Refresh account data after successful withdraw submission
            onOperationComplete();
            // Trigger checkPendingAction to update pendingActionKind quickly (show "Withdraw in progress...")
            checkPendingAction();
            // Don't await loadConfidentialBalance here - the balance won't change until the operation is processed
            // The event listener will handle loading the new balance
            loadPendingBalance(0, true);
            updateTokenBalance();
            
            // For Privy users, add additional delayed refreshes since event listeners may not work reliably
            refreshBalancesForPrivy('withdraw');
            
        } catch (error) {
            console.error('Error withdrawing tokens:', error);
            const cleanError = getCleanErrorMessage(error);
            showNotification(`Withdraw failed: ${cleanError}`, 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const encodeProofData = (proof) => {
        if (activeTab === 'transfer') {
            const proofData = {
                equality_proof: proof.data.equality_proof,
                ciphertext_validity_proof: proof.data.ciphertext_validity_proof,
                range_proof: proof.data.range_proof
            };
            
            return ethers.utils.defaultAbiCoder.encode(['string', 'string', 'string'], [
                proofData.equality_proof,
                proofData.ciphertext_validity_proof,
                proofData.range_proof
            ]);
        } else if (activeTab === 'withdraw') {
            const proofData = {
                equality_proof: proof.data.equality_proof,
                range_proof: proof.data.range_proof
            };
            
            return ethers.utils.defaultAbiCoder.encode(['string', 'string'], [
                proofData.equality_proof,
                proofData.range_proof
            ]);
        }
        return '';
    };

    const renderTransferTab = () => {
        const isConnected = !!userAccount;
        const accountStatus = checkAccountStatus();
        const hasSelection = !!(selectedChainId && selectedTokenId && tokenData && isValidTokenAddress(tokenData.address));
        const tokenDec = normalizeTokenDecimals(tokenData?.decimals);
        const amountStep = 10 ** -tokenDec;
        
        return (
            <div className="operation-form">
                
                {renderKeyInputs()}
                
                <div className="form-group amount-section">
                    <div className="token-selector-in-section">
                        <TokenSelectorPill
                            token={tokenData ? {
                                symbol: `C${tokenData.symbol}`,
                                color: tokenData.color,
                                logo: tokenData.icon,
                            } : null}
                            onClick={() => setShowTokenSelector(true)}
                            disabled={tokenData ? !isValidTokenAddress(tokenData.address) : false}
                        />
                    </div>
                    <div className="amount-input-container">
                        <input
                            type="number"
                            className="amount-input"
                            placeholder="$0"
                            value={transferAmount}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || value === '-') {
                                    setTransferAmount(value);
                                    setTransferAmountError('');
                                } else {
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue) || numValue < 0) {
                                        setTransferAmountError('Amount cannot be negative');
                                        setTransferAmount(value); // Allow typing but show error
                                    } else {
                                        const decimalCheck = value.split('.');
                                        if (decimalCheck.length > 1 && decimalCheck[1].length > tokenDec) {
                                            setTransferAmountError(`Maximum ${tokenDec} decimal places allowed`);
                                            setTransferAmount(value);
                                        } else {
                                            const truncated = truncateAmountInputString(value, tokenDec);
                                            const strict = parseUserAmountToPlainBnStrict(truncated, tokenDec);
                                            const shouldShowMaxError = selectedChainId && confidentialBalance !== null && confidentialBalance > 0;
                                            
                                            if (!strict.ok && strict.reason === 'max' && shouldShowMaxError) {
                                                const maxAmount = maxUserFacingAmountFromPlainCap(tokenDec);
                                                setTransferAmountError(`Amount exceeds maximum (${formatTokenAmount(maxAmount, tokenDec)} ${tokenData?.symbol || 'tokens'})`);
                                                setTransferAmount(value); // Allow typing but show error
                                            } else {
                                                setTransferAmount(value);
                                                setTransferAmountError('');
                                            }
                                        }
                                    }
                                }
                            }}
                            step={amountStep}
                            min="0"
                            aria-invalid={transferAmountError ? 'true' : 'false'}
                            aria-describedby={transferAmountError ? 'transfer-amount-error' : undefined}
                        />
                        {isConnected && tokenData && (
                            <div className="balance-display-below">
                                {(() => {
                                    // Check if there's a pending action that affects balance (deposit, transfer, secure claim, or withdraw)
                                    const progressMessage = getPendingProgressMessage(pendingActionKind);
                                    // Use progressMessage directly since getPendingProgressMessage now handles type conversion
                                    if (progressMessage) {
                                        return progressMessage;
                                    }
                                    return confidentialBalance !== null 
                                        ? `${formatPlainForDisplay(confidentialBalance, tokenDec)} C${tokenData.symbol}` 
                                        : (keyLoading ? 'Deriving key...' : 'Loading balance...');
                                })()}
                            </div>
                        )}
                        {transferAmountError && (
                            <span id="transfer-amount-error" className="input-error-message" style={{display: 'block', marginTop: '8px', color: '#ff4444', fontSize: '14px'}}>
                                {transferAmountError}
                            </span>
                        )}
                    </div>
                    <div className="percentage-buttons">
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handlePercentageClick(20)}
                        >
                            20%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handlePercentageClick(50)}
                        >
                            50%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handlePercentageClick(75)}
                        >
                            75%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handlePercentageClick('max')}
                        >
                            Max
                        </button>
                    </div>
                </div>

                <div className="form-group address-section">
                    <div className="address-input-container" style={{ position: 'relative' }}>
                        <input
                            type="text"
                            className={`address-input ${recipientError ? 'input-error' : ''}`}
                            placeholder=""
                            value={recipient}
                            onChange={(e) => handleRecipientChange(e.target.value)}
                            aria-invalid={recipientError ? 'true' : 'false'}
                            aria-describedby={recipientError ? 'recipient-error' : recipientAccountStatus === 'no_account' ? 'recipient-no-account' : undefined}
                        />
                        {!recipient && (
                            <div style={{
                                position: 'absolute',
                                left: 'clamp(18px, 3vw, 24px)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                fontSize: 'clamp(15px, 2.2vw, 17px)',
                                color: '#999',
                                whiteSpace: 'nowrap'
                            }}>
                                Enter receiver address or use our{' '}
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRecipientChange(CHAINS.stellar.demoTransferRecipient);
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRecipientChange(CHAINS.stellar.demoTransferRecipient);
                                    }}
                                    style={{
                                        color: 'var(--button-text-color, #00A3FF)',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        pointerEvents: 'auto'
                                    }}
                                >
                                    demo account
                                </a>
                            </div>
                        )}
                        {recipientError && (
                            <span id="recipient-error" className="input-error-message">
                                {recipientError}
                            </span>
                        )}
                        {recipientAccountStatus === 'checking' && (
                            <span className="recipient-status-message checking">
                                <i className="fas fa-spinner fa-spin"></i> Checking recipient...
                            </span>
                        )}
                        {recipientAccountStatus === 'no_account' && (
                            <div id="recipient-no-account" className="recipient-invite-message">
                                <span className="invite-warning-text">
                                The receiver is not on Stabletrust. Send an invite.
                                </span>
                                <button 
                                    type="button" 
                                    className="copy-invite-link-btn"
                                    onClick={copyInviteLink}
                                    disabled={isGeneratingInvite}
                                >
                                    {isGeneratingInvite ? 'Generating...' : 'Copy Invite Link'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {isConnected ? (
                    <>
                        <button 
                            className="operation-button"
                            onClick={handleTransfer}
                            disabled={
                                !hasSelection ||
                                isProcessing ||
                                isCreatingAccount ||
                                pendingActionKind == 1 ||
                                !!transferAmountError ||
                                recipientAccountStatus === 'no_account' ||
                                (keysDerived && !accountCreationFailed && (!accountStatus.hasAccount || !accountStatus.hasBalance))
                            }
                        >
                            { !hasSelection
                                ? 'Select chain and token'
                                : (isProcessing ? 'Processing...' : 
                                   (isCreatingAccount || pendingActionKind == 1) ? 'Creating Stabletrust account...this may take a moment.' :
                                   (!keysDerived || accountCreationFailed ? 'Sign' : 'Confidential Transfer'))
                            }
                        </button>

                        {ENABLE_FEE_BREAKDOWN_UI && (
                        <div className="fee-breakdown-container">
                            <button
                                type="button"
                                className="view-breakdown-trigger"
                                onClick={() => {
                                    const nextOpen = !(showGasBreakdown && gasBreakdownOperation === 'transfer');
                                    setShowGasBreakdown(nextOpen);
                                    if (nextOpen) {
                                        fetchGasBreakdown('transfer');
                                    }
                                }}
                            >
                                View Breakdown <span className="view-breakdown-icon">ⓘ</span>
                            </button>
                            {showGasBreakdown && gasBreakdownOperation === 'transfer' && (
                                <div className="fee-breakdown-panel">
                                    {isGasBreakdownLoading && (
                                        <div className="fee-breakdown-row muted">Estimating network fees...</div>
                                    )}
                                    {!isGasBreakdownLoading && gasBreakdownError && (
                                        <div className="fee-breakdown-row error-text">{gasBreakdownError}</div>
                                    )}
                                    {!isGasBreakdownLoading && !gasBreakdownError && gasBreakdown && (
                                        <>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">You send</span>
                                                <span className="fee-breakdown-value">
                                                    {transferAmount && tokenData
                                                        ? `${transferAmount} ${tokenData.symbol}`
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">Est. network fee</span>
                                                <span className="fee-breakdown-value">
                                                    {`${Number(gasBreakdown.estimatedFeeNative).toFixed(6)} ${gasBreakdown.nativeSymbol}`}
                                                </span>
                                            </div>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">Max network fee</span>
                                                <span className="fee-breakdown-value">
                                                    {`${Number(gasBreakdown.maxFeeNative).toFixed(6)} ${gasBreakdown.nativeSymbol}`}
                                                </span>
                                            </div>
                                            {gasBreakdown.protocolFeeDisplay && (
                                                <div className="fee-breakdown-row">
                                                    <span className="fee-breakdown-label">Protocol fee</span>
                                                    <span className="fee-breakdown-value">
                                                        {gasBreakdown.protocolFeeDisplay}
                                                    </span>
                                                </div>
                                            )}
                                            
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        )}

                        {(hasSelection && hasActiveAccount && confidentialBalance !== null && confidentialBalance === 0 && !isCreatingAccount && pendingActionKind != 1 && pendingActionKind != 2 && !(hasPendingDeposit && accountStatus.hasAccount)) && (
                            <button 
                                type="button"
                                className="deposit-redirect-button"
                                onClick={() => setActiveTab('deposit')}
                            >
                                <i className="fas fa-arrow-right" aria-hidden="true"></i>
                                Deposit funds to continue
                            </button>
                        )}
                    </>
                ) : (
                    <button className="operation-button" onClick={onConnect}>Connect</button>
                )}
            </div>
        );
    };

    const renderDepositTab = () => {
        const isConnected = !!userAccount;
        const hasSelection = !!(selectedChainId && selectedTokenId && tokenData && isValidTokenAddress(tokenData.address));
        const tokenDec = normalizeTokenDecimals(tokenData?.decimals);
        const amountStep = 10 ** -tokenDec;
        
        return (
            <div className="operation-form">
                {renderKeyInputs()}
                
                <div className="form-group amount-section">
                    <div className="token-selector-in-section">
                        <TokenSelectorPill
                            token={tokenData ? {
                                symbol: tokenData.symbol,
                                color: tokenData.color,
                                logo: tokenData.icon,
                            } : null}
                            onClick={() => setShowTokenSelector(true)}
                            disabled={tokenData ? !isValidTokenAddress(tokenData.address) : false}
                        />
                    </div>
                    <div className="amount-input-container">
                        <input
                            type="number"
                            className="amount-input"
                            placeholder="$0"
                            value={depositAmount}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || value === '-') {
                                    setDepositAmount(value);
                                    setDepositAmountError('');
                                } else {
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue) || numValue < 0) {
                                        setDepositAmountError('Amount cannot be negative');
                                        setDepositAmount(value); // Allow typing but show error
                                    } else {
                                        const decimalCheck = value.split('.');
                                        if (decimalCheck.length > 1 && decimalCheck[1].length > tokenDec) {
                                            setDepositAmountError(`Maximum ${tokenDec} decimal places allowed`);
                                            setDepositAmount(value);
                                        } else {
                                            const MAX_DEPOSIT_TOKENS = 40000000;
                                            const shouldShowMaxError = selectedChainId && confidentialBalance !== null && confidentialBalance > 0;
                                            const truncated = truncateAmountInputString(value, tokenDec);
                                            const strict = parseUserAmountToPlainBnStrict(truncated, tokenDec);
                                            
                                            if (numValue > MAX_DEPOSIT_TOKENS && shouldShowMaxError) {
                                                setDepositAmountError(`Amount exceeds maximum (${MAX_DEPOSIT_TOKENS} ${tokenData?.symbol || 'tokens'})`);
                                                setDepositAmount(value);
                                            } else if (!strict.ok && strict.reason === 'max' && shouldShowMaxError) {
                                                const maxAmount = maxUserFacingAmountFromPlainCap(tokenDec);
                                                setDepositAmountError(`Amount exceeds maximum (${formatTokenAmount(maxAmount, tokenDec)} ${tokenData?.symbol || 'tokens'})`);
                                                setDepositAmount(value);
                                            } else {
                                                setDepositAmount(value);
                                                setDepositAmountError('');
                                            }
                                        }
                                    }
                                }
                            }}
                            step={amountStep}
                            min="0"
                            aria-invalid={depositAmountError ? 'true' : 'false'}
                            aria-describedby={depositAmountError ? 'deposit-amount-error' : undefined}
                        />
                        {depositAmountError && (
                            <span id="deposit-amount-error" className="input-error-message" style={{display: 'block', marginTop: '8px', color: '#ff4444', fontSize: '14px'}}>
                                {depositAmountError}
                            </span>
                        )}
                    </div>
                    <div className="percentage-buttons">
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleDepositPercentageClick(20)}
                        >
                            20%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleDepositPercentageClick(50)}
                        >
                            50%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleDepositPercentageClick(75)}
                        >
                            75%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleDepositPercentageClick('max')}
                        >
                            Max
                        </button>
                    </div>
                </div>

                {isConnected ? (
                    <>
                        <button 
                            className="operation-button"
                            onClick={handleDeposit}
                            disabled={
                                !hasSelection ||
                                isProcessing ||
                                isCreatingAccount ||
                                pendingActionKind == 1 ||
                                !!depositAmountError ||
                                (keysDerived && !accountCreationFailed && (!accountExists || !hasActiveAccount))
                            }
                        >
                            { !hasSelection
                                ? 'Select chain and token'
                                : (isProcessing ? 'Processing...' : 
                                   (isCreatingAccount || pendingActionKind == 1) ? 'Creating Stabletrust account...this may take a moment.' :
                                       (!keysDerived || accountCreationFailed ? 'Sign' : 
                                       (hasInsufficientBalance() ? `Insufficient balance for ${tokenData?.symbol || 'tokens'}` : 'Deposit Tokens')))
                            }
                        </button>

                        {ENABLE_FEE_BREAKDOWN_UI && (
                        <div className="fee-breakdown-container">
                            <button
                                type="button"
                                className="view-breakdown-trigger"
                                onClick={() => {
                                    const nextOpen = !(showGasBreakdown && gasBreakdownOperation === 'deposit');
                                    setShowGasBreakdown(nextOpen);
                                    if (nextOpen) {
                                        const opts = tokenData?.address
                                            ? { tokenAddress: tokenData.address, amountForContract: depositAmount ? parseUserAmountToPlainInt(depositAmount, tokenDec) : 0 }
                                            : {};
                                        fetchGasBreakdown('deposit', opts);
                                    }
                                }}
                            >
                                View Breakdown <span className="view-breakdown-icon">ⓘ</span>
                            </button>
                            {showGasBreakdown && gasBreakdownOperation === 'deposit' && (
                                <div className="fee-breakdown-panel">
                                    {isGasBreakdownLoading && (
                                        <div className="fee-breakdown-row muted">Estimating network fees...</div>
                                    )}
                                    {!isGasBreakdownLoading && gasBreakdownError && (
                                        <div className="fee-breakdown-row error-text">{gasBreakdownError}</div>
                                    )}
                                    {!isGasBreakdownLoading && !gasBreakdownError && gasBreakdown && (
                                        <>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">You deposit</span>
                                                <span className="fee-breakdown-value">
                                                    {depositAmount && tokenData
                                                        ? `${depositAmount} ${tokenData.symbol}`
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">Est. network fee</span>
                                                <span className="fee-breakdown-value">
                                                    {`${Number(gasBreakdown.estimatedFeeNative).toFixed(6)} ${gasBreakdown.nativeSymbol}`}
                                                </span>
                                            </div>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">Max network fee</span>
                                                <span className="fee-breakdown-value">
                                                    {`${Number(gasBreakdown.maxFeeNative).toFixed(6)} ${gasBreakdown.nativeSymbol}`}
                                                </span>
                                            </div>
                                            
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        )}
                    </>
                ) : (
                    <button className="operation-button" onClick={onConnect}>Connect</button>
                )}
            </div>
        );
    };

    const renderWithdrawTab = () => {
        const isConnected = !!userAccount;
        const accountStatus = checkAccountStatus();
        const hasSelection = !!(selectedChainId && selectedTokenId && tokenData && isValidTokenAddress(tokenData.address));
        const tokenDec = normalizeTokenDecimals(tokenData?.decimals);
        const amountStep = 10 ** -tokenDec;
        
        return (
            <div className="operation-form">
                {renderKeyInputs()}
                
                <div className="form-group amount-section">
                    <div className="token-selector-in-section">
                        <TokenSelectorPill
                            token={tokenData ? {
                                symbol: `C${tokenData.symbol}`,
                                color: tokenData.color,
                                logo: tokenData.icon,
                            } : null}
                            onClick={() => setShowTokenSelector(true)}
                            disabled={tokenData ? !isValidTokenAddress(tokenData.address) : false}
                        />
                    </div>
                    <div className="amount-input-container">
                        <input
                            type="number"
                            className="amount-input"
                            placeholder="$0"
                            value={withdrawAmount}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || value === '-') {
                                    setWithdrawAmount(value);
                                    setWithdrawAmountError('');
                                } else {
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue) || numValue < 0) {
                                        setWithdrawAmountError('Amount cannot be negative');
                                        setWithdrawAmount(value); // Allow typing but show error
                                    } else {
                                        const decimalCheck = value.split('.');
                                        if (decimalCheck.length > 1 && decimalCheck[1].length > tokenDec) {
                                            setWithdrawAmountError(`Maximum ${tokenDec} decimal places allowed`);
                                            setWithdrawAmount(value);
                                        } else {
                                            const truncated = truncateAmountInputString(value, tokenDec);
                                            const strict = parseUserAmountToPlainBnStrict(truncated, tokenDec);
                                            const shouldShowMaxError = selectedChainId && confidentialBalance !== null && confidentialBalance > 0;
                                            
                                            if (!strict.ok && strict.reason === 'max' && shouldShowMaxError) {
                                                const maxAmount = maxUserFacingAmountFromPlainCap(tokenDec);
                                                setWithdrawAmountError(`Amount exceeds maximum (${formatTokenAmount(maxAmount, tokenDec)} ${tokenData?.symbol || 'tokens'})`);
                                                setWithdrawAmount(value); // Allow typing but show error
                                            } else {
                                                setWithdrawAmount(value);
                                                setWithdrawAmountError('');
                                            }
                                        }
                                    }
                                }
                            }}
                            step={amountStep}
                            min="0"
                            aria-invalid={withdrawAmountError ? 'true' : 'false'}
                            aria-describedby={withdrawAmountError ? 'withdraw-amount-error' : undefined}
                        />
                        {isConnected && tokenData && (
                            <div className="balance-display-below">
                                {(() => {
                                    // Check if there's a pending action that affects balance (deposit, transfer, secure claim, or withdraw)
                                    const progressMessage = getPendingProgressMessage(pendingActionKind);
                                    // Use progressMessage directly since getPendingProgressMessage now handles type conversion
                                    if (progressMessage) {
                                        return progressMessage;
                                    }
                                    return confidentialBalance !== null 
                                        ? `${formatPlainForDisplay(confidentialBalance, tokenDec)} C${tokenData.symbol}` 
                                        : (keyLoading ? 'Deriving key...' : 'Loading balance...');
                                })()}
                            </div>
                        )}
                        {withdrawAmountError && (
                            <span id="withdraw-amount-error" className="input-error-message" style={{display: 'block', marginTop: '8px', color: '#ff4444', fontSize: '14px'}}>
                                {withdrawAmountError}
                            </span>
                        )}
                    </div>
                    <div className="percentage-buttons">
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleWithdrawPercentageClick(20)}
                        >
                            20%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleWithdrawPercentageClick(50)}
                        >
                            50%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleWithdrawPercentageClick(75)}
                        >
                            75%
                        </button>
                        <button 
                            type="button" 
                            className="percentage-btn"
                            onClick={() => handleWithdrawPercentageClick('max')}
                        >
                            Max
                        </button>
                    </div>
                </div>

                {isConnected ? (
                    <>
                        <button 
                            className="operation-button"
                            onClick={handleWithdraw}
                            disabled={
                                !hasSelection ||
                                isProcessing ||
                                isCreatingAccount ||
                                pendingActionKind == 1 ||
                                !!withdrawAmountError ||
                                (keysDerived && !accountCreationFailed && (!accountStatus.hasAccount || !accountStatus.hasBalance))
                            }
                        >
                            { !hasSelection
                                ? 'Select chain and token'
                                : (isProcessing ? 'Processing...' : 
                                   (isCreatingAccount || pendingActionKind == 1) ? 'Creating Stabletrust account...this may take a moment.' :
                                   (!keysDerived || accountCreationFailed ? 'Sign' : 'Withdraw Tokens'))
                            }
                        </button>

                        {ENABLE_FEE_BREAKDOWN_UI && (
                        <div className="fee-breakdown-container">
                            <button
                                type="button"
                                className="view-breakdown-trigger"
                                onClick={() => {
                                    const nextOpen = !(showGasBreakdown && gasBreakdownOperation === 'withdraw');
                                    setShowGasBreakdown(nextOpen);
                                    if (nextOpen) {
                                        const opts = tokenData?.address
                                            ? { tokenAddress: tokenData.address, amountForContract: withdrawAmount ? parseUserAmountToPlainInt(withdrawAmount, tokenDec) : 0 }
                                            : {};
                                        fetchGasBreakdown('withdraw', opts);
                                    }
                                }}
                            >
                                View Breakdown <span className="view-breakdown-icon">ⓘ</span>
                            </button>
                            {showGasBreakdown && gasBreakdownOperation === 'withdraw' && (
                                <div className="fee-breakdown-panel">
                                    {isGasBreakdownLoading && (
                                        <div className="fee-breakdown-row muted">Estimating network fees...</div>
                                    )}
                                    {!isGasBreakdownLoading && gasBreakdownError && (
                                        <div className="fee-breakdown-row error-text">{gasBreakdownError}</div>
                                    )}
                                    {!isGasBreakdownLoading && !gasBreakdownError && gasBreakdown && (
                                        <>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">You withdraw</span>
                                                <span className="fee-breakdown-value">
                                                    {withdrawAmount && tokenData
                                                        ? `${withdrawAmount} C${tokenData.symbol}`
                                                        : '—'}
                                                </span>
                                            </div>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">Est. network fee</span>
                                                <span className="fee-breakdown-value">
                                                    {`${Number(gasBreakdown.estimatedFeeNative).toFixed(6)} ${gasBreakdown.nativeSymbol}`}
                                                </span>
                                            </div>
                                            <div className="fee-breakdown-row">
                                                <span className="fee-breakdown-label">Max network fee</span>
                                                <span className="fee-breakdown-value">
                                                    {`${Number(gasBreakdown.maxFeeNative).toFixed(6)} ${gasBreakdown.nativeSymbol}`}
                                                </span>
                                            </div>
                                            
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        )}

                        {(hasSelection && hasActiveAccount && confidentialBalance !== null && confidentialBalance === 0 && !isCreatingAccount && pendingActionKind != 1 && pendingActionKind != 2 && !(hasPendingDeposit && accountStatus.hasAccount)) && (
                            <button 
                                type="button"
                                className="deposit-redirect-button"
                                onClick={() => setActiveTab('deposit')}
                            >
                                <i className="fas fa-arrow-right" aria-hidden="true"></i>
                                Deposit funds to continue
                            </button>
                        )}
                    </>
                ) : (
                    <button className="operation-button" onClick={onConnect}>Connect</button>
                )}
            </div>
        );
    };

    const BalanceCard = ({ label, amount }) => {
        const d = normalizeTokenDecimals(tokenData?.decimals);
        return (
        <div className="balance-card">
            <div className="balance-label">{label}</div>
            <div className="balance-amount">
                {amount !== null ? `${formatPlainForDisplay(amount, d)} C${tokenData.symbol}` : (keyLoading ? 'Deriving key...' : 'Loading...')}
            </div>
        </div>
        );
    };

    // Apply Pending tab removed; replaced by overlay flow

    // Key derivation is now handled by the WASM hook

    const renderKeyInputs = () => (
        <></>
    );

    return (
        <>
            {/* Key Derivation Consent Modal */}
            <KeyDerivationConsentModal 
                isOpen={showConsentModal}
                onConsent={handleKeyDerivationConsent}
                onCancel={handleKeyDerivationCancel}
                chainName={CHAINS[selectedChainId]?.name || 'Unknown Chain'}
                accountExists={accountExists}
                accountCoreSynced={accountCoreSynced}
                isFirstTime={isFirstTimeConnection}
            />
            
            {/* Pending Balance Notification */}
            {showPendingNotification && tokenData && (
                <div 
                    className="pending-notification-container"
                    ref={pendingNotificationRef}
                >
                    <div className="pending-notification">
                        <div className="pending-notification-header">
                            <div className="pending-notification-content">
                                <div className="pending-notification-title">
                                    <i className="fas fa-info-circle"></i>
                                   <h3> Pending balance available</h3>
                                </div>
                                <div className="pending-notification-message">
                                    You have {pendingBalance !== null ? `${formatPlainForDisplay(pendingBalance, normalizeTokenDecimals(tokenData?.decimals))}` : '0'} C{tokenData.symbol} ready to claim. Secure it to your available balance now.
                                </div>
                            </div>
                            <button 
                                className="pending-notification-close" 
                                onClick={() => {
                                    setShowPendingNotification(false);
                                    setPendingNotificationDismissed(true);
                                }}
                                aria-label="Dismiss notification"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <button 
                            className="pending-notification-button" 
                            onClick={handleApplyPending}
                            disabled={isApplyingPending}
                        >
                            {isApplyingPending ? 'Processing...' : (!keysDerived || accountCreationFailed ? 'Sign' : 'Secure Claim')}
                        </button>
                    </div>
                </div>
            )}
            
        <div className="tabbed-operations">
            <div className="tabs">
                <button 
                    className={`tab-button ${activeTab === 'transfer' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('transfer')}
                >
                    <span className="tab-pill">Transfer</span>
                </button>
                <button 
                    className={`tab-button ${activeTab === 'deposit' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('deposit')}
                >
                    <span className="tab-pill">Deposit</span>
                </button>
                <button 
                    className={`tab-button ${activeTab === 'withdraw' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('withdraw')}
                >
                    <span className="tab-pill">Withdraw</span>
                </button>
                <button 
                    className={`tab-button ${activeTab === 'history' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('history')}
                >
                    <span className="tab-pill">History</span>
                </button>
            </div>
            
            <div className="tab-content">
                {activeTab === 'transfer' && renderTransferTab()}
                {activeTab === 'deposit' && renderDepositTab()}
                {activeTab === 'withdraw' && renderWithdrawTab()}
                {activeTab === 'history' && (
                    <History
                        contract={contract}
                        userAccount={userAccount}
                        provider={provider}
                        keypair={keypair}
                        chainId={selectedChainId}
                    />
                )}
            </div>

            {/* Key Input Modal */}
            {/* No key input modal needed: key is derived automatically */}
            
            {/* Token Selector Modal */}
            <TokenSelector
                isOpen={showTokenSelector}
                onClose={() => setShowTokenSelector(false)}
                onTokenSelect={handleTokenSelect}
                selectedChainId={selectedChainId}
                selectedTokenId={selectedTokenId}
                title={activeTab === 'transfer' ? "Select confidential token to transfer" : 
                       activeTab === 'deposit' ? "Select token to deposit" : 
                       activeTab === 'withdraw' ? "Select confidential token to withdraw" : 
                       "Select token"}
                showConfidential={activeTab === 'transfer' || activeTab === 'withdraw'}
            />
        </div>
        </>
    );
};

export default TabbedOperations;
